// Chemistry Weekly Lessons with Daily Lesson Plans
// Based on Vanuatu National Chemistry Curriculum

export const chemistryWeeklyLessons = {
  year11: {
    term1: {
      name: 'Term 1',
      focus: 'Introduction to Chemistry and Atomic Structure',
      weeks: 10,
      scheme: [
        {
          week: 1,
          topic: 'Introduction to Chemistry',
          strand: 'Matter and Its Properties',
          subStrand: 'Nature of Chemistry',
          learningOutcomes: [
            'Define chemistry and its importance',
            'Identify branches of chemistry',
            'Understand the scientific method'
          ],
          activities: [
            'Lab safety introduction',
            'Scientific method experiments',
            'Classification activities'
          ],
          resources: ['Lab equipment', 'Safety posters', 'Scientific method worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'What is Chemistry?',
              duration: '60 min',
              objectives: ['Define chemistry', 'Understand why chemistry is important'],
              introduction: 'Ask students: "What do you think chemistry is about?"',
              mainActivities: [
                { activity: 'Definition Discussion', duration: '15 min', description: 'Define chemistry as the study of matter and its changes' },
                { activity: 'Chemistry in Daily Life', duration: '25 min', description: 'Identify chemistry applications: cooking, medicine, agriculture' },
                { activity: 'Branches of Chemistry', duration: '20 min', description: 'Introduce organic, inorganic, physical, analytical, biochemistry' }
              ],
              materials: ['Chemistry examples poster', 'Branches chart'],
              assessment: 'List 5 ways chemistry affects daily life',
              homework: 'Find 3 examples of chemistry at home',
              teacherNotes: 'Make chemistry relevant to students\' lives in Vanuatu'
            },
            {
              day: 'Tuesday',
              title: 'Laboratory Safety',
              duration: '60 min',
              objectives: ['Learn lab safety rules', 'Identify safety equipment'],
              introduction: 'Safety is the most important thing in the chemistry lab!',
              mainActivities: [
                { activity: 'Safety Rules', duration: '20 min', description: 'Go through all safety rules systematically' },
                { activity: 'Lab Tour', duration: '25 min', description: 'Identify fire extinguisher, eye wash, first aid, exits' },
                { activity: 'Safety Quiz', duration: '15 min', description: 'Quick quiz on safety procedures' }
              ],
              materials: ['Safety goggles', 'Lab coat', 'Safety poster'],
              assessment: 'Pass safety quiz with 100%',
              homework: 'Draw lab safety poster',
              teacherNotes: 'No lab work until safety quiz passed'
            },
            {
              day: 'Wednesday',
              title: 'Scientific Method - Part 1',
              duration: '60 min',
              objectives: ['Understand the scientific method', 'Identify its steps'],
              introduction: 'Scientists follow a systematic approach to solve problems.',
              mainActivities: [
                { activity: 'Method Steps', duration: '20 min', description: 'Observation, question, hypothesis, experiment, conclusion' },
                { activity: 'Real Examples', duration: '25 min', description: 'Trace famous discoveries through the method' },
                { activity: 'Step Practice', duration: '15 min', description: 'Identify steps in given scenarios' }
              ],
              materials: ['Scientific method flowchart', 'Example scenarios'],
              assessment: 'Correctly order the steps',
              homework: 'Write hypothesis for an observation',
              teacherNotes: 'Scientific method is foundation of all chemistry'
            },
            {
              day: 'Thursday',
              title: 'Scientific Method - Part 2',
              duration: '60 min',
              objectives: ['Apply scientific method', 'Design simple experiments'],
              introduction: 'Let\'s practice using the scientific method.',
              mainActivities: [
                { activity: 'Hypothesis Writing', duration: '20 min', description: 'Write testable hypotheses' },
                { activity: 'Experiment Design', duration: '25 min', description: 'Design experiment to test hypothesis' },
                { activity: 'Variable Identification', duration: '15 min', description: 'Identify independent, dependent, controlled variables' }
              ],
              materials: ['Experiment planning sheets', 'Variable chart'],
              assessment: 'Design complete experiment plan',
              homework: 'Complete experiment design worksheet',
              teacherNotes: 'Variables concept is crucial for experiments'
            },
            {
              day: 'Friday',
              title: 'Measuring and Recording',
              duration: '60 min',
              objectives: ['Use measuring equipment', 'Record data accurately'],
              introduction: 'Accurate measurements are essential in chemistry.',
              mainActivities: [
                { activity: 'Measuring Equipment', duration: '20 min', description: 'Introduce rulers, balances, graduated cylinders, thermometers' },
                { activity: 'Measurement Practice', duration: '30 min', description: 'Practice measuring mass, volume, temperature' },
                { activity: 'Data Recording', duration: '10 min', description: 'Record measurements in data tables' }
              ],
              materials: ['Measuring equipment', 'Data recording sheets'],
              assessment: 'Take accurate measurements',
              homework: 'Practice measuring at home',
              teacherNotes: 'Emphasize significant figures and units'
            }
          ]
        },
        {
          week: 2,
          topic: 'Classification of Matter',
          strand: 'Matter and Its Properties',
          subStrand: 'Types of Matter',
          learningOutcomes: [
            'Classify matter as pure substance or mixture',
            'Distinguish between elements and compounds',
            'Identify homogeneous and heterogeneous mixtures'
          ],
          activities: [
            'Classification sorting activities',
            'Mixture separation experiments',
            'Element and compound identification'
          ],
          resources: ['Sample substances', 'Separation equipment', 'Classification charts'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'What is Matter?',
              duration: '60 min',
              objectives: ['Define matter', 'Identify properties of matter'],
              introduction: 'Everything around us is made of matter!',
              mainActivities: [
                { activity: 'Matter Definition', duration: '15 min', description: 'Matter has mass and takes up space' },
                { activity: 'Matter Examples', duration: '25 min', description: 'Identify matter vs non-matter (light, heat, sound)' },
                { activity: 'Properties of Matter', duration: '20 min', description: 'Physical and chemical properties' }
              ],
              materials: ['Various objects', 'Properties chart'],
              assessment: 'Classify items as matter or non-matter',
              homework: 'List 10 examples of matter at home',
              teacherNotes: 'Use familiar Vanuatu examples'
            },
            {
              day: 'Tuesday',
              title: 'Pure Substances vs Mixtures',
              duration: '60 min',
              objectives: ['Distinguish pure substances from mixtures', 'Identify examples of each'],
              introduction: 'Is salt water a pure substance?',
              mainActivities: [
                { activity: 'Pure Substance Definition', duration: '15 min', description: 'Same composition throughout, single type of particle' },
                { activity: 'Mixture Definition', duration: '15 min', description: 'Two or more substances physically combined' },
                { activity: 'Sorting Activity', duration: '30 min', description: 'Sort samples into pure substances and mixtures' }
              ],
              materials: ['Sample substances', 'Sorting cards'],
              assessment: 'Correctly classify 10 substances',
              homework: 'Find 5 mixtures at home',
              teacherNotes: 'Common confusion: solutions can seem pure'
            },
            {
              day: 'Wednesday',
              title: 'Elements and Compounds',
              duration: '60 min',
              objectives: ['Define elements and compounds', 'Use chemical symbols'],
              introduction: 'Pure substances can be elements or compounds.',
              mainActivities: [
                { activity: 'Element Introduction', duration: '20 min', description: 'Cannot be broken down, represented by symbols' },
                { activity: 'Compound Introduction', duration: '20 min', description: 'Two or more elements chemically combined' },
                { activity: 'Symbol Practice', duration: '20 min', description: 'Learn common element symbols: H, O, C, N, Fe, Cu' }
              ],
              materials: ['Periodic table', 'Symbol flashcards'],
              assessment: 'Write symbols for 10 common elements',
              homework: 'Memorize symbols for 20 elements',
              teacherNotes: 'Periodic table will be covered in detail later'
            },
            {
              day: 'Thursday',
              title: 'Types of Mixtures',
              duration: '60 min',
              objectives: ['Distinguish homogeneous from heterogeneous mixtures', 'Give examples of each'],
              introduction: 'Can you see the parts of a mixture?',
              mainActivities: [
                { activity: 'Homogeneous Mixtures', duration: '20 min', description: 'Uniform throughout - solutions' },
                { activity: 'Heterogeneous Mixtures', duration: '20 min', description: 'Non-uniform - can see different parts' },
                { activity: 'Identification Practice', duration: '20 min', description: 'Classify mixture samples' }
              ],
              materials: ['Mixture samples', 'Classification worksheet'],
              assessment: 'Correctly classify 8 mixtures',
              homework: 'Find 3 examples of each type at home',
              teacherNotes: 'Use local examples: coconut milk, kava, etc.'
            },
            {
              day: 'Friday',
              title: 'Separating Mixtures',
              duration: '60 min',
              objectives: ['Learn separation techniques', 'Understand when to use each'],
              introduction: 'How do we separate the parts of a mixture?',
              mainActivities: [
                { activity: 'Separation Methods', duration: '20 min', description: 'Filtration, evaporation, distillation, chromatography' },
                { activity: 'Demonstration', duration: '25 min', description: 'Demonstrate filtration and evaporation' },
                { activity: 'Method Matching', duration: '15 min', description: 'Match separation method to mixture type' }
              ],
              materials: ['Filter paper', 'Salt water', 'Sand and water'],
              assessment: 'Select correct separation method',
              homework: 'Design procedure to separate sand, salt, and water mixture',
              teacherNotes: 'Practical demo makes concepts concrete'
            }
          ]
        },
        {
          week: 3,
          topic: 'Atomic Structure',
          strand: 'Atomic Theory',
          subStrand: 'Structure of the Atom',
          learningOutcomes: [
            'Describe the structure of an atom',
            'Identify subatomic particles',
            'Calculate atomic number and mass number'
          ],
          activities: [
            'Atom model building',
            'Subatomic particle calculations',
            'Atomic structure diagrams'
          ],
          resources: ['Atom model kits', 'Periodic table', 'Calculation worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'History of Atomic Theory',
              duration: '60 min',
              objectives: ['Trace the development of atomic theory', 'Know key scientists and their contributions'],
              introduction: 'People have wondered about atoms for over 2000 years!',
              mainActivities: [
                { activity: 'Democritus', duration: '10 min', description: 'Ancient Greek idea of "atomos" - uncuttable' },
                { activity: 'Dalton\'s Theory', duration: '15 min', description: 'First scientific atomic theory' },
                { activity: 'Thomson to Rutherford', duration: '20 min', description: 'Discovery of electrons and nucleus' },
                { activity: 'Bohr Model', duration: '15 min', description: 'Electron shells/energy levels' }
              ],
              materials: ['Timeline poster', 'Scientist portraits'],
              assessment: 'Create timeline of atomic theory',
              homework: 'Research Niels Bohr',
              teacherNotes: 'Show how scientific understanding evolves'
            },
            {
              day: 'Tuesday',
              title: 'Subatomic Particles',
              duration: '60 min',
              objectives: ['Identify protons, neutrons, electrons', 'Know their properties'],
              introduction: 'Atoms are made of even smaller particles!',
              mainActivities: [
                { activity: 'Protons', duration: '15 min', description: 'Positive charge, in nucleus, determines element' },
                { activity: 'Neutrons', duration: '15 min', description: 'No charge, in nucleus, affects mass' },
                { activity: 'Electrons', duration: '15 min', description: 'Negative charge, orbit nucleus, involved in bonding' },
                { activity: 'Comparison Table', duration: '15 min', description: 'Create table comparing all three' }
              ],
              materials: ['Subatomic particle chart', 'Comparison table template'],
              assessment: 'Complete comparison table correctly',
              homework: 'Memorize properties of each particle',
              teacherNotes: 'Relative mass and charge are key concepts'
            },
            {
              day: 'Wednesday',
              title: 'Atomic Number and Mass Number',
              duration: '60 min',
              objectives: ['Define atomic number and mass number', 'Calculate number of particles'],
              introduction: 'How do we describe different atoms?',
              mainActivities: [
                { activity: 'Atomic Number', duration: '20 min', description: 'Number of protons = atomic number = element identity' },
                { activity: 'Mass Number', duration: '20 min', description: 'Protons + neutrons = mass number' },
                { activity: 'Calculations', duration: '20 min', description: 'Calculate particles from atomic and mass numbers' }
              ],
              materials: ['Periodic table', 'Calculation worksheet'],
              assessment: 'Calculate particles for 10 elements',
              homework: 'Complete calculation practice sheet',
              teacherNotes: 'This is foundational for all future chemistry'
            },
            {
              day: 'Thursday',
              title: 'Electron Configuration',
              duration: '60 min',
              objectives: ['Understand electron shells', 'Write simple electron configurations'],
              introduction: 'Electrons don\'t just float around randomly!',
              mainActivities: [
                { activity: 'Shell Capacity', duration: '15 min', description: 'Shell 1=2, Shell 2=8, Shell 3=8 electrons' },
                { activity: 'Configuration Writing', duration: '25 min', description: 'Write configurations for elements 1-20' },
                { activity: 'Shell Diagrams', duration: '20 min', description: 'Draw electron shell diagrams' }
              ],
              materials: ['Shell diagram templates', 'Configuration chart'],
              assessment: 'Write configurations for first 20 elements',
              homework: 'Draw shell diagrams for Na, Cl, Ar',
              teacherNotes: 'Electron configuration explains chemical behavior'
            },
            {
              day: 'Friday',
              title: 'Building Atom Models',
              duration: '60 min',
              objectives: ['Construct atomic models', 'Demonstrate understanding of structure'],
              introduction: 'Let\'s build our own atom models!',
              mainActivities: [
                { activity: 'Model Planning', duration: '10 min', description: 'Choose element and calculate particles needed' },
                { activity: 'Model Building', duration: '35 min', description: 'Build model showing nucleus and electron shells' },
                { activity: 'Presentations', duration: '15 min', description: 'Present models and explain structure' }
              ],
              materials: ['Model building materials', 'Colored balls or paper'],
              assessment: 'Accurate atom model with correct particles',
              homework: 'Study for atomic structure quiz',
              teacherNotes: 'Hands-on activity reinforces concepts'
            }
          ]
        },
        {
          week: 4,
          topic: 'The Periodic Table',
          strand: 'Atomic Theory',
          subStrand: 'Periodic Classification',
          learningOutcomes: [
            'Navigate the periodic table',
            'Identify groups and periods',
            'Relate position to electron configuration'
          ],
          activities: [
            'Periodic table exploration',
            'Element research projects',
            'Group characteristics study'
          ],
          resources: ['Periodic tables', 'Element cards', 'Research materials'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to the Periodic Table',
              duration: '60 min',
              objectives: ['Understand the purpose of the periodic table', 'Navigate basic structure'],
              introduction: 'The periodic table organizes all known elements!',
              mainActivities: [
                { activity: 'History', duration: '15 min', description: 'Mendeleev\'s contribution and predictions' },
                { activity: 'Table Structure', duration: '25 min', description: 'Periods (rows) and groups (columns)' },
                { activity: 'Finding Elements', duration: '20 min', description: 'Practice locating elements by name and symbol' }
              ],
              materials: ['Periodic table', 'Element hunt worksheet'],
              assessment: 'Find 15 elements within time limit',
              homework: 'Color and label blank periodic table',
              teacherNotes: 'Periodic table is the most important chemistry tool'
            },
            {
              day: 'Tuesday',
              title: 'Groups and Families',
              duration: '60 min',
              objectives: ['Identify main groups', 'Understand group properties'],
              introduction: 'Elements in the same column behave similarly.',
              mainActivities: [
                { activity: 'Alkali Metals', duration: '15 min', description: 'Group 1 - highly reactive metals' },
                { activity: 'Halogens', duration: '15 min', description: 'Group 17 - reactive non-metals' },
                { activity: 'Noble Gases', duration: '15 min', description: 'Group 18 - unreactive gases' },
                { activity: 'Group Properties', duration: '15 min', description: 'Compare properties within groups' }
              ],
              materials: ['Group characteristic charts', 'Sample reactions (videos)'],
              assessment: 'List properties of 3 main groups',
              homework: 'Research transition metals',
              teacherNotes: 'Focus on groups 1, 2, 17, 18 for now'
            },
            {
              day: 'Wednesday',
              title: 'Periods and Electron Shells',
              duration: '60 min',
              objectives: ['Relate period to electron shells', 'Predict configuration from position'],
              introduction: 'Why do elements end up where they are?',
              mainActivities: [
                { activity: 'Period = Shells', duration: '20 min', description: 'Period number = number of electron shells' },
                { activity: 'Group = Valence', duration: '20 min', description: 'Group number relates to valence electrons' },
                { activity: 'Prediction Practice', duration: '20 min', description: 'Predict configuration from table position' }
              ],
              materials: ['Periodic table', 'Configuration worksheet'],
              assessment: 'Predict configurations for 10 elements',
              homework: 'Complete prediction practice sheet',
              teacherNotes: 'This connects atomic structure to periodic table'
            },
            {
              day: 'Thursday',
              title: 'Metals, Non-metals, and Metalloids',
              duration: '60 min',
              objectives: ['Classify elements by type', 'Compare properties'],
              introduction: 'Elements can be grouped by their properties.',
              mainActivities: [
                { activity: 'Metal Properties', duration: '15 min', description: 'Shiny, conduct heat/electricity, malleable' },
                { activity: 'Non-metal Properties', duration: '15 min', description: 'Dull, poor conductors, brittle' },
                { activity: 'Metalloids', duration: '15 min', description: 'Properties of both - semiconductors' },
                { activity: 'Classification Practice', duration: '15 min', description: 'Classify elements on periodic table' }
              ],
              materials: ['Metal and non-metal samples', 'Classification worksheet'],
              assessment: 'Classify 20 elements correctly',
              homework: 'Find metal and non-metal items at home',
              teacherNotes: 'The "staircase" divides metals from non-metals'
            },
            {
              day: 'Friday',
              title: 'Periodic Trends Introduction',
              duration: '60 min',
              objectives: ['Understand atomic radius trend', 'Introduction to electronegativity'],
              introduction: 'Properties change predictably across the table.',
              mainActivities: [
                { activity: 'Atomic Radius', duration: '25 min', description: 'Decreases across period, increases down group' },
                { activity: 'Electronegativity', duration: '20 min', description: 'Increases across period, decreases down group' },
                { activity: 'Trend Practice', duration: '15 min', description: 'Predict and compare atomic sizes' }
              ],
              materials: ['Trend diagrams', 'Comparison exercises'],
              assessment: 'Correctly predict trends',
              homework: 'Study for periodic table quiz',
              teacherNotes: 'Trends will be explored more in Year 12'
            }
          ]
        },
        {
          week: 5,
          topic: 'Chemical Bonding - Ionic Bonds',
          strand: 'Chemical Bonding',
          subStrand: 'Ionic Bonding',
          learningOutcomes: [
            'Explain how ionic bonds form',
            'Draw ionic bond diagrams',
            'Predict ionic compound formulas'
          ],
          activities: [
            'Electron transfer demonstrations',
            'Ionic compound formula writing',
            'Crystal structure models'
          ],
          resources: ['Ion models', 'Formula writing worksheets', 'Crystal samples'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Why Atoms Bond',
              duration: '60 min',
              objectives: ['Understand the octet rule', 'Explain why bonding occurs'],
              introduction: 'Atoms "want" to have full outer shells.',
              mainActivities: [
                { activity: 'Octet Rule', duration: '20 min', description: 'Atoms are stable with 8 valence electrons' },
                { activity: 'Noble Gas Configuration', duration: '20 min', description: 'Atoms bond to achieve noble gas arrangement' },
                { activity: 'Stability Practice', duration: '20 min', description: 'Identify how atoms achieve stability' }
              ],
              materials: ['Electron configuration charts', 'Stability worksheet'],
              assessment: 'Explain why atoms bond',
              homework: 'List which noble gas configuration elements will achieve',
              teacherNotes: 'Octet rule has exceptions but is good starting point'
            },
            {
              day: 'Tuesday',
              title: 'Formation of Ionic Bonds',
              duration: '60 min',
              objectives: ['Describe ionic bond formation', 'Identify electron transfer'],
              introduction: 'Some atoms give electrons, others take them.',
              mainActivities: [
                { activity: 'Electron Transfer', duration: '20 min', description: 'Metals lose electrons, non-metals gain' },
                { activity: 'Ion Formation', duration: '20 min', description: 'Cations (+) and anions (-) form' },
                { activity: 'Bond Demonstration', duration: '20 min', description: 'Model NaCl formation' }
              ],
              materials: ['Ion formation diagrams', 'Model materials'],
              assessment: 'Draw ionic bond formation diagram',
              homework: 'Draw ionic bonding for MgO',
              teacherNotes: 'Use sodium and chlorine as primary example'
            },
            {
              day: 'Wednesday',
              title: 'Writing Ionic Formulas',
              duration: '60 min',
              objectives: ['Write formulas for ionic compounds', 'Balance charges'],
              introduction: 'Ionic compounds must be electrically neutral.',
              mainActivities: [
                { activity: 'Ion Charges', duration: '15 min', description: 'Common ion charges from periodic table' },
                { activity: 'Balancing Charges', duration: '25 min', description: 'Use crisscross method for formulas' },
                { activity: 'Formula Practice', duration: '20 min', description: 'Write formulas for 10 ionic compounds' }
              ],
              materials: ['Ion charge chart', 'Formula worksheet'],
              assessment: 'Correctly write 10 ionic formulas',
              homework: 'Complete formula writing practice',
              teacherNotes: 'Crisscross method is efficient for formula writing'
            },
            {
              day: 'Thursday',
              title: 'Naming Ionic Compounds',
              duration: '60 min',
              objectives: ['Name ionic compounds from formula', 'Write formula from name'],
              introduction: 'Ionic compound names follow specific rules.',
              mainActivities: [
                { activity: 'Naming Rules', duration: '20 min', description: 'Metal name + non-metal with -ide ending' },
                { activity: 'Naming Practice', duration: '25 min', description: 'Name compounds from formulas' },
                { activity: 'Formula from Name', duration: '15 min', description: 'Write formulas from compound names' }
              ],
              materials: ['Naming rules chart', 'Practice worksheet'],
              assessment: 'Name and write formulas for 15 compounds',
              homework: 'Complete naming worksheet',
              teacherNotes: 'Polyatomic ions will be covered later'
            },
            {
              day: 'Friday',
              title: 'Properties of Ionic Compounds',
              duration: '60 min',
              objectives: ['Describe ionic compound properties', 'Explain properties in terms of structure'],
              introduction: 'Why is salt hard and dissolves in water?',
              mainActivities: [
                { activity: 'Crystal Structure', duration: '15 min', description: 'Ionic compounds form crystal lattices' },
                { activity: 'Physical Properties', duration: '25 min', description: 'High melting point, brittle, conduct when dissolved' },
                { activity: 'Property Testing', duration: '20 min', description: 'Test solubility and conductivity' }
              ],
              materials: ['Salt samples', 'Conductivity tester', 'Water'],
              assessment: 'Explain 3 properties of ionic compounds',
              homework: 'Study for ionic bonding quiz',
              teacherNotes: 'Connect structure to properties'
            }
          ]
        },
        {
          week: 6,
          topic: 'Chemical Bonding - Covalent Bonds',
          strand: 'Chemical Bonding',
          subStrand: 'Covalent Bonding',
          learningOutcomes: [
            'Explain how covalent bonds form',
            'Draw Lewis structures',
            'Distinguish single, double, and triple bonds'
          ],
          activities: [
            'Electron sharing models',
            'Lewis structure practice',
            'Molecular model building'
          ],
          resources: ['Molecular model kits', 'Lewis structure templates', 'Bond diagrams'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Covalent Bonding',
              duration: '60 min',
              objectives: ['Describe covalent bond formation', 'Compare to ionic bonding'],
              introduction: 'Not all atoms transfer electrons - some share!',
              mainActivities: [
                { activity: 'Sharing Electrons', duration: '20 min', description: 'Non-metals share electrons to achieve octet' },
                { activity: 'Single Bonds', duration: '20 min', description: 'One shared pair of electrons' },
                { activity: 'Comparison', duration: '20 min', description: 'Compare ionic and covalent bonding' }
              ],
              materials: ['Bonding comparison chart', 'Sharing diagrams'],
              assessment: 'Explain difference between ionic and covalent bonds',
              homework: 'List 5 covalent compounds',
              teacherNotes: 'H2 and Cl2 are good simple examples'
            },
            {
              day: 'Tuesday',
              title: 'Lewis Dot Structures - Atoms',
              duration: '60 min',
              objectives: ['Draw Lewis structures for atoms', 'Identify valence electrons'],
              introduction: 'Lewis structures show valence electrons as dots.',
              mainActivities: [
                { activity: 'Valence Electrons', duration: '15 min', description: 'Find valence electrons from group number' },
                { activity: 'Dot Placement', duration: '25 min', description: 'Draw dots around element symbol' },
                { activity: 'Practice', duration: '20 min', description: 'Draw Lewis structures for various atoms' }
              ],
              materials: ['Lewis structure guide', 'Practice worksheet'],
              assessment: 'Draw correct Lewis structures for 15 atoms',
              homework: 'Complete Lewis structure practice',
              teacherNotes: 'Focus on elements from periods 2 and 3'
            },
            {
              day: 'Wednesday',
              title: 'Lewis Structures - Molecules',
              duration: '60 min',
              objectives: ['Draw Lewis structures for molecules', 'Show shared pairs'],
              introduction: 'Now let\'s show how atoms share electrons.',
              mainActivities: [
                { activity: 'Simple Molecules', duration: '20 min', description: 'H2, Cl2, HCl, H2O structures' },
                { activity: 'Shared Pairs', duration: '20 min', description: 'Bonding pairs vs lone pairs' },
                { activity: 'Structure Practice', duration: '20 min', description: 'Draw structures for various molecules' }
              ],
              materials: ['Molecular structure examples', 'Practice molecules'],
              assessment: 'Draw correct structures for 10 molecules',
              homework: 'Draw structures for CH4, NH3, CO2',
              teacherNotes: 'Count total valence electrons first'
            },
            {
              day: 'Thursday',
              title: 'Multiple Bonds',
              duration: '60 min',
              objectives: ['Draw double and triple bonds', 'Identify multiple bond examples'],
              introduction: 'Sometimes atoms share more than one pair.',
              mainActivities: [
                { activity: 'Double Bonds', duration: '20 min', description: 'Two shared pairs - O2, CO2' },
                { activity: 'Triple Bonds', duration: '20 min', description: 'Three shared pairs - N2' },
                { activity: 'Bond Practice', duration: '20 min', description: 'Identify and draw multiple bonds' }
              ],
              materials: ['Multiple bond examples', 'Practice worksheet'],
              assessment: 'Draw structures with multiple bonds',
              homework: 'Research molecules with multiple bonds',
              teacherNotes: 'N2 triple bond explains nitrogen\'s stability'
            },
            {
              day: 'Friday',
              title: 'Molecular Models',
              duration: '60 min',
              objectives: ['Build 3D molecular models', 'Understand molecular shapes'],
              introduction: 'Molecules have 3D shapes, not flat!',
              mainActivities: [
                { activity: 'Model Building', duration: '35 min', description: 'Build models of H2O, CH4, NH3, CO2' },
                { activity: 'Shape Names', duration: '15 min', description: 'Linear, bent, tetrahedral, etc.' },
                { activity: 'Shape Gallery', duration: '10 min', description: 'Display and compare models' }
              ],
              materials: ['Molecular model kits', 'Shape reference chart'],
              assessment: 'Build accurate molecular models',
              homework: 'Draw 3D shapes of molecules built',
              teacherNotes: 'VSEPR theory will be covered more in Year 12'
            }
          ]
        },
        {
          week: 7,
          topic: 'Chemical Reactions',
          strand: 'Chemical Reactions',
          subStrand: 'Introduction to Reactions',
          learningOutcomes: [
            'Identify signs of chemical reactions',
            'Write word equations',
            'Understand conservation of mass'
          ],
          activities: [
            'Reaction demonstrations',
            'Equation writing practice',
            'Mass conservation experiments'
          ],
          resources: ['Reaction chemicals', 'Balance scales', 'Equation worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Physical vs Chemical Changes',
              duration: '60 min',
              objectives: ['Distinguish physical from chemical changes', 'Identify signs of chemical change'],
              introduction: 'Is melting ice a chemical change?',
              mainActivities: [
                { activity: 'Physical Changes', duration: '15 min', description: 'Changes in form, not composition' },
                { activity: 'Chemical Changes', duration: '20 min', description: 'New substances formed' },
                { activity: 'Signs of Reaction', duration: '25 min', description: 'Color, gas, precipitate, temperature, light' }
              ],
              materials: ['Change examples', 'Demonstration materials'],
              assessment: 'Classify 10 changes correctly',
              homework: 'Find 3 chemical changes at home',
              teacherNotes: 'Use cooking examples - very relatable'
            },
            {
              day: 'Tuesday',
              title: 'Writing Word Equations',
              duration: '60 min',
              objectives: ['Write word equations for reactions', 'Identify reactants and products'],
              introduction: 'Word equations describe reactions in words.',
              mainActivities: [
                { activity: 'Equation Format', duration: '15 min', description: 'Reactants → Products' },
                { activity: 'Example Equations', duration: '25 min', description: 'Write equations for common reactions' },
                { activity: 'Practice', duration: '20 min', description: 'Convert descriptions to word equations' }
              ],
              materials: ['Word equation examples', 'Practice scenarios'],
              assessment: 'Write 10 correct word equations',
              homework: 'Complete word equation worksheet',
              teacherNotes: 'Foundation for balanced equations next week'
            },
            {
              day: 'Wednesday',
              title: 'Conservation of Mass',
              duration: '60 min',
              objectives: ['State law of conservation of mass', 'Demonstrate mass conservation'],
              introduction: 'Where does the mass go in a reaction?',
              mainActivities: [
                { activity: 'The Law', duration: '15 min', description: 'Mass is neither created nor destroyed' },
                { activity: 'Demonstration', duration: '30 min', description: 'Weigh reactants and products' },
                { activity: 'Discussion', duration: '15 min', description: 'Explain apparent mass changes' }
              ],
              materials: ['Balance', 'Reaction materials', 'Data sheets'],
              assessment: 'Explain conservation of mass with example',
              homework: 'Research Lavoisier\'s experiments',
              teacherNotes: 'Gas release can make mass seem to decrease'
            },
            {
              day: 'Thursday',
              title: 'Types of Reactions - Part 1',
              duration: '60 min',
              objectives: ['Identify synthesis and decomposition reactions', 'Write equations for each'],
              introduction: 'Reactions can be classified by type.',
              mainActivities: [
                { activity: 'Synthesis Reactions', duration: '20 min', description: 'A + B → AB (building up)' },
                { activity: 'Decomposition', duration: '20 min', description: 'AB → A + B (breaking down)' },
                { activity: 'Classification Practice', duration: '20 min', description: 'Identify reaction types' }
              ],
              materials: ['Reaction type charts', 'Practice equations'],
              assessment: 'Classify and write equations for 8 reactions',
              homework: 'Find examples of each type',
              teacherNotes: 'Photosynthesis vs respiration good example'
            },
            {
              day: 'Friday',
              title: 'Types of Reactions - Part 2',
              duration: '60 min',
              objectives: ['Identify single and double replacement reactions', 'Predict products'],
              introduction: 'Some reactions involve swapping parts.',
              mainActivities: [
                { activity: 'Single Replacement', duration: '20 min', description: 'A + BC → AC + B' },
                { activity: 'Double Replacement', duration: '20 min', description: 'AB + CD → AD + CB' },
                { activity: 'Prediction Practice', duration: '20 min', description: 'Predict products of replacement reactions' }
              ],
              materials: ['Replacement reaction charts', 'Practice problems'],
              assessment: 'Predict products for 10 reactions',
              homework: 'Study for reaction types quiz',
              teacherNotes: 'Activity series helpful for predictions'
            }
          ]
        },
        {
          week: 8,
          topic: 'Balancing Chemical Equations',
          strand: 'Chemical Reactions',
          subStrand: 'Chemical Equations',
          learningOutcomes: [
            'Write balanced chemical equations',
            'Apply conservation of mass',
            'Interpret balanced equations'
          ],
          activities: [
            'Equation balancing practice',
            'Coefficient exercises',
            'Equation interpretation'
          ],
          resources: ['Balancing worksheets', 'Atom counting guides', 'Practice equations'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Chemical Formulas Review',
              duration: '60 min',
              objectives: ['Review writing chemical formulas', 'Interpret subscripts'],
              introduction: 'Before balancing, we must understand formulas.',
              mainActivities: [
                { activity: 'Formula Review', duration: '20 min', description: 'Review ionic and molecular formulas' },
                { activity: 'Subscripts', duration: '20 min', description: 'What subscripts mean for atom count' },
                { activity: 'Atom Counting', duration: '20 min', description: 'Count atoms in various formulas' }
              ],
              materials: ['Formula review sheet', 'Counting practice'],
              assessment: 'Correctly count atoms in 15 formulas',
              homework: 'Formula counting practice',
              teacherNotes: 'Atom counting is essential for balancing'
            },
            {
              day: 'Tuesday',
              title: 'Introduction to Balancing',
              duration: '60 min',
              objectives: ['Understand why equations must be balanced', 'Balance simple equations'],
              introduction: 'Why must atoms be equal on both sides?',
              mainActivities: [
                { activity: 'Unbalanced Equations', duration: '15 min', description: 'Show why unbalanced violates conservation' },
                { activity: 'Coefficients', duration: '20 min', description: 'Use coefficients, not subscripts, to balance' },
                { activity: 'Simple Practice', duration: '25 min', description: 'Balance simple two-element equations' }
              ],
              materials: ['Balancing guide', 'Simple equations'],
              assessment: 'Balance 8 simple equations',
              homework: 'Complete simple balancing practice',
              teacherNotes: 'Never change subscripts to balance!'
            },
            {
              day: 'Wednesday',
              title: 'Balancing Strategies',
              duration: '60 min',
              objectives: ['Apply systematic balancing strategies', 'Balance more complex equations'],
              introduction: 'There are strategies to make balancing easier.',
              mainActivities: [
                { activity: 'Balance Metals First', duration: '15 min', description: 'Start with elements in one compound only' },
                { activity: 'Balance Oxygen/Hydrogen Last', duration: '15 min', description: 'These are often in multiple compounds' },
                { activity: 'Practice', duration: '30 min', description: 'Apply strategies to various equations' }
              ],
              materials: ['Strategy guide', 'Practice equations'],
              assessment: 'Balance 10 equations using strategies',
              homework: 'Balance combustion equations',
              teacherNotes: 'Strategies make balancing more systematic'
            },
            {
              day: 'Thursday',
              title: 'Complex Equations',
              duration: '60 min',
              objectives: ['Balance equations with polyatomic ions', 'Handle larger coefficients'],
              introduction: 'Some equations are more challenging.',
              mainActivities: [
                { activity: 'Polyatomic Ions', duration: '20 min', description: 'Treat polyatomic ions as units when possible' },
                { activity: 'Larger Numbers', duration: '20 min', description: 'Sometimes need larger coefficients' },
                { activity: 'Challenge Practice', duration: '20 min', description: 'Balance complex equations' }
              ],
              materials: ['Complex equations', 'Polyatomic ion list'],
              assessment: 'Balance 8 complex equations',
              homework: 'Complete complex equation worksheet',
              teacherNotes: 'Patience needed - some equations are tricky'
            },
            {
              day: 'Friday',
              title: 'Interpreting Balanced Equations',
              duration: '60 min',
              objectives: ['Read information from balanced equations', 'Understand mole ratios'],
              introduction: 'Balanced equations tell us much more than atom counts.',
              mainActivities: [
                { activity: 'Particle Ratios', duration: '20 min', description: 'Coefficients show particle ratios' },
                { activity: 'Mole Concept Introduction', duration: '20 min', description: 'Preview of stoichiometry' },
                { activity: 'Week Review', duration: '20 min', description: 'Comprehensive balancing practice' }
              ],
              materials: ['Equation interpretation guide', 'Review worksheet'],
              assessment: 'Interpret and balance equations correctly',
              homework: 'Study for balancing equations quiz',
              teacherNotes: 'Foundation for stoichiometry unit'
            }
          ]
        },
        {
          week: 9,
          topic: 'Acids and Bases Introduction',
          strand: 'Acids and Bases',
          subStrand: 'Properties of Acids and Bases',
          learningOutcomes: [
            'Identify acids and bases',
            'Describe their properties',
            'Use pH scale'
          ],
          activities: [
            'Acid-base testing',
            'pH measurements',
            'Neutralization experiments'
          ],
          resources: ['Indicators', 'pH paper', 'Common acids and bases'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'What are Acids?',
              duration: '60 min',
              objectives: ['Define acids', 'Identify common acids and their properties'],
              introduction: 'You eat acids every day - lemons, vinegar!',
              mainActivities: [
                { activity: 'Acid Definition', duration: '15 min', description: 'Acids produce H+ ions in water' },
                { activity: 'Common Acids', duration: '25 min', description: 'HCl, H2SO4, HNO3, CH3COOH, citric acid' },
                { activity: 'Properties', duration: '20 min', description: 'Sour taste, react with metals, corrosive' }
              ],
              materials: ['Acid samples (dilute)', 'Property chart'],
              assessment: 'List 5 acids and 3 properties',
              homework: 'Find 5 acids at home',
              teacherNotes: 'Safety first with all acid handling'
            },
            {
              day: 'Tuesday',
              title: 'What are Bases?',
              duration: '60 min',
              objectives: ['Define bases', 'Identify common bases and their properties'],
              introduction: 'Bases are the opposite of acids.',
              mainActivities: [
                { activity: 'Base Definition', duration: '15 min', description: 'Bases produce OH- ions in water' },
                { activity: 'Common Bases', duration: '25 min', description: 'NaOH, KOH, NH3, Mg(OH)2' },
                { activity: 'Properties', duration: '20 min', description: 'Bitter taste, slippery feel, corrosive' }
              ],
              materials: ['Base samples (dilute)', 'Property comparison'],
              assessment: 'List 5 bases and 3 properties',
              homework: 'Find 5 bases at home',
              teacherNotes: 'Never taste chemicals in lab!'
            },
            {
              day: 'Wednesday',
              title: 'Indicators and pH Scale',
              duration: '60 min',
              objectives: ['Use indicators to test acids/bases', 'Understand pH scale'],
              introduction: 'How do we know if something is acid or base?',
              mainActivities: [
                { activity: 'Indicators', duration: '20 min', description: 'Litmus, phenolphthalein, universal indicator' },
                { activity: 'pH Scale', duration: '20 min', description: '0-14 scale, 7 is neutral' },
                { activity: 'Testing Practice', duration: '20 min', description: 'Test various solutions' }
              ],
              materials: ['Indicators', 'pH paper', 'Test solutions'],
              assessment: 'Correctly test and identify 8 solutions',
              homework: 'Test household substances with red cabbage',
              teacherNotes: 'Red cabbage makes great natural indicator'
            },
            {
              day: 'Thursday',
              title: 'Neutralization Reactions',
              duration: '60 min',
              objectives: ['Explain neutralization', 'Write neutralization equations'],
              introduction: 'What happens when acids and bases meet?',
              mainActivities: [
                { activity: 'Neutralization Concept', duration: '15 min', description: 'Acid + Base → Salt + Water' },
                { activity: 'Demonstration', duration: '25 min', description: 'Neutralize acid with base, test pH' },
                { activity: 'Equation Writing', duration: '20 min', description: 'Write neutralization equations' }
              ],
              materials: ['Dilute HCl', 'NaOH solution', 'Indicators'],
              assessment: 'Write 5 neutralization equations',
              homework: 'Complete neutralization equation worksheet',
              teacherNotes: 'Great visual with indicator color change'
            },
            {
              day: 'Friday',
              title: 'Acids and Bases in Daily Life',
              duration: '60 min',
              objectives: ['Apply acid-base knowledge', 'Understand real-world applications'],
              introduction: 'Acids and bases are everywhere!',
              mainActivities: [
                { activity: 'Everyday Examples', duration: '20 min', description: 'Stomach acid, antacids, cleaning products' },
                { activity: 'Vanuatu Applications', duration: '20 min', description: 'Agriculture, fishing, food preservation' },
                { activity: 'Week Review', duration: '20 min', description: 'Review quiz on acids and bases' }
              ],
              materials: ['Application examples', 'Review quiz'],
              assessment: 'Explain 3 real-world applications',
              homework: 'Study for term 1 assessment',
              teacherNotes: 'Connect to local context'
            }
          ]
        },
        {
          week: 10,
          topic: 'Term 1 Review and Assessment',
          strand: 'Review',
          subStrand: 'Term 1 Revision',
          learningOutcomes: [
            'Demonstrate understanding of atomic structure',
            'Apply bonding and reaction concepts',
            'Show practical laboratory skills'
          ],
          activities: [
            'Comprehensive review',
            'Practice assessments',
            'Term examination'
          ],
          resources: ['Review materials', 'Practice tests', 'Assessment papers'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Atomic Structure Review',
              duration: '60 min',
              objectives: ['Review atomic theory and periodic table'],
              introduction: 'Let\'s review what we\'ve learned this term.',
              mainActivities: [
                { activity: 'Atomic Structure Review', duration: '25 min', description: 'Review particles, atomic/mass number' },
                { activity: 'Periodic Table Review', duration: '25 min', description: 'Review groups, periods, trends' },
                { activity: 'Q&A', duration: '10 min', description: 'Answer student questions' }
              ],
              materials: ['Review notes', 'Periodic table'],
              assessment: 'Practice quiz on atomic structure',
              homework: 'Study atomic structure topics',
              teacherNotes: 'Focus on commonly misunderstood concepts'
            },
            {
              day: 'Tuesday',
              title: 'Chemical Bonding Review',
              duration: '60 min',
              objectives: ['Review ionic and covalent bonding'],
              introduction: 'Bonding is fundamental to chemistry.',
              mainActivities: [
                { activity: 'Ionic Bonding Review', duration: '25 min', description: 'Review ion formation, formulas' },
                { activity: 'Covalent Bonding Review', duration: '25 min', description: 'Review Lewis structures, shapes' },
                { activity: 'Comparison', duration: '10 min', description: 'Compare bond types' }
              ],
              materials: ['Bonding review sheet', 'Practice structures'],
              assessment: 'Practice quiz on bonding',
              homework: 'Study bonding concepts',
              teacherNotes: 'Drawing practice is essential'
            },
            {
              day: 'Wednesday',
              title: 'Chemical Reactions Review',
              duration: '60 min',
              objectives: ['Review reaction types and balancing'],
              introduction: 'Reactions are at the heart of chemistry.',
              mainActivities: [
                { activity: 'Reaction Types', duration: '20 min', description: 'Review all reaction types' },
                { activity: 'Balancing Practice', duration: '25 min', description: 'Intensive balancing practice' },
                { activity: 'Acids and Bases', duration: '15 min', description: 'Quick acid-base review' }
              ],
              materials: ['Equation practice', 'Type identification'],
              assessment: 'Practice quiz on reactions',
              homework: 'Complete all review worksheets',
              teacherNotes: 'Balancing is often challenging'
            },
            {
              day: 'Thursday',
              title: 'Practical Skills Review',
              duration: '60 min',
              objectives: ['Review laboratory techniques', 'Practice practical skills'],
              introduction: 'Chemistry involves hands-on skills too.',
              mainActivities: [
                { activity: 'Safety Review', duration: '15 min', description: 'Review all safety procedures' },
                { activity: 'Equipment Review', duration: '20 min', description: 'Name and describe equipment use' },
                { activity: 'Mini Practical', duration: '25 min', description: 'Practice key laboratory skills' }
              ],
              materials: ['Lab equipment', 'Safety checklist'],
              assessment: 'Demonstrate safe lab techniques',
              homework: 'Final study preparation',
              teacherNotes: 'Practical skills assessed in exam'
            },
            {
              day: 'Friday',
              title: 'Term 1 Assessment',
              duration: '60 min',
              objectives: ['Complete term 1 assessment'],
              introduction: 'Show what you\'ve learned this term.',
              mainActivities: [
                { activity: 'Written Test', duration: '40 min', description: 'Theory questions on all topics' },
                { activity: 'Practical Component', duration: '15 min', description: 'Brief practical task' },
                { activity: 'Submission', duration: '5 min', description: 'Submit all materials' }
              ],
              materials: ['Assessment papers', 'Lab materials'],
              assessment: 'Term 1 Examination',
              homework: 'Enjoy your term break!',
              teacherNotes: 'Fair assessment covering all learning outcomes'
            }
          ]
        }
      ]
    },
    term2: {
      name: 'Term 2',
      focus: 'Chemical Calculations and Stoichiometry',
      weeks: 10,
      scheme: [
        {
          week: 1,
          topic: 'Mole Concept Review',
          strand: 'Quantitative Chemistry',
          subStrand: 'The Mole',
          learningOutcomes: [
            'Review the mole concept and Avogadro\'s number',
            'Calculate molar mass of compounds',
            'Convert between moles, mass, and particles'
          ],
          activities: ['Mole calculation review', 'Problem-solving practice', 'Group exercises'],
          resources: ['Periodic table', 'Calculators', 'Practice worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'The Mole Concept Review',
              duration: '60 min',
              objectives: ['Review the definition of a mole', 'Recall Avogadro\'s number'],
              introduction: 'Welcome back! Let\'s review the mole concept from last term.',
              mainActivities: [
                { activity: 'Mole Definition Review', duration: '15 min', description: 'A mole = 6.022 × 10²³ particles' },
                { activity: 'Avogadro\'s Number Practice', duration: '25 min', description: 'Using Avogadro\'s number in calculations' },
                { activity: 'Quick Quiz', duration: '20 min', description: 'Diagnostic quiz on mole concept' }
              ],
              materials: ['Review worksheet', 'Calculators'],
              assessment: 'Complete diagnostic quiz',
              homework: 'Review mole concept notes from Term 1',
              teacherNotes: 'Identify gaps in understanding from Term 1'
            },
            {
              day: 'Tuesday',
              title: 'Molar Mass Calculations',
              duration: '60 min',
              objectives: ['Calculate molar mass of compounds', 'Use periodic table effectively'],
              introduction: 'Molar mass connects moles to measurable mass.',
              mainActivities: [
                { activity: 'Molar Mass Formula', duration: '20 min', description: 'Sum of atomic masses × subscripts' },
                { activity: 'Simple Compounds', duration: '20 min', description: 'Calculate molar mass of NaCl, H₂O, CO₂' },
                { activity: 'Complex Compounds', duration: '20 min', description: 'Calculate molar mass of Ca(OH)₂, Al₂(SO₄)₃' }
              ],
              materials: ['Periodic table', 'Calculators', 'Practice problems'],
              assessment: 'Calculate molar mass of 10 compounds accurately',
              homework: 'Complete molar mass worksheet',
              teacherNotes: 'Watch for errors with polyatomic ions'
            },
            {
              day: 'Wednesday',
              title: 'Mole-Mass Conversions',
              duration: '60 min',
              objectives: ['Convert moles to mass using n = m/M', 'Convert mass to moles'],
              introduction: 'The formula n = m/M is essential for all chemistry calculations.',
              mainActivities: [
                { activity: 'Formula Practice', duration: '15 min', description: 'n = m/M rearrangement' },
                { activity: 'Moles to Mass', duration: '20 min', description: 'Given moles, calculate mass' },
                { activity: 'Mass to Moles', duration: '25 min', description: 'Given mass, calculate moles' }
              ],
              materials: ['Formula triangles', 'Practice problems'],
              assessment: 'Complete 15 conversion problems',
              homework: 'Mixed conversion practice',
              teacherNotes: 'Formula triangle helps with rearrangement'
            },
            {
              day: 'Thursday',
              title: 'Mole-Particle Conversions',
              duration: '60 min',
              objectives: ['Convert between moles and number of particles', 'Apply Avogadro\'s number'],
              introduction: 'How many atoms are in 2 moles of carbon?',
              mainActivities: [
                { activity: 'N = n × Nₐ', duration: '15 min', description: 'Formula for particle calculations' },
                { activity: 'Moles to Particles', duration: '20 min', description: 'Calculate number of atoms/molecules' },
                { activity: 'Particles to Moles', duration: '25 min', description: 'Calculate moles from particle count' }
              ],
              materials: ['Scientific calculators', 'Practice problems'],
              assessment: 'Complete particle conversion problems',
              homework: 'Mixed particle-mole problems',
              teacherNotes: 'Review scientific notation if needed'
            },
            {
              day: 'Friday',
              title: 'Combined Mole Calculations',
              duration: '60 min',
              objectives: ['Solve multi-step mole problems', 'Connect mass, moles, and particles'],
              introduction: 'Real chemistry problems often need multiple steps.',
              mainActivities: [
                { activity: 'Multi-step Strategy', duration: '15 min', description: 'Mass → Moles → Particles pathway' },
                { activity: 'Practice Problems', duration: '30 min', description: 'Challenging multi-step problems' },
                { activity: 'Week Review', duration: '15 min', description: 'Q&A session' }
              ],
              materials: ['Problem sets', 'Formula sheet'],
              assessment: 'Complete multi-step calculation quiz',
              homework: 'Study for percentage composition',
              teacherNotes: 'Foundation for all stoichiometry'
            }
          ]
        },
        {
          week: 2,
          topic: 'Percentage Composition',
          strand: 'Quantitative Chemistry',
          subStrand: 'Chemical Formulas',
          learningOutcomes: [
            'Calculate percentage composition of compounds',
            'Determine mass of element in given mass of compound',
            'Analyze composition data'
          ],
          activities: ['Percentage calculations', 'Data analysis', 'Real-world applications'],
          resources: ['Calculators', 'Periodic table', 'Sample problems'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Percentage Composition',
              duration: '60 min',
              objectives: ['Define percentage composition', 'Calculate percentage of element in compound'],
              introduction: 'What percentage of water is hydrogen?',
              mainActivities: [
                { activity: 'Definition', duration: '15 min', description: 'Mass of element ÷ molar mass × 100%' },
                { activity: 'Simple Calculations', duration: '25 min', description: 'Calculate % composition of H₂O, NaCl' },
                { activity: 'Practice', duration: '20 min', description: 'Calculate for various compounds' }
              ],
              materials: ['Formula sheet', 'Calculators'],
              assessment: 'Calculate % composition of 5 compounds',
              homework: 'Complete percentage composition worksheet',
              teacherNotes: 'Emphasize the formula structure'
            },
            {
              day: 'Tuesday',
              title: 'Percentage Composition Practice',
              duration: '60 min',
              objectives: ['Calculate percentage of all elements in a compound', 'Verify total equals 100%'],
              introduction: 'All percentages in a compound must add to 100%.',
              mainActivities: [
                { activity: 'Complete Analysis', duration: '20 min', description: 'Calculate % of each element in compound' },
                { activity: 'Verification', duration: '15 min', description: 'Check totals equal 100%' },
                { activity: 'Complex Compounds', duration: '25 min', description: 'Analyze compounds with 3+ elements' }
              ],
              materials: ['Calculators', 'Practice problems'],
              assessment: 'Complete full analysis of 8 compounds',
              homework: 'Analysis of glucose C₆H₁₂O₆',
              teacherNotes: 'Small rounding errors may prevent exactly 100%'
            },
            {
              day: 'Wednesday',
              title: 'Mass from Percentage Composition',
              duration: '60 min',
              objectives: ['Calculate mass of element from mass of compound', 'Apply percentages to real quantities'],
              introduction: 'If I have 50g of NaCl, how much sodium is there?',
              mainActivities: [
                { activity: 'Reverse Calculation', duration: '20 min', description: 'Mass of element = % × mass of compound' },
                { activity: 'Practice Problems', duration: '25 min', description: 'Find mass of specific element' },
                { activity: 'Real Applications', duration: '15 min', description: 'Nutrient content in fertilizers' }
              ],
              materials: ['Practice problems', 'Application scenarios'],
              assessment: 'Calculate element masses correctly',
              homework: 'Application problems',
              teacherNotes: 'Connect to agriculture applications'
            },
            {
              day: 'Thursday',
              title: 'Empirical Formula from Percentage',
              duration: '60 min',
              objectives: ['Determine empirical formula from percentage data', 'Convert percentages to mole ratios'],
              introduction: 'Working backwards from composition to formula.',
              mainActivities: [
                { activity: 'Step-by-Step Method', duration: '20 min', description: 'Convert % to mass, mass to moles, find ratio' },
                { activity: 'Example Problems', duration: '25 min', description: 'Find empirical formula from % composition' },
                { activity: 'Practice', duration: '15 min', description: 'Independent practice' }
              ],
              materials: ['Method guide', 'Practice problems'],
              assessment: 'Determine empirical formula of 3 compounds',
              homework: 'Empirical formula practice',
              teacherNotes: 'This is challenging - go slowly'
            },
            {
              day: 'Friday',
              title: 'Molecular Formula from Empirical',
              duration: '60 min',
              objectives: ['Determine molecular formula from empirical formula and molar mass', 'Understand the relationship between formulas'],
              introduction: 'Empirical CH₂O could be C₂H₄O₂ or C₆H₁₂O₆!',
              mainActivities: [
                { activity: 'Relationship Concept', duration: '15 min', description: 'Molecular = n × Empirical' },
                { activity: 'Finding n', duration: '20 min', description: 'n = Molar mass ÷ Empirical mass' },
                { activity: 'Complete Problems', duration: '25 min', description: 'Find molecular formula from data' }
              ],
              materials: ['Practice problems', 'Worked examples'],
              assessment: 'Determine molecular formulas correctly',
              homework: 'Study for week 3 - stoichiometry',
              teacherNotes: 'Common exam question type'
            }
          ]
        },
        {
          week: 3,
          topic: 'Stoichiometry Basics',
          strand: 'Quantitative Chemistry',
          subStrand: 'Chemical Calculations',
          learningOutcomes: [
            'Use balanced equations for calculations',
            'Calculate mole ratios from equations',
            'Perform mole-to-mole conversions'
          ],
          activities: ['Stoichiometry calculations', 'Balanced equation analysis', 'Problem solving'],
          resources: ['Balanced equations', 'Calculators', 'Practice sheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Stoichiometry',
              duration: '60 min',
              objectives: ['Define stoichiometry', 'Understand mole ratios in equations'],
              introduction: 'Stoichiometry lets us predict amounts in reactions.',
              mainActivities: [
                { activity: 'Definition', duration: '15 min', description: 'Stoichiometry = calculation of quantities in reactions' },
                { activity: 'Mole Ratios', duration: '25 min', description: 'Coefficients show mole ratios' },
                { activity: 'Reading Equations', duration: '20 min', description: 'Interpret balanced equations quantitatively' }
              ],
              materials: ['Balanced equations', 'Ratio worksheets'],
              assessment: 'Identify mole ratios in equations',
              homework: 'Mole ratio practice',
              teacherNotes: 'Foundation for all quantitative chemistry'
            },
            {
              day: 'Tuesday',
              title: 'Mole-to-Mole Calculations',
              duration: '60 min',
              objectives: ['Use mole ratios for calculations', 'Calculate moles of product from reactant'],
              introduction: 'If I use 2 moles of hydrogen, how much water forms?',
              mainActivities: [
                { activity: 'Setting Up', duration: '15 min', description: 'Identify given, find, and ratio' },
                { activity: 'Calculation Method', duration: '25 min', description: 'Given × (wanted ratio/given ratio)' },
                { activity: 'Practice', duration: '20 min', description: 'Mole-to-mole problems' }
              ],
              materials: ['Practice problems', 'Method guide'],
              assessment: 'Complete 10 mole-to-mole calculations',
              homework: 'Additional practice problems',
              teacherNotes: 'Emphasize systematic approach'
            },
            {
              day: 'Wednesday',
              title: 'Mass-to-Mass Calculations',
              duration: '60 min',
              objectives: ['Convert mass to moles to mass', 'Complete full stoichiometry problems'],
              introduction: 'Real chemistry measures mass, not moles.',
              mainActivities: [
                { activity: 'Three-Step Process', duration: '15 min', description: 'Mass → Moles → Moles → Mass' },
                { activity: 'Worked Examples', duration: '25 min', description: 'Step-by-step mass-to-mass problems' },
                { activity: 'Independent Practice', duration: '20 min', description: 'Complete problems independently' }
              ],
              materials: ['Practice problems', 'Step guide'],
              assessment: 'Complete 8 mass-to-mass calculations',
              homework: 'Mass-to-mass worksheet',
              teacherNotes: 'Most common stoichiometry problem type'
            },
            {
              day: 'Thursday',
              title: 'Mixed Stoichiometry Problems',
              duration: '60 min',
              objectives: ['Solve various stoichiometry problems', 'Choose appropriate calculation pathway'],
              introduction: 'Let\'s practice different problem types.',
              mainActivities: [
                { activity: 'Problem Types', duration: '15 min', description: 'Identify what\'s given and what\'s needed' },
                { activity: 'Mixed Practice', duration: '30 min', description: 'Variety of stoichiometry problems' },
                { activity: 'Peer Review', duration: '15 min', description: 'Check each other\'s work' }
              ],
              materials: ['Mixed problem set', 'Answer key'],
              assessment: 'Complete mixed problem set',
              homework: 'Prepare for limiting reactants',
              teacherNotes: 'Identify common errors'
            },
            {
              day: 'Friday',
              title: 'Stoichiometry Review and Quiz',
              duration: '60 min',
              objectives: ['Review stoichiometry concepts', 'Demonstrate understanding'],
              introduction: 'Let\'s see how well we\'ve learned stoichiometry.',
              mainActivities: [
                { activity: 'Review Session', duration: '25 min', description: 'Address questions and common errors' },
                { activity: 'Quiz', duration: '30 min', description: 'Stoichiometry assessment' },
                { activity: 'Wrap Up', duration: '5 min', description: 'Preview next week' }
              ],
              materials: ['Quiz papers', 'Calculators'],
              assessment: 'Stoichiometry quiz',
              homework: 'Review limiting reactants concept',
              teacherNotes: 'Assess readiness for limiting reactants'
            }
          ]
        },
        {
          week: 4,
          topic: 'Limiting Reactants',
          strand: 'Quantitative Chemistry',
          subStrand: 'Reaction Calculations',
          learningOutcomes: [
            'Identify limiting and excess reactants',
            'Calculate theoretical yield',
            'Determine amount of excess reactant remaining'
          ],
          activities: ['Limiting reactant problems', 'Yield calculations', 'Lab demonstrations'],
          resources: ['Practice problems', 'Lab materials', 'Calculators'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Limiting Reactant Concept',
              duration: '60 min',
              objectives: ['Understand limiting reactant concept', 'Identify limiting reactant in simple cases'],
              introduction: 'What happens when reactants aren\'t in perfect ratio?',
              mainActivities: [
                { activity: 'Analogy Introduction', duration: '15 min', description: 'Sandwich making - bread vs filling' },
                { activity: 'Chemistry Examples', duration: '25 min', description: 'Limiting reactant in chemical reactions' },
                { activity: 'Identification Practice', duration: '20 min', description: 'Identify limiting reactant' }
              ],
              materials: ['Visual aids', 'Practice scenarios'],
              assessment: 'Explain limiting reactant concept',
              homework: 'Limiting reactant conceptual questions',
              teacherNotes: 'Food analogies work well'
            },
            {
              day: 'Tuesday',
              title: 'Calculating Limiting Reactant',
              duration: '60 min',
              objectives: ['Calculate to find limiting reactant', 'Use mole ratios for comparison'],
              introduction: 'How do we mathematically find the limiting reactant?',
              mainActivities: [
                { activity: 'Calculation Method', duration: '20 min', description: 'Convert both to moles, compare to ratio' },
                { activity: 'Worked Examples', duration: '25 min', description: 'Step-by-step limiting reactant problems' },
                { activity: 'Practice', duration: '15 min', description: 'Independent calculation practice' }
              ],
              materials: ['Method guide', 'Practice problems'],
              assessment: 'Find limiting reactant in 5 problems',
              homework: 'Limiting reactant calculations',
              teacherNotes: 'Two main methods - teach both'
            },
            {
              day: 'Wednesday',
              title: 'Theoretical Yield',
              duration: '60 min',
              objectives: ['Calculate theoretical yield', 'Use limiting reactant for calculations'],
              introduction: 'The limiting reactant determines how much product forms.',
              mainActivities: [
                { activity: 'Theoretical Yield Concept', duration: '15 min', description: 'Maximum possible product' },
                { activity: 'Calculation Method', duration: '25 min', description: 'Use limiting reactant for stoichiometry' },
                { activity: 'Practice Problems', duration: '20 min', description: 'Calculate theoretical yield' }
              ],
              materials: ['Practice problems', 'Calculators'],
              assessment: 'Calculate theoretical yield correctly',
              homework: 'Theoretical yield problems',
              teacherNotes: 'Connect to actual lab yields'
            },
            {
              day: 'Thursday',
              title: 'Excess Reactant Remaining',
              duration: '60 min',
              objectives: ['Calculate amount of excess reactant used', 'Determine excess remaining after reaction'],
              introduction: 'What happens to the reactant that\'s not limiting?',
              mainActivities: [
                { activity: 'Excess Concept', duration: '15 min', description: 'Excess reactant is left over' },
                { activity: 'Calculation Method', duration: '25 min', description: 'Calculate excess used, then remaining' },
                { activity: 'Complete Problems', duration: '20 min', description: 'Full limiting reactant problems' }
              ],
              materials: ['Practice problems', 'Method summary'],
              assessment: 'Calculate excess remaining',
              homework: 'Complete limiting reactant worksheet',
              teacherNotes: 'Common exam question'
            },
            {
              day: 'Friday',
              title: 'Limiting Reactant Lab',
              duration: '60 min',
              objectives: ['Observe limiting reactant in action', 'Compare predicted and actual results'],
              introduction: 'Let\'s see limiting reactants in a real reaction.',
              mainActivities: [
                { activity: 'Lab Setup', duration: '10 min', description: 'Set up acid-carbonate reaction' },
                { activity: 'Experiment', duration: '30 min', description: 'React with different amounts, observe' },
                { activity: 'Analysis', duration: '20 min', description: 'Identify limiting reactant, discuss results' }
              ],
              materials: ['HCl', 'CaCO₃', 'Balances', 'Safety equipment'],
              assessment: 'Lab report on limiting reactant',
              homework: 'Complete lab report',
              teacherNotes: 'Safety first with acid reactions'
            }
          ]
        },
        {
          week: 5,
          topic: 'Percent Yield',
          strand: 'Quantitative Chemistry',
          subStrand: 'Reaction Efficiency',
          learningOutcomes: [
            'Calculate percent yield of reactions',
            'Understand why actual yield differs from theoretical',
            'Evaluate reaction efficiency'
          ],
          activities: ['Yield calculations', 'Lab experiments', 'Efficiency analysis'],
          resources: ['Lab equipment', 'Calculators', 'Data sheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Actual vs Theoretical Yield',
              duration: '60 min',
              objectives: ['Distinguish actual from theoretical yield', 'Understand why yields differ'],
              introduction: 'Why don\'t we always get the amount we calculate?',
              mainActivities: [
                { activity: 'Yield Types', duration: '20 min', description: 'Theoretical = calculated, Actual = measured' },
                { activity: 'Reasons for Difference', duration: '25 min', description: 'Incomplete reactions, side reactions, losses' },
                { activity: 'Discussion', duration: '15 min', description: 'Real-world yield examples' }
              ],
              materials: ['Yield concept diagrams', 'Examples'],
              assessment: 'Explain why yields differ',
              homework: 'List reasons for yield loss',
              teacherNotes: 'Connect to industrial chemistry'
            },
            {
              day: 'Tuesday',
              title: 'Percent Yield Calculation',
              duration: '60 min',
              objectives: ['Calculate percent yield', 'Apply formula correctly'],
              introduction: '% Yield = (Actual/Theoretical) × 100%',
              mainActivities: [
                { activity: 'Formula Introduction', duration: '15 min', description: 'Percent yield formula' },
                { activity: 'Worked Examples', duration: '25 min', description: 'Calculate percent yield step-by-step' },
                { activity: 'Practice', duration: '20 min', description: 'Independent calculation practice' }
              ],
              materials: ['Formula sheet', 'Practice problems'],
              assessment: 'Calculate percent yield for 8 reactions',
              homework: 'Percent yield worksheet',
              teacherNotes: 'Answer should be ≤100%'
            },
            {
              day: 'Wednesday',
              title: 'Reverse Yield Calculations',
              duration: '60 min',
              objectives: ['Calculate actual yield from percent yield', 'Calculate theoretical from actual and percent'],
              introduction: 'Sometimes we need to work backwards.',
              mainActivities: [
                { activity: 'Formula Rearrangement', duration: '15 min', description: 'Solve for actual or theoretical' },
                { activity: 'Finding Actual Yield', duration: '20 min', description: 'Given %, find actual yield' },
                { activity: 'Finding Theoretical', duration: '25 min', description: 'Given % and actual, find theoretical' }
              ],
              materials: ['Practice problems', 'Formula triangles'],
              assessment: 'Complete reverse calculations',
              homework: 'Mixed yield problems',
              teacherNotes: 'Important for practical planning'
            },
            {
              day: 'Thursday',
              title: 'Percent Yield Lab',
              duration: '60 min',
              objectives: ['Conduct reaction and measure actual yield', 'Calculate percent yield experimentally'],
              introduction: 'Let\'s measure percent yield ourselves.',
              mainActivities: [
                { activity: 'Reaction Setup', duration: '15 min', description: 'Prepare precipitation reaction' },
                { activity: 'Conduct Reaction', duration: '25 min', description: 'React, filter, dry precipitate' },
                { activity: 'Calculations', duration: '20 min', description: 'Calculate and compare yields' }
              ],
              materials: ['Reactants', 'Filter equipment', 'Balances'],
              assessment: 'Lab report with yield calculations',
              homework: 'Complete lab calculations',
              teacherNotes: 'Drying precipitate affects results'
            },
            {
              day: 'Friday',
              title: 'Yield Review and Applications',
              duration: '60 min',
              objectives: ['Review all yield concepts', 'Apply to industrial scenarios'],
              introduction: 'Yield is crucial in industrial chemistry.',
              mainActivities: [
                { activity: 'Concept Review', duration: '20 min', description: 'Review limiting reactant and yield' },
                { activity: 'Industrial Applications', duration: '25 min', description: 'Importance in manufacturing' },
                { activity: 'Problem Set', duration: '15 min', description: 'Mixed challenging problems' }
              ],
              materials: ['Review materials', 'Industrial examples'],
              assessment: 'Complete review problems',
              homework: 'Study for mid-term assessment',
              teacherNotes: 'Connect to economic considerations'
            }
          ]
        },
        {
          week: 6,
          topic: 'Solution Chemistry',
          strand: 'Solutions',
          subStrand: 'Concentration',
          learningOutcomes: [
            'Define concentration and molarity',
            'Calculate molarity of solutions',
            'Prepare solutions of known concentration'
          ],
          activities: ['Concentration calculations', 'Solution preparation', 'Dilution practice'],
          resources: ['Volumetric flasks', 'Balances', 'Solutes'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Concentration Concepts',
              duration: '60 min',
              objectives: ['Define concentration and molarity', 'Understand units of concentration'],
              introduction: 'How do we describe how strong a solution is?',
              mainActivities: [
                { activity: 'Concentration Definition', duration: '15 min', description: 'Amount of solute per volume of solution' },
                { activity: 'Molarity', duration: '25 min', description: 'M = n/V, moles per liter' },
                { activity: 'Units Practice', duration: '20 min', description: 'Converting between units' }
              ],
              materials: ['Concentration diagrams', 'Formula sheet'],
              assessment: 'Define concentration terms',
              homework: 'Concentration concept questions',
              teacherNotes: 'Distinguish concentration from amount'
            },
            {
              day: 'Tuesday',
              title: 'Molarity Calculations',
              duration: '60 min',
              objectives: ['Calculate molarity from moles and volume', 'Rearrange molarity formula'],
              introduction: 'Let\'s practice using M = n/V.',
              mainActivities: [
                { activity: 'Finding Molarity', duration: '20 min', description: 'Calculate M from n and V' },
                { activity: 'Finding Moles', duration: '20 min', description: 'Calculate n from M and V' },
                { activity: 'Finding Volume', duration: '20 min', description: 'Calculate V from M and n' }
              ],
              materials: ['Calculators', 'Practice problems'],
              assessment: 'Complete molarity calculations',
              homework: 'Molarity worksheet',
              teacherNotes: 'Volume must be in liters'
            },
            {
              day: 'Wednesday',
              title: 'Making Solutions',
              duration: '60 min',
              objectives: ['Calculate mass needed for solution', 'Prepare solution accurately'],
              introduction: 'How do we make a solution of specific concentration?',
              mainActivities: [
                { activity: 'Calculation Steps', duration: '15 min', description: 'Moles needed → mass needed' },
                { activity: 'Solution Preparation Demo', duration: '25 min', description: 'Demonstrate proper technique' },
                { activity: 'Practice Calculations', duration: '20 min', description: 'Calculate for various solutions' }
              ],
              materials: ['Volumetric flasks', 'Balance', 'Solutes'],
              assessment: 'Calculate mass needed for solutions',
              homework: 'Solution preparation calculations',
              teacherNotes: 'Volumetric flask technique is important'
            },
            {
              day: 'Thursday',
              title: 'Solution Preparation Lab',
              duration: '60 min',
              objectives: ['Prepare standard solution', 'Use proper laboratory technique'],
              introduction: 'Today you\'ll make your own standard solution.',
              mainActivities: [
                { activity: 'Calculations', duration: '10 min', description: 'Calculate mass needed' },
                { activity: 'Preparation', duration: '35 min', description: 'Weigh, dissolve, make up to volume' },
                { activity: 'Labeling', duration: '15 min', description: 'Properly label solutions' }
              ],
              materials: ['Balance', 'Volumetric flasks', 'NaCl or sugar'],
              assessment: 'Prepare accurate standard solution',
              homework: 'Lab report on solution preparation',
              teacherNotes: 'Emphasize precision and technique'
            },
            {
              day: 'Friday',
              title: 'Dilution Calculations',
              duration: '60 min',
              objectives: ['Understand dilution concept', 'Use M₁V₁ = M₂V₂'],
              introduction: 'How do we make a weaker solution from a stronger one?',
              mainActivities: [
                { activity: 'Dilution Concept', duration: '15 min', description: 'Adding solvent decreases concentration' },
                { activity: 'Dilution Formula', duration: '25 min', description: 'M₁V₁ = M₂V₂ and applications' },
                { activity: 'Practice', duration: '20 min', description: 'Dilution calculations' }
              ],
              materials: ['Formula sheet', 'Practice problems'],
              assessment: 'Complete dilution calculations',
              homework: 'Dilution worksheet',
              teacherNotes: 'Moles of solute stay constant'
            }
          ]
        },
        {
          week: 7,
          topic: 'Gases and the Mole',
          strand: 'States of Matter',
          subStrand: 'Gas Laws',
          learningOutcomes: [
            'Understand molar volume of gases at STP',
            'Apply gas laws to calculations',
            'Convert between volume and moles of gas'
          ],
          activities: ['Gas calculations', 'Volume-mole conversions', 'Gas law problems'],
          resources: ['Gas law charts', 'Calculators', 'Demonstrations'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Molar Volume of Gases',
              duration: '60 min',
              objectives: ['Define molar volume', 'Use 22.4 L/mol at STP'],
              introduction: 'Gases behave differently from solids and liquids.',
              mainActivities: [
                { activity: 'Gas Properties Review', duration: '15 min', description: 'Gases fill container, compressible' },
                { activity: 'Molar Volume Concept', duration: '25 min', description: '1 mole of any gas = 22.4 L at STP' },
                { activity: 'Simple Calculations', duration: '20 min', description: 'Moles to volume conversions' }
              ],
              materials: ['Gas volume diagrams', 'Practice problems'],
              assessment: 'Convert moles to liters and back',
              homework: 'Molar volume practice',
              teacherNotes: 'STP = 0°C, 1 atm'
            },
            {
              day: 'Tuesday',
              title: 'Gas Volume Calculations',
              duration: '60 min',
              objectives: ['Calculate volume from moles', 'Calculate moles from volume'],
              introduction: 'n = V/22.4 at STP.',
              mainActivities: [
                { activity: 'Formula Application', duration: '20 min', description: 'V = n × 22.4 and n = V/22.4' },
                { activity: 'Practice Problems', duration: '25 min', description: 'Volume-mole conversions' },
                { activity: 'Multi-step Problems', duration: '15 min', description: 'Mass to volume via moles' }
              ],
              materials: ['Calculators', 'Practice problems'],
              assessment: 'Complete gas volume calculations',
              homework: 'Gas calculation worksheet',
              teacherNotes: 'Must be at STP for 22.4 L'
            },
            {
              day: 'Wednesday',
              title: 'Stoichiometry with Gases',
              duration: '60 min',
              objectives: ['Apply stoichiometry to gas reactions', 'Calculate gas volumes in reactions'],
              introduction: 'Gases are common in many chemical reactions.',
              mainActivities: [
                { activity: 'Gas Stoichiometry Concept', duration: '15 min', description: 'Use molar volume in stoichiometry' },
                { activity: 'Worked Examples', duration: '25 min', description: 'Gas volume from mass of reactant' },
                { activity: 'Practice', duration: '20 min', description: 'Various gas stoichiometry problems' }
              ],
              materials: ['Practice problems', 'Method guide'],
              assessment: 'Complete gas stoichiometry problems',
              homework: 'Gas reaction calculations',
              teacherNotes: 'Common in acid-carbonate reactions'
            },
            {
              day: 'Thursday',
              title: 'Ideal Gas Law Introduction',
              duration: '60 min',
              objectives: ['Understand PV = nRT', 'Apply ideal gas law to calculations'],
              introduction: 'What if conditions aren\'t STP?',
              mainActivities: [
                { activity: 'Ideal Gas Law', duration: '20 min', description: 'PV = nRT, where R = 8.314 J/(mol·K)' },
                { activity: 'Unit Conversions', duration: '20 min', description: 'Temperature to Kelvin, pressure units' },
                { activity: 'Simple Applications', duration: '20 min', description: 'Calculate n, V, T, or P' }
              ],
              materials: ['Formula sheet', 'Calculators'],
              assessment: 'Apply ideal gas law correctly',
              homework: 'Ideal gas law problems',
              teacherNotes: 'Temperature must be in Kelvin!'
            },
            {
              day: 'Friday',
              title: 'Gas Laws Review',
              duration: '60 min',
              objectives: ['Review all gas concepts', 'Solve mixed gas problems'],
              introduction: 'Let\'s consolidate our gas knowledge.',
              mainActivities: [
                { activity: 'Concept Review', duration: '20 min', description: 'Molar volume, gas stoichiometry, ideal gas law' },
                { activity: 'Mixed Problems', duration: '25 min', description: 'Variety of gas calculation problems' },
                { activity: 'Quiz', duration: '15 min', description: 'Gas calculations assessment' }
              ],
              materials: ['Review sheet', 'Quiz papers'],
              assessment: 'Gas calculations quiz',
              homework: 'Prepare for oxidation-reduction',
              teacherNotes: 'Assessment before new topic'
            }
          ]
        },
        {
          week: 8,
          topic: 'Introduction to Oxidation-Reduction',
          strand: 'Electrochemistry',
          subStrand: 'Redox Reactions',
          learningOutcomes: [
            'Define oxidation and reduction',
            'Identify oxidizing and reducing agents',
            'Write half-reactions'
          ],
          activities: ['Redox identification', 'Half-reaction writing', 'Agent identification'],
          resources: ['Redox examples', 'Practice reactions', 'Visual aids'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'What is Oxidation-Reduction?',
              duration: '60 min',
              objectives: ['Define oxidation and reduction', 'Understand electron transfer'],
              introduction: 'Redox reactions are everywhere - rusting, burning, batteries!',
              mainActivities: [
                { activity: 'Oxidation Definition', duration: '15 min', description: 'Loss of electrons (LEO)' },
                { activity: 'Reduction Definition', duration: '15 min', description: 'Gain of electrons (GER)' },
                { activity: 'Examples', duration: '30 min', description: 'Identify oxidation and reduction in reactions' }
              ],
              materials: ['Redox concept cards', 'Example reactions'],
              assessment: 'Identify oxidation and reduction',
              homework: 'LEO GER practice',
              teacherNotes: 'LEO says GER - memory aid'
            },
            {
              day: 'Tuesday',
              title: 'Oxidizing and Reducing Agents',
              duration: '60 min',
              objectives: ['Identify oxidizing agents', 'Identify reducing agents'],
              introduction: 'Agents cause oxidation or reduction in others.',
              mainActivities: [
                { activity: 'Oxidizing Agent', duration: '20 min', description: 'Causes oxidation, is itself reduced' },
                { activity: 'Reducing Agent', duration: '20 min', description: 'Causes reduction, is itself oxidized' },
                { activity: 'Identification Practice', duration: '20 min', description: 'Find agents in reactions' }
              ],
              materials: ['Agent identification chart', 'Practice reactions'],
              assessment: 'Correctly identify agents in 8 reactions',
              homework: 'Agent identification worksheet',
              teacherNotes: 'Agents are named for what they cause'
            },
            {
              day: 'Wednesday',
              title: 'Half-Reactions',
              duration: '60 min',
              objectives: ['Write oxidation half-reactions', 'Write reduction half-reactions'],
              introduction: 'We can split redox reactions into two parts.',
              mainActivities: [
                { activity: 'Half-Reaction Concept', duration: '15 min', description: 'Separate oxidation and reduction parts' },
                { activity: 'Writing Oxidation Half', duration: '20 min', description: 'Show electron loss' },
                { activity: 'Writing Reduction Half', duration: '25 min', description: 'Show electron gain' }
              ],
              materials: ['Half-reaction examples', 'Practice reactions'],
              assessment: 'Write half-reactions for 5 reactions',
              homework: 'Half-reaction practice',
              teacherNotes: 'Balance electrons gained = lost'
            },
            {
              day: 'Thursday',
              title: 'Common Redox Reactions',
              duration: '60 min',
              objectives: ['Recognize common redox reactions', 'Apply redox concepts to familiar reactions'],
              introduction: 'Many reactions you know are redox reactions!',
              mainActivities: [
                { activity: 'Combustion as Redox', duration: '15 min', description: 'Burning involves oxidation' },
                { activity: 'Metal Reactions', duration: '20 min', description: 'Metal + acid, displacement reactions' },
                { activity: 'Corrosion', duration: '25 min', description: 'Rusting and prevention' }
              ],
              materials: ['Reaction examples', 'Local examples'],
              assessment: 'Explain 3 common redox reactions',
              homework: 'Find redox reactions at home',
              teacherNotes: 'Connect to everyday examples'
            },
            {
              day: 'Friday',
              title: 'Redox Review',
              duration: '60 min',
              objectives: ['Review redox concepts', 'Practice all redox skills'],
              introduction: 'Let\'s consolidate our redox knowledge.',
              mainActivities: [
                { activity: 'Concept Review', duration: '20 min', description: 'Definitions, agents, half-reactions' },
                { activity: 'Practice Problems', duration: '25 min', description: 'Mixed redox problems' },
                { activity: 'Q&A', duration: '15 min', description: 'Address questions' }
              ],
              materials: ['Review materials', 'Practice problems'],
              assessment: 'Redox concept quiz',
              homework: 'Study for Term 2 assessment',
              teacherNotes: 'Prepare for assessment'
            }
          ]
        },
        {
          week: 9,
          topic: 'Organic Chemistry Introduction',
          strand: 'Organic Chemistry',
          subStrand: 'Hydrocarbons',
          learningOutcomes: [
            'Define organic chemistry',
            'Identify alkanes and their properties',
            'Name simple hydrocarbons'
          ],
          activities: ['Hydrocarbon naming', 'Structure drawing', 'Property exploration'],
          resources: ['Molecular models', 'Naming charts', 'Structure worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Organic Chemistry',
              duration: '60 min',
              objectives: ['Define organic chemistry', 'Understand importance of carbon'],
              introduction: 'Organic chemistry is the chemistry of carbon compounds.',
              mainActivities: [
                { activity: 'Organic Definition', duration: '15 min', description: 'Chemistry of carbon compounds' },
                { activity: 'Carbon Properties', duration: '25 min', description: 'Tetravalent, forms chains and rings' },
                { activity: 'Organic Compound Examples', duration: '20 min', description: 'Fuels, plastics, medicines, food' }
              ],
              materials: ['Carbon bonding diagrams', 'Example compounds'],
              assessment: 'Explain why carbon forms so many compounds',
              homework: 'List 10 organic compounds at home',
              teacherNotes: 'Carbon\'s unique bonding ability'
            },
            {
              day: 'Tuesday',
              title: 'Alkanes - Structure',
              duration: '60 min',
              objectives: ['Understand alkane structure', 'Draw structural formulas'],
              introduction: 'Alkanes are the simplest organic compounds.',
              mainActivities: [
                { activity: 'Alkane Definition', duration: '15 min', description: 'Saturated hydrocarbons, CₙH₂ₙ₊₂' },
                { activity: 'Simple Alkanes', duration: '25 min', description: 'Methane, ethane, propane, butane' },
                { activity: 'Drawing Structures', duration: '20 min', description: 'Draw structural formulas' }
              ],
              materials: ['Structure cards', 'Drawing practice'],
              assessment: 'Draw first 5 alkanes',
              homework: 'Alkane structure practice',
              teacherNotes: 'Emphasize saturated = single bonds only'
            },
            {
              day: 'Wednesday',
              title: 'Naming Alkanes',
              duration: '60 min',
              objectives: ['Name alkanes using IUPAC rules', 'Identify carbon chain length'],
              introduction: 'IUPAC naming gives every compound a unique name.',
              mainActivities: [
                { activity: 'Prefix System', duration: '20 min', description: 'Meth-, eth-, prop-, but-, etc.' },
                { activity: 'Naming Practice', duration: '25 min', description: 'Name alkanes from structures' },
                { activity: 'Drawing from Names', duration: '15 min', description: 'Draw structure from name' }
              ],
              materials: ['Naming chart', 'Practice structures'],
              assessment: 'Name and draw 10 alkanes',
              homework: 'Naming worksheet',
              teacherNotes: 'Systematic naming is essential'
            },
            {
              day: 'Thursday',
              title: 'Alkane Properties',
              duration: '60 min',
              objectives: ['Describe physical properties of alkanes', 'Explain property trends'],
              introduction: 'What are alkanes like? Can we burn them?',
              mainActivities: [
                { activity: 'Physical Properties', duration: '20 min', description: 'Boiling points, states, solubility' },
                { activity: 'Property Trends', duration: '20 min', description: 'How properties change with size' },
                { activity: 'Combustion', duration: '20 min', description: 'Alkanes as fuels' }
              ],
              materials: ['Property data table', 'Combustion examples'],
              assessment: 'Explain alkane property trends',
              homework: 'Property trend questions',
              teacherNotes: 'Connect to fuels and energy'
            },
            {
              day: 'Friday',
              title: 'Alkenes Introduction',
              duration: '60 min',
              objectives: ['Understand alkene structure', 'Compare alkenes to alkanes'],
              introduction: 'Alkenes have double bonds - they\'re unsaturated.',
              mainActivities: [
                { activity: 'Alkene Definition', duration: '15 min', description: 'Unsaturated, contains C=C double bond' },
                { activity: 'Naming Alkenes', duration: '25 min', description: 'Same prefixes, -ene ending' },
                { activity: 'Comparison', duration: '20 min', description: 'Alkane vs alkene properties' }
              ],
              materials: ['Alkene structures', 'Comparison chart'],
              assessment: 'Name and draw simple alkenes',
              homework: 'Alkene practice',
              teacherNotes: 'Double bond makes them more reactive'
            }
          ]
        },
        {
          week: 10,
          topic: 'Term 2 Review and Assessment',
          strand: 'Review',
          subStrand: 'Term 2 Revision',
          learningOutcomes: [
            'Demonstrate understanding of stoichiometry',
            'Apply solution and gas concepts',
            'Show knowledge of redox and organic basics'
          ],
          activities: ['Comprehensive review', 'Practice assessment', 'Term examination'],
          resources: ['Review materials', 'Practice tests', 'Assessment papers'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Stoichiometry Review',
              duration: '60 min',
              objectives: ['Review mole calculations', 'Review limiting reactants and yield'],
              introduction: 'Let\'s review our quantitative chemistry.',
              mainActivities: [
                { activity: 'Mole Concept Review', duration: '20 min', description: 'Moles, mass, particles' },
                { activity: 'Stoichiometry Review', duration: '25 min', description: 'Limiting reactants, yield' },
                { activity: 'Practice Problems', duration: '15 min', description: 'Mixed calculation problems' }
              ],
              materials: ['Review worksheets', 'Formula sheets'],
              assessment: 'Complete review problems',
              homework: 'Study stoichiometry',
              teacherNotes: 'Focus on common errors'
            },
            {
              day: 'Tuesday',
              title: 'Solutions and Gases Review',
              duration: '60 min',
              objectives: ['Review concentration calculations', 'Review gas laws'],
              introduction: 'Solutions and gases are important topics.',
              mainActivities: [
                { activity: 'Concentration Review', duration: '25 min', description: 'Molarity, dilution, preparation' },
                { activity: 'Gas Laws Review', duration: '25 min', description: 'Molar volume, ideal gas law' },
                { activity: 'Practice', duration: '10 min', description: 'Mixed problems' }
              ],
              materials: ['Review materials', 'Practice problems'],
              assessment: 'Complete solution and gas problems',
              homework: 'Study solutions and gases',
              teacherNotes: 'Check unit conversions'
            },
            {
              day: 'Wednesday',
              title: 'Redox and Organic Review',
              duration: '60 min',
              objectives: ['Review oxidation-reduction', 'Review organic chemistry basics'],
              introduction: 'Redox and organic chemistry are foundational.',
              mainActivities: [
                { activity: 'Redox Review', duration: '25 min', description: 'Definitions, agents, half-reactions' },
                { activity: 'Organic Review', duration: '25 min', description: 'Alkanes, alkenes, naming' },
                { activity: 'Q&A', duration: '10 min', description: 'Address questions' }
              ],
              materials: ['Review sheets', 'Practice reactions'],
              assessment: 'Complete redox and organic problems',
              homework: 'Final study preparation',
              teacherNotes: 'Address any remaining confusion'
            },
            {
              day: 'Thursday',
              title: 'Practice Assessment',
              duration: '60 min',
              objectives: ['Complete practice test', 'Identify areas for improvement'],
              introduction: 'This practice test helps prepare for tomorrow.',
              mainActivities: [
                { activity: 'Practice Test', duration: '45 min', description: 'Full practice assessment' },
                { activity: 'Review Answers', duration: '15 min', description: 'Go through key questions' }
              ],
              materials: ['Practice test papers', 'Calculators'],
              assessment: 'Practice test completion',
              homework: 'Review incorrect answers',
              teacherNotes: 'Similar format to actual assessment'
            },
            {
              day: 'Friday',
              title: 'Term 2 Assessment',
              duration: '60 min',
              objectives: ['Complete Term 2 assessment'],
              introduction: 'Show what you\'ve learned this term.',
              mainActivities: [
                { activity: 'Written Assessment', duration: '50 min', description: 'Theory and calculations' },
                { activity: 'Submission', duration: '10 min', description: 'Submit and relax' }
              ],
              materials: ['Assessment papers', 'Calculators', 'Periodic tables'],
              assessment: 'Term 2 examination',
              homework: 'Enjoy your break!',
              teacherNotes: 'Fair assessment of Term 2 learning'
            }
          ]
        }
      ]
    },
    term3: {
      name: 'Term 3',
      focus: 'Organic Chemistry and Thermochemistry',
      weeks: 10,
      scheme: [
        {
          week: 1,
          topic: 'Organic Functional Groups',
          strand: 'Organic Chemistry',
          subStrand: 'Functional Groups',
          learningOutcomes: [
            'Identify common functional groups',
            'Name compounds with functional groups',
            'Understand functional group properties'
          ],
          activities: ['Functional group identification', 'Naming practice', 'Property comparison'],
          resources: ['Functional group charts', 'Molecular models', 'Practice worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Functional Groups',
              duration: '60 min',
              objectives: ['Define functional groups', 'Understand their importance'],
              introduction: 'Functional groups determine how organic compounds behave.',
              mainActivities: [
                { activity: 'Functional Group Concept', duration: '20 min', description: 'Specific atom groups that determine properties' },
                { activity: 'Common Groups Overview', duration: '25 min', description: 'Alcohols, acids, aldehydes, ketones' },
                { activity: 'Identification Practice', duration: '15 min', description: 'Find functional groups in structures' }
              ],
              materials: ['Functional group chart', 'Structure examples'],
              assessment: 'Identify functional groups in 10 structures',
              homework: 'Functional group recognition practice',
              teacherNotes: 'Foundation for organic chemistry'
            },
            {
              day: 'Tuesday',
              title: 'Alcohols',
              duration: '60 min',
              objectives: ['Identify alcohol functional group', 'Name simple alcohols'],
              introduction: 'Alcohols contain the -OH group.',
              mainActivities: [
                { activity: 'Alcohol Structure', duration: '20 min', description: '-OH group attached to carbon' },
                { activity: 'Naming Alcohols', duration: '25 min', description: '-ol ending, e.g., methanol, ethanol' },
                { activity: 'Alcohol Properties', duration: '15 min', description: 'Hydrogen bonding, solubility' }
              ],
              materials: ['Alcohol structures', 'Naming practice'],
              assessment: 'Name and draw 8 alcohols',
              homework: 'Alcohol naming worksheet',
              teacherNotes: 'Ethanol is most common example'
            },
            {
              day: 'Wednesday',
              title: 'Carboxylic Acids',
              duration: '60 min',
              objectives: ['Identify carboxylic acid group', 'Name simple carboxylic acids'],
              introduction: 'Carboxylic acids contain -COOH.',
              mainActivities: [
                { activity: 'Acid Structure', duration: '20 min', description: '-COOH (carboxyl) group' },
                { activity: 'Naming Acids', duration: '25 min', description: '-oic acid ending, e.g., ethanoic acid' },
                { activity: 'Acid Properties', duration: '15 min', description: 'Acidic, sour taste, reactions' }
              ],
              materials: ['Acid structures', 'Naming practice'],
              assessment: 'Name and draw 6 carboxylic acids',
              homework: 'Carboxylic acid worksheet',
              teacherNotes: 'Vinegar = acetic acid = ethanoic acid'
            },
            {
              day: 'Thursday',
              title: 'Esters',
              duration: '60 min',
              objectives: ['Understand ester formation', 'Name simple esters'],
              introduction: 'Esters give fruits their smell.',
              mainActivities: [
                { activity: 'Ester Formation', duration: '20 min', description: 'Alcohol + Acid → Ester + Water' },
                { activity: 'Ester Structure', duration: '20 min', description: '-COO- group' },
                { activity: 'Naming Esters', duration: '20 min', description: 'Alkyl + acid name (alkanoate)' }
              ],
              materials: ['Ester formation diagram', 'Naming examples'],
              assessment: 'Name esters from components',
              homework: 'Ester naming practice',
              teacherNotes: 'Pleasant fruit-like odors'
            },
            {
              day: 'Friday',
              title: 'Functional Group Review',
              duration: '60 min',
              objectives: ['Review all functional groups', 'Practice identification and naming'],
              introduction: 'Let\'s consolidate our functional group knowledge.',
              mainActivities: [
                { activity: 'Group Review', duration: '20 min', description: 'All functional groups covered' },
                { activity: 'Mixed Practice', duration: '25 min', description: 'Identify and name various compounds' },
                { activity: 'Quiz', duration: '15 min', description: 'Functional group identification quiz' }
              ],
              materials: ['Review chart', 'Quiz papers'],
              assessment: 'Functional group quiz',
              homework: 'Continue functional group practice',
              teacherNotes: 'Important foundation'
            }
          ]
        },
        {
          week: 2,
          topic: 'Organic Reactions',
          strand: 'Organic Chemistry',
          subStrand: 'Reaction Types',
          learningOutcomes: [
            'Understand combustion reactions',
            'Understand addition reactions',
            'Write equations for organic reactions'
          ],
          activities: ['Reaction demonstrations', 'Equation writing', 'Product prediction'],
          resources: ['Reaction examples', 'Lab materials', 'Equation worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Combustion Reactions',
              duration: '60 min',
              objectives: ['Write combustion equations', 'Understand complete vs incomplete combustion'],
              introduction: 'Burning organic compounds is combustion.',
              mainActivities: [
                { activity: 'Complete Combustion', duration: '20 min', description: 'Hydrocarbon + O₂ → CO₂ + H₂O' },
                { activity: 'Incomplete Combustion', duration: '20 min', description: 'Limited O₂ produces CO or C' },
                { activity: 'Equation Writing', duration: '20 min', description: 'Write balanced combustion equations' }
              ],
              materials: ['Combustion examples', 'Equation practice'],
              assessment: 'Balance 6 combustion equations',
              homework: 'Combustion equation worksheet',
              teacherNotes: 'Important for energy and safety'
            },
            {
              day: 'Tuesday',
              title: 'Addition Reactions',
              duration: '60 min',
              objectives: ['Understand addition to double bonds', 'Write addition equations'],
              introduction: 'Alkenes can add atoms across the double bond.',
              mainActivities: [
                { activity: 'Addition Concept', duration: '20 min', description: 'Breaking double bond, adding atoms' },
                { activity: 'Hydrogenation', duration: '20 min', description: 'Adding H₂ to make alkane' },
                { activity: 'Halogenation', duration: '20 min', description: 'Adding Br₂ to test for unsaturation' }
              ],
              materials: ['Addition diagrams', 'Equation examples'],
              assessment: 'Write addition reaction equations',
              homework: 'Addition reaction practice',
              teacherNotes: 'Bromine water test for double bonds'
            },
            {
              day: 'Wednesday',
              title: 'Substitution Reactions',
              duration: '60 min',
              objectives: ['Understand substitution in alkanes', 'Write substitution equations'],
              introduction: 'Alkanes can substitute H for other atoms.',
              mainActivities: [
                { activity: 'Substitution Concept', duration: '20 min', description: 'Replacing H with another atom' },
                { activity: 'Halogenation of Alkanes', duration: '25 min', description: 'Alkane + Cl₂ → Chloroalkane + HCl' },
                { activity: 'Practice', duration: '15 min', description: 'Write substitution equations' }
              ],
              materials: ['Substitution examples', 'Equation practice'],
              assessment: 'Write substitution equations',
              homework: 'Substitution practice',
              teacherNotes: 'Requires UV light or heat'
            },
            {
              day: 'Thursday',
              title: 'Oxidation of Alcohols',
              duration: '60 min',
              objectives: ['Understand alcohol oxidation', 'Predict oxidation products'],
              introduction: 'Alcohols can be oxidized to other compounds.',
              mainActivities: [
                { activity: 'Primary Alcohol Oxidation', duration: '20 min', description: 'Primary → Aldehyde → Carboxylic acid' },
                { activity: 'Secondary Alcohol Oxidation', duration: '20 min', description: 'Secondary → Ketone' },
                { activity: 'Tertiary Alcohols', duration: '20 min', description: 'Tertiary alcohols resist oxidation' }
              ],
              materials: ['Oxidation pathway diagram', 'Examples'],
              assessment: 'Predict oxidation products',
              homework: 'Alcohol oxidation worksheet',
              teacherNotes: 'Potassium dichromate as oxidizing agent'
            },
            {
              day: 'Friday',
              title: 'Organic Reactions Review',
              duration: '60 min',
              objectives: ['Review all organic reaction types', 'Predict products of reactions'],
              introduction: 'Let\'s review what we\'ve learned about organic reactions.',
              mainActivities: [
                { activity: 'Reaction Type Review', duration: '20 min', description: 'Combustion, addition, substitution, oxidation' },
                { activity: 'Product Prediction', duration: '25 min', description: 'Given reactants, predict products' },
                { activity: 'Quiz', duration: '15 min', description: 'Organic reactions quiz' }
              ],
              materials: ['Review materials', 'Quiz papers'],
              assessment: 'Organic reactions quiz',
              homework: 'Prepare for thermochemistry',
              teacherNotes: 'Transition to thermochemistry next week'
            }
          ]
        },
        {
          week: 3,
          topic: 'Energy Changes in Reactions',
          strand: 'Thermochemistry',
          subStrand: 'Enthalpy',
          learningOutcomes: [
            'Define enthalpy change',
            'Distinguish exothermic and endothermic reactions',
            'Draw energy diagrams'
          ],
          activities: ['Energy demonstrations', 'Diagram drawing', 'Temperature measurements'],
          resources: ['Thermometers', 'Calorimeter', 'Energy diagram templates'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Thermochemistry',
              duration: '60 min',
              objectives: ['Define enthalpy', 'Understand energy in chemical reactions'],
              introduction: 'Chemical reactions involve energy changes.',
              mainActivities: [
                { activity: 'Enthalpy Definition', duration: '20 min', description: 'Heat content of a system at constant pressure' },
                { activity: 'Enthalpy Change (ΔH)', duration: '20 min', description: 'Change in heat during reaction' },
                { activity: 'Sign Conventions', duration: '20 min', description: 'Negative = heat released, Positive = heat absorbed' }
              ],
              materials: ['Enthalpy diagrams', 'Definition cards'],
              assessment: 'Explain enthalpy and ΔH',
              homework: 'Enthalpy concept questions',
              teacherNotes: 'Foundation for all thermochemistry'
            },
            {
              day: 'Tuesday',
              title: 'Exothermic Reactions',
              duration: '60 min',
              objectives: ['Define exothermic reactions', 'Identify exothermic examples'],
              introduction: 'Some reactions release heat - we can feel them get warm.',
              mainActivities: [
                { activity: 'Exothermic Definition', duration: '15 min', description: 'Heat released, ΔH is negative' },
                { activity: 'Examples', duration: '25 min', description: 'Combustion, neutralization, respiration' },
                { activity: 'Demonstration', duration: '20 min', description: 'Observe exothermic reaction' }
              ],
              materials: ['Exothermic reaction materials', 'Thermometer'],
              assessment: 'List 5 exothermic reactions',
              homework: 'Find exothermic reactions at home',
              teacherNotes: 'Safety with exothermic reactions'
            },
            {
              day: 'Wednesday',
              title: 'Endothermic Reactions',
              duration: '60 min',
              objectives: ['Define endothermic reactions', 'Identify endothermic examples'],
              introduction: 'Some reactions absorb heat - they feel cold.',
              mainActivities: [
                { activity: 'Endothermic Definition', duration: '15 min', description: 'Heat absorbed, ΔH is positive' },
                { activity: 'Examples', duration: '25 min', description: 'Photosynthesis, dissolving NH₄NO₃' },
                { activity: 'Demonstration', duration: '20 min', description: 'Observe endothermic reaction' }
              ],
              materials: ['Endothermic reaction materials', 'Thermometer'],
              assessment: 'List 5 endothermic reactions',
              homework: 'Compare exo- and endothermic',
              teacherNotes: 'Cold packs use endothermic reactions'
            },
            {
              day: 'Thursday',
              title: 'Energy Level Diagrams',
              duration: '60 min',
              objectives: ['Draw energy diagrams for reactions', 'Show activation energy'],
              introduction: 'Diagrams help visualize energy changes.',
              mainActivities: [
                { activity: 'Exothermic Diagram', duration: '20 min', description: 'Products lower than reactants' },
                { activity: 'Endothermic Diagram', duration: '20 min', description: 'Products higher than reactants' },
                { activity: 'Activation Energy', duration: '20 min', description: 'Energy barrier to start reaction' }
              ],
              materials: ['Diagram templates', 'Drawing practice'],
              assessment: 'Draw correct energy diagrams',
              homework: 'Energy diagram worksheet',
              teacherNotes: 'Diagrams are common exam questions'
            },
            {
              day: 'Friday',
              title: 'Measuring Enthalpy Changes',
              duration: '60 min',
              objectives: ['Understand calorimetry', 'Calculate ΔH from temperature change'],
              introduction: 'We can measure heat changes in the lab.',
              mainActivities: [
                { activity: 'Calorimetry Concept', duration: '15 min', description: 'Measure temperature change' },
                { activity: 'Heat Formula', duration: '20 min', description: 'q = mcΔT' },
                { activity: 'Calculation Practice', duration: '25 min', description: 'Calculate heat from data' }
              ],
              materials: ['Calorimeter', 'Calculation worksheets'],
              assessment: 'Calculate heat from temperature data',
              homework: 'Calorimetry calculations',
              teacherNotes: 'Specific heat of water = 4.18 J/g°C'
            }
          ]
        },
        {
          week: 4,
          topic: 'Hess\'s Law',
          strand: 'Thermochemistry',
          subStrand: 'Enthalpy Calculations',
          learningOutcomes: [
            'State Hess\'s Law',
            'Calculate ΔH using Hess\'s Law',
            'Apply enthalpy cycles'
          ],
          activities: ['Hess\'s Law calculations', 'Enthalpy cycle construction', 'Problem solving'],
          resources: ['Enthalpy data tables', 'Cycle diagrams', 'Practice problems'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Hess\'s Law',
              duration: '60 min',
              objectives: ['State Hess\'s Law', 'Understand its significance'],
              introduction: 'Hess\'s Law lets us calculate ΔH for any reaction.',
              mainActivities: [
                { activity: 'Hess\'s Law Statement', duration: '20 min', description: 'Total ΔH is independent of pathway' },
                { activity: 'Why It Works', duration: '20 min', description: 'Enthalpy is a state function' },
                { activity: 'Simple Example', duration: '20 min', description: 'One-step vs two-step reactions' }
              ],
              materials: ['Hess\'s Law diagrams', 'Examples'],
              assessment: 'Explain Hess\'s Law in own words',
              homework: 'Hess\'s Law conceptual questions',
              teacherNotes: 'Key concept for indirect calculations'
            },
            {
              day: 'Tuesday',
              title: 'Applying Hess\'s Law - Method 1',
              duration: '60 min',
              objectives: ['Add equations to find target equation', 'Combine ΔH values'],
              introduction: 'We can add reactions like mathematical equations.',
              mainActivities: [
                { activity: 'Adding Equations', duration: '20 min', description: 'Combine given equations to get target' },
                { activity: 'Reversing Equations', duration: '20 min', description: 'Reverse equation, change sign of ΔH' },
                { activity: 'Practice', duration: '20 min', description: 'Hess\'s Law calculations' }
              ],
              materials: ['Practice problems', 'Method guide'],
              assessment: 'Complete 4 Hess\'s Law calculations',
              homework: 'Hess\'s Law worksheet',
              teacherNotes: 'Algebraic manipulation of equations'
            },
            {
              day: 'Wednesday',
              title: 'Enthalpy Cycles',
              duration: '60 min',
              objectives: ['Construct enthalpy cycles', 'Use cycles to calculate ΔH'],
              introduction: 'Enthalpy cycles visually represent Hess\'s Law.',
              mainActivities: [
                { activity: 'Cycle Construction', duration: '25 min', description: 'Draw enthalpy cycle for reactions' },
                { activity: 'Using Cycles', duration: '20 min', description: 'Sum of clockwise = sum of anticlockwise' },
                { activity: 'Practice', duration: '15 min', description: 'Solve using enthalpy cycles' }
              ],
              materials: ['Cycle templates', 'Practice problems'],
              assessment: 'Construct and use enthalpy cycle',
              homework: 'Enthalpy cycle practice',
              teacherNotes: 'Visual approach helps many students'
            },
            {
              day: 'Thursday',
              title: 'Standard Enthalpy of Formation',
              duration: '60 min',
              objectives: ['Define standard enthalpy of formation', 'Use ΔHf values in calculations'],
              introduction: 'Formation enthalpies are very useful for calculations.',
              mainActivities: [
                { activity: 'ΔHf Definition', duration: '15 min', description: 'Enthalpy change forming 1 mol from elements' },
                { activity: 'Using ΔHf Values', duration: '25 min', description: 'ΔH = ΣΔHf(products) - ΣΔHf(reactants)' },
                { activity: 'Calculations', duration: '20 min', description: 'Calculate ΔH using ΔHf data' }
              ],
              materials: ['ΔHf data tables', 'Calculation practice'],
              assessment: 'Calculate ΔH using formation enthalpies',
              homework: 'Formation enthalpy calculations',
              teacherNotes: 'ΔHf of elements = 0'
            },
            {
              day: 'Friday',
              title: 'Thermochemistry Review',
              duration: '60 min',
              objectives: ['Review all thermochemistry concepts', 'Practice calculations'],
              introduction: 'Let\'s consolidate our thermochemistry knowledge.',
              mainActivities: [
                { activity: 'Concept Review', duration: '20 min', description: 'ΔH, exo/endo, Hess\'s Law, ΔHf' },
                { activity: 'Mixed Problems', duration: '25 min', description: 'Various thermochemistry calculations' },
                { activity: 'Quiz', duration: '15 min', description: 'Thermochemistry quiz' }
              ],
              materials: ['Review materials', 'Quiz papers'],
              assessment: 'Thermochemistry quiz',
              homework: 'Continue practice',
              teacherNotes: 'Assessment of understanding'
            }
          ]
        },
        {
          week: 5,
          topic: 'Reaction Rates',
          strand: 'Chemical Kinetics',
          subStrand: 'Rate of Reaction',
          learningOutcomes: [
            'Define rate of reaction',
            'Measure reaction rates',
            'Identify factors affecting rate'
          ],
          activities: ['Rate measurements', 'Factor investigations', 'Rate calculations'],
          resources: ['Stopwatches', 'Reaction materials', 'Data collection sheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Reaction Rates',
              duration: '60 min',
              objectives: ['Define rate of reaction', 'Understand rate measurement'],
              introduction: 'Some reactions are fast, others are slow.',
              mainActivities: [
                { activity: 'Rate Definition', duration: '20 min', description: 'Change in concentration per unit time' },
                { activity: 'Measuring Rate', duration: '25 min', description: 'Gas volume, mass loss, color change' },
                { activity: 'Examples', duration: '15 min', description: 'Compare fast and slow reactions' }
              ],
              materials: ['Rate concept diagrams', 'Examples'],
              assessment: 'Define rate and explain measurement',
              homework: 'Rate concept questions',
              teacherNotes: 'Practical examples help understanding'
            },
            {
              day: 'Tuesday',
              title: 'Effect of Concentration',
              duration: '60 min',
              objectives: ['Understand effect of concentration on rate', 'Explain using collision theory'],
              introduction: 'More reactant particles means more collisions.',
              mainActivities: [
                { activity: 'Concentration Effect', duration: '20 min', description: 'Higher concentration = faster rate' },
                { activity: 'Collision Theory', duration: '20 min', description: 'More particles, more collision opportunities' },
                { activity: 'Investigation', duration: '20 min', description: 'Observe concentration effect' }
              ],
              materials: ['Reaction materials', 'Different concentrations'],
              assessment: 'Explain concentration effect',
              homework: 'Concentration and rate questions',
              teacherNotes: 'Acid-carbonate reaction works well'
            },
            {
              day: 'Wednesday',
              title: 'Effect of Temperature',
              duration: '60 min',
              objectives: ['Understand temperature effect on rate', 'Explain using kinetic energy'],
              introduction: 'Heating usually makes reactions faster.',
              mainActivities: [
                { activity: 'Temperature Effect', duration: '20 min', description: 'Higher temperature = faster rate' },
                { activity: 'Energy Explanation', duration: '20 min', description: 'More particles have activation energy' },
                { activity: 'Investigation', duration: '20 min', description: 'Observe temperature effect' }
              ],
              materials: ['Thermometers', 'Heating equipment', 'Reaction materials'],
              assessment: 'Explain temperature effect on rate',
              homework: 'Temperature and rate questions',
              teacherNotes: 'Safety with hot solutions'
            },
            {
              day: 'Thursday',
              title: 'Effect of Surface Area',
              duration: '60 min',
              objectives: ['Understand surface area effect', 'Explain using collision theory'],
              introduction: 'Smaller pieces react faster.',
              mainActivities: [
                { activity: 'Surface Area Effect', duration: '20 min', description: 'Larger surface area = faster rate' },
                { activity: 'Collision Explanation', duration: '20 min', description: 'More surface exposed for collisions' },
                { activity: 'Investigation', duration: '20 min', description: 'Compare powder vs lumps' }
              ],
              materials: ['Chalk/marble in different sizes', 'Acid'],
              assessment: 'Explain surface area effect',
              homework: 'Surface area questions',
              teacherNotes: 'Dramatic difference with powder'
            },
            {
              day: 'Friday',
              title: 'Catalysts',
              duration: '60 min',
              objectives: ['Define catalyst', 'Explain how catalysts work'],
              introduction: 'Catalysts speed reactions without being used up.',
              mainActivities: [
                { activity: 'Catalyst Definition', duration: '15 min', description: 'Speeds reaction, unchanged at end' },
                { activity: 'How Catalysts Work', duration: '20 min', description: 'Lower activation energy' },
                { activity: 'Examples', duration: '25 min', description: 'Enzymes, industrial catalysts' }
              ],
              materials: ['Catalyst examples', 'Diagrams'],
              assessment: 'Explain how catalysts work',
              homework: 'Catalyst research',
              teacherNotes: 'Enzymes are biological catalysts'
            }
          ]
        },
        {
          week: 6,
          topic: 'Chemical Equilibrium',
          strand: 'Chemical Kinetics',
          subStrand: 'Equilibrium',
          learningOutcomes: [
            'Define chemical equilibrium',
            'Understand dynamic equilibrium',
            'Write equilibrium expressions'
          ],
          activities: ['Equilibrium demonstrations', 'Expression writing', 'Le Chatelier\'s principle'],
          resources: ['Equilibrium demonstrations', 'Expression worksheets', 'Simulation software'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Equilibrium',
              duration: '60 min',
              objectives: ['Define chemical equilibrium', 'Understand reversible reactions'],
              introduction: 'Some reactions can go both forward and backward.',
              mainActivities: [
                { activity: 'Reversible Reactions', duration: '20 min', description: 'Products can reform reactants' },
                { activity: 'Equilibrium State', duration: '25 min', description: 'Forward rate = reverse rate' },
                { activity: 'Dynamic Nature', duration: '15 min', description: 'Reactions still occurring' }
              ],
              materials: ['Equilibrium diagrams', 'Reversible reaction examples'],
              assessment: 'Define equilibrium and explain dynamic nature',
              homework: 'Equilibrium concept questions',
              teacherNotes: 'Emphasize dynamic, not static'
            },
            {
              day: 'Tuesday',
              title: 'Equilibrium Constant',
              duration: '60 min',
              objectives: ['Write equilibrium expressions', 'Understand Kc'],
              introduction: 'We can quantify equilibrium position.',
              mainActivities: [
                { activity: 'Kc Expression', duration: '25 min', description: 'Products over reactants, raised to coefficients' },
                { activity: 'Writing Expressions', duration: '20 min', description: 'Practice writing Kc for reactions' },
                { activity: 'Interpreting Kc', duration: '15 min', description: 'Large Kc = products favored' }
              ],
              materials: ['Expression examples', 'Practice reactions'],
              assessment: 'Write Kc expressions correctly',
              homework: 'Kc expression worksheet',
              teacherNotes: 'Only include aqueous and gases'
            },
            {
              day: 'Wednesday',
              title: 'Le Chatelier\'s Principle',
              duration: '60 min',
              objectives: ['State Le Chatelier\'s Principle', 'Predict equilibrium shifts'],
              introduction: 'Systems respond to stress to restore equilibrium.',
              mainActivities: [
                { activity: 'Principle Statement', duration: '15 min', description: 'System shifts to counteract change' },
                { activity: 'Concentration Changes', duration: '20 min', description: 'Adding/removing reactants or products' },
                { activity: 'Predictions', duration: '25 min', description: 'Predict direction of shift' }
              ],
              materials: ['Le Chatelier diagrams', 'Prediction practice'],
              assessment: 'Predict shifts for various changes',
              homework: 'Le Chatelier practice',
              teacherNotes: 'Key concept for equilibrium'
            },
            {
              day: 'Thursday',
              title: 'Temperature and Pressure Effects',
              duration: '60 min',
              objectives: ['Predict effect of temperature on equilibrium', 'Predict effect of pressure on equilibrium'],
              introduction: 'Temperature and pressure also affect equilibrium.',
              mainActivities: [
                { activity: 'Temperature Effect', duration: '25 min', description: 'Shifts to absorb or release heat' },
                { activity: 'Pressure Effect', duration: '25 min', description: 'Shifts to side with fewer gas moles' },
                { activity: 'Practice', duration: '10 min', description: 'Mixed predictions' }
              ],
              materials: ['Effect diagrams', 'Practice problems'],
              assessment: 'Predict temperature and pressure effects',
              homework: 'Equilibrium shift worksheet',
              teacherNotes: 'Temperature changes Kc'
            },
            {
              day: 'Friday',
              title: 'Equilibrium Review',
              duration: '60 min',
              objectives: ['Review equilibrium concepts', 'Practice equilibrium problems'],
              introduction: 'Let\'s consolidate our equilibrium knowledge.',
              mainActivities: [
                { activity: 'Concept Review', duration: '20 min', description: 'Kc, Le Chatelier, shifts' },
                { activity: 'Problem Practice', duration: '25 min', description: 'Mixed equilibrium problems' },
                { activity: 'Quiz', duration: '15 min', description: 'Equilibrium quiz' }
              ],
              materials: ['Review materials', 'Quiz papers'],
              assessment: 'Equilibrium quiz',
              homework: 'Continue practice',
              teacherNotes: 'Important for Year 12 chemistry'
            }
          ]
        },
        {
          week: 7,
          topic: 'Acids and Bases - Advanced',
          strand: 'Acids and Bases',
          subStrand: 'Acid-Base Theory',
          learningOutcomes: [
            'Understand Brønsted-Lowry theory',
            'Identify conjugate acid-base pairs',
            'Calculate pH'
          ],
          activities: ['Theory application', 'pH calculations', 'Conjugate pair identification'],
          resources: ['pH meters', 'Indicators', 'Calculation worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Brønsted-Lowry Theory',
              duration: '60 min',
              objectives: ['State Brønsted-Lowry definitions', 'Identify proton donors and acceptors'],
              introduction: 'A more comprehensive theory of acids and bases.',
              mainActivities: [
                { activity: 'Brønsted Acid', duration: '20 min', description: 'Proton (H⁺) donor' },
                { activity: 'Brønsted Base', duration: '20 min', description: 'Proton (H⁺) acceptor' },
                { activity: 'Identification', duration: '20 min', description: 'Find acids and bases in reactions' }
              ],
              materials: ['Theory diagrams', 'Example reactions'],
              assessment: 'Identify Brønsted acids and bases',
              homework: 'Brønsted theory worksheet',
              teacherNotes: 'More general than Arrhenius theory'
            },
            {
              day: 'Tuesday',
              title: 'Conjugate Acid-Base Pairs',
              duration: '60 min',
              objectives: ['Identify conjugate pairs', 'Understand proton transfer'],
              introduction: 'Acids and bases come in conjugate pairs.',
              mainActivities: [
                { activity: 'Conjugate Concept', duration: '20 min', description: 'Differ by one H⁺' },
                { activity: 'Finding Pairs', duration: '25 min', description: 'Identify conjugate pairs in reactions' },
                { activity: 'Practice', duration: '15 min', description: 'Conjugate pair exercises' }
              ],
              materials: ['Pair examples', 'Practice reactions'],
              assessment: 'Correctly identify conjugate pairs',
              homework: 'Conjugate pair worksheet',
              teacherNotes: 'Two conjugate pairs per reaction'
            },
            {
              day: 'Wednesday',
              title: 'pH Scale and Calculations',
              duration: '60 min',
              objectives: ['Calculate pH from [H⁺]', 'Calculate [H⁺] from pH'],
              introduction: 'pH is a measure of hydrogen ion concentration.',
              mainActivities: [
                { activity: 'pH Formula', duration: '15 min', description: 'pH = -log[H⁺]' },
                { activity: 'Calculating pH', duration: '20 min', description: 'Find pH from concentration' },
                { activity: 'Finding [H⁺]', duration: '25 min', description: '[H⁺] = 10^(-pH)' }
              ],
              materials: ['Calculators', 'pH calculation practice'],
              assessment: 'Complete pH calculations',
              homework: 'pH calculation worksheet',
              teacherNotes: 'Scientific calculator needed'
            },
            {
              day: 'Thursday',
              title: 'Strong vs Weak Acids',
              duration: '60 min',
              objectives: ['Distinguish strong from weak acids', 'Understand degree of ionization'],
              introduction: 'Not all acids ionize completely.',
              mainActivities: [
                { activity: 'Strong Acids', duration: '20 min', description: 'Complete ionization, e.g., HCl, HNO₃' },
                { activity: 'Weak Acids', duration: '20 min', description: 'Partial ionization, e.g., CH₃COOH' },
                { activity: 'pH Comparison', duration: '20 min', description: 'Same concentration, different pH' }
              ],
              materials: ['Acid strength diagrams', 'pH data'],
              assessment: 'Explain difference between strong and weak acids',
              homework: 'Strong/weak acid questions',
              teacherNotes: 'Important distinction'
            },
            {
              day: 'Friday',
              title: 'Acid-Base Review',
              duration: '60 min',
              objectives: ['Review advanced acid-base concepts', 'Practice calculations'],
              introduction: 'Let\'s review what we\'ve learned.',
              mainActivities: [
                { activity: 'Concept Review', duration: '20 min', description: 'Brønsted theory, conjugates, pH' },
                { activity: 'Mixed Practice', duration: '25 min', description: 'Various acid-base problems' },
                { activity: 'Quiz', duration: '15 min', description: 'Acid-base quiz' }
              ],
              materials: ['Review materials', 'Quiz papers'],
              assessment: 'Acid-base quiz',
              homework: 'Continue practice',
              teacherNotes: 'Assessment of understanding'
            }
          ]
        },
        {
          week: 8,
          topic: 'Practical Applications',
          strand: 'Applied Chemistry',
          subStrand: 'Real-World Chemistry',
          learningOutcomes: [
            'Apply chemistry to everyday situations',
            'Understand industrial processes',
            'Connect chemistry to Vanuatu context'
          ],
          activities: ['Case studies', 'Local applications', 'Project work'],
          resources: ['Case study materials', 'Local examples', 'Research resources'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Chemistry in Agriculture',
              duration: '60 min',
              objectives: ['Understand fertilizers and soil chemistry', 'Apply concepts to local agriculture'],
              introduction: 'Chemistry is essential for farming.',
              mainActivities: [
                { activity: 'Fertilizers', duration: '25 min', description: 'NPK and their roles in plants' },
                { activity: 'Soil pH', duration: '20 min', description: 'Importance of soil pH for crops' },
                { activity: 'Local Applications', duration: '15 min', description: 'Vanuatu crops and chemistry' }
              ],
              materials: ['Fertilizer samples', 'Soil pH data'],
              assessment: 'Explain role of chemistry in agriculture',
              homework: 'Research local farming practices',
              teacherNotes: 'Relevant to many students'
            },
            {
              day: 'Tuesday',
              title: 'Chemistry in Health',
              duration: '60 min',
              objectives: ['Understand medicines and chemistry', 'Apply acid-base concepts to health'],
              introduction: 'Chemistry keeps us healthy.',
              mainActivities: [
                { activity: 'Medicines', duration: '20 min', description: 'Drugs as chemical compounds' },
                { activity: 'Antacids', duration: '20 min', description: 'Neutralizing stomach acid' },
                { activity: 'Local Health', duration: '20 min', description: 'Traditional and modern medicine' }
              ],
              materials: ['Medicine examples', 'Antacid demonstration'],
              assessment: 'Explain chemistry in health',
              homework: 'Research local medicinal plants',
              teacherNotes: 'Connect to local knowledge'
            },
            {
              day: 'Wednesday',
              title: 'Environmental Chemistry',
              duration: '60 min',
              objectives: ['Understand environmental issues', 'Apply chemistry to environmental problems'],
              introduction: 'Chemistry helps us understand environmental issues.',
              mainActivities: [
                { activity: 'Climate Change', duration: '20 min', description: 'Greenhouse gases and chemistry' },
                { activity: 'Ocean Acidification', duration: '20 min', description: 'CO₂ and ocean pH' },
                { activity: 'Local Environment', duration: '20 min', description: 'Vanuatu environmental concerns' }
              ],
              materials: ['Environmental data', 'Local examples'],
              assessment: 'Explain chemistry of environmental issue',
              homework: 'Research local environmental issue',
              teacherNotes: 'Relevant to island communities'
            },
            {
              day: 'Thursday',
              title: 'Chemistry in Industry',
              duration: '60 min',
              objectives: ['Understand industrial chemistry', 'Apply concepts to manufacturing'],
              introduction: 'Chemistry makes the products we use.',
              mainActivities: [
                { activity: 'Petrochemicals', duration: '20 min', description: 'Fuels and plastics from oil' },
                { activity: 'Food Processing', duration: '20 min', description: 'Chemistry in food preservation' },
                { activity: 'Local Industry', duration: '20 min', description: 'Vanuatu industries using chemistry' }
              ],
              materials: ['Industry examples', 'Local products'],
              assessment: 'Explain industrial application of chemistry',
              homework: 'Research local industry',
              teacherNotes: 'Connect to employment opportunities'
            },
            {
              day: 'Friday',
              title: 'Chemistry Careers',
              duration: '60 min',
              objectives: ['Explore chemistry-related careers', 'Plan for future studies'],
              introduction: 'Where can chemistry take you?',
              mainActivities: [
                { activity: 'Career Options', duration: '25 min', description: 'Pharmacy, nursing, engineering, research' },
                { activity: 'Study Pathways', duration: '20 min', description: 'Requirements for chemistry careers' },
                { activity: 'Goal Setting', duration: '15 min', description: 'Personal career planning' }
              ],
              materials: ['Career information', 'Study guides'],
              assessment: 'Identify potential career paths',
              homework: 'Research career of interest',
              teacherNotes: 'Motivational and practical'
            }
          ]
        },
        {
          week: 9,
          topic: 'Review and Exam Preparation',
          strand: 'Review',
          subStrand: 'Term 3 Revision',
          learningOutcomes: [
            'Review all Term 3 content',
            'Practice exam techniques',
            'Identify areas for improvement'
          ],
          activities: ['Comprehensive review', 'Practice tests', 'Study skills'],
          resources: ['Review materials', 'Practice papers', 'Study guides'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Organic Chemistry Review',
              duration: '60 min',
              objectives: ['Review functional groups and reactions'],
              introduction: 'Let\'s review organic chemistry.',
              mainActivities: [
                { activity: 'Functional Groups', duration: '25 min', description: 'Review all groups learned' },
                { activity: 'Organic Reactions', duration: '25 min', description: 'Review reaction types' },
                { activity: 'Practice', duration: '10 min', description: 'Mixed organic problems' }
              ],
              materials: ['Organic review sheet', 'Practice problems'],
              assessment: 'Complete organic review problems',
              homework: 'Study organic chemistry',
              teacherNotes: 'Focus on naming and reactions'
            },
            {
              day: 'Tuesday',
              title: 'Thermochemistry and Kinetics Review',
              duration: '60 min',
              objectives: ['Review energy and rate concepts'],
              introduction: 'Energy and rates are key topics.',
              mainActivities: [
                { activity: 'Thermochemistry Review', duration: '25 min', description: 'ΔH, Hess\'s Law' },
                { activity: 'Kinetics Review', duration: '25 min', description: 'Rates, factors, equilibrium' },
                { activity: 'Practice', duration: '10 min', description: 'Mixed problems' }
              ],
              materials: ['Review materials', 'Practice problems'],
              assessment: 'Complete thermo/kinetics problems',
              homework: 'Study thermochemistry and kinetics',
              teacherNotes: 'Calculation practice important'
            },
            {
              day: 'Wednesday',
              title: 'Acids and Bases Review',
              duration: '60 min',
              objectives: ['Review acid-base concepts and calculations'],
              introduction: 'Acids and bases are fundamental.',
              mainActivities: [
                { activity: 'Theory Review', duration: '20 min', description: 'Brønsted-Lowry, conjugates' },
                { activity: 'pH Calculations', duration: '25 min', description: 'pH calculation practice' },
                { activity: 'Application Problems', duration: '15 min', description: 'Applied acid-base problems' }
              ],
              materials: ['Review sheet', 'Calculation practice'],
              assessment: 'Complete acid-base problems',
              homework: 'Study acids and bases',
              teacherNotes: 'Calculator skills important'
            },
            {
              day: 'Thursday',
              title: 'Practice Examination',
              duration: '60 min',
              objectives: ['Complete practice exam', 'Identify weak areas'],
              introduction: 'Practice under exam conditions.',
              mainActivities: [
                { activity: 'Practice Exam', duration: '50 min', description: 'Full practice assessment' },
                { activity: 'Quick Review', duration: '10 min', description: 'Check key answers' }
              ],
              materials: ['Practice exam papers', 'Calculators'],
              assessment: 'Complete practice examination',
              homework: 'Review incorrect answers',
              teacherNotes: 'Exam conditions preparation'
            },
            {
              day: 'Friday',
              title: 'Final Preparation',
              duration: '60 min',
              objectives: ['Address remaining questions', 'Build exam confidence'],
              introduction: 'Last chance to ask questions before the exam.',
              mainActivities: [
                { activity: 'Q&A Session', duration: '30 min', description: 'Answer student questions' },
                { activity: 'Exam Tips', duration: '15 min', description: 'Time management, strategies' },
                { activity: 'Confidence Building', duration: '15 min', description: 'Review achievements' }
              ],
              materials: ['Review materials'],
              assessment: 'Participate in Q&A',
              homework: 'Rest and prepare for exam',
              teacherNotes: 'Supportive atmosphere'
            }
          ]
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'Assessment',
          subStrand: 'Examination',
          learningOutcomes: [
            'Demonstrate understanding of Term 3 content',
            'Apply chemistry knowledge and skills',
            'Complete year 11 chemistry successfully'
          ],
          activities: ['Term examination', 'Practical assessment', 'Course completion'],
          resources: ['Exam papers', 'Lab materials', 'Certificates'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Written Examination - Part 1',
              duration: '60 min',
              objectives: ['Complete first part of term exam'],
              introduction: 'Examination time - do your best!',
              mainActivities: [
                { activity: 'Examination', duration: '55 min', description: 'Organic and thermochemistry' },
                { activity: 'Submission', duration: '5 min', description: 'Submit papers' }
              ],
              materials: ['Exam papers', 'Calculators'],
              assessment: 'Part 1 examination',
              homework: 'Prepare for Part 2',
              teacherNotes: 'Strict exam conditions'
            },
            {
              day: 'Tuesday',
              title: 'Written Examination - Part 2',
              duration: '60 min',
              objectives: ['Complete second part of term exam'],
              introduction: 'Continue your examination.',
              mainActivities: [
                { activity: 'Examination', duration: '55 min', description: 'Kinetics, equilibrium, acids/bases' },
                { activity: 'Submission', duration: '5 min', description: 'Submit papers' }
              ],
              materials: ['Exam papers', 'Calculators', 'Periodic tables'],
              assessment: 'Part 2 examination',
              homework: 'Prepare for practical',
              teacherNotes: 'Provide periodic table and formulas'
            },
            {
              day: 'Wednesday',
              title: 'Practical Assessment',
              duration: '60 min',
              objectives: ['Demonstrate practical chemistry skills'],
              introduction: 'Show your laboratory skills.',
              mainActivities: [
                { activity: 'Practical Task', duration: '45 min', description: 'Complete assigned practical' },
                { activity: 'Write-up', duration: '15 min', description: 'Complete lab report' }
              ],
              materials: ['Lab equipment', 'Chemicals', 'Report sheets'],
              assessment: 'Practical examination',
              homework: 'None',
              teacherNotes: 'Assess technique and accuracy'
            },
            {
              day: 'Thursday',
              title: 'Exam Review',
              duration: '60 min',
              objectives: ['Review examination', 'Learn from mistakes'],
              introduction: 'Let\'s look at the exam and learn.',
              mainActivities: [
                { activity: 'Paper Review', duration: '35 min', description: 'Go through exam questions' },
                { activity: 'Discussion', duration: '15 min', description: 'Discuss common errors' },
                { activity: 'Feedback', duration: '10 min', description: 'Individual feedback' }
              ],
              materials: ['Marked papers', 'Answer keys'],
              assessment: 'None',
              homework: 'Reflect on learning',
              teacherNotes: 'Learning opportunity from exam'
            },
            {
              day: 'Friday',
              title: 'Course Completion',
              duration: '60 min',
              objectives: ['Complete Year 11 Chemistry', 'Prepare for Year 12'],
              introduction: 'Congratulations on completing Year 11!',
              mainActivities: [
                { activity: 'Grade Distribution', duration: '15 min', description: 'Return final grades' },
                { activity: 'Year 12 Preview', duration: '20 min', description: 'What to expect next year' },
                { activity: 'Celebration', duration: '25 min', description: 'Celebrate achievements' }
              ],
              materials: ['Grade reports', 'Year 12 outline'],
              assessment: 'None',
              homework: 'Enjoy your break!',
              teacherNotes: 'End on positive note'
            }
          ]
        }
      ]
    }
  },
  year12: {
    term1: {
      name: 'Term 1',
      focus: 'Stoichiometry and Chemical Equilibrium',
      weeks: 10,
      scheme: [
        {
          week: 1,
          topic: 'The Mole Concept',
          strand: 'Quantitative Chemistry',
          subStrand: 'Stoichiometry',
          learningOutcomes: [
            'Define the mole and Avogadro\'s number',
            'Calculate molar mass of compounds',
            'Convert between moles, mass, and particles'
          ],
          activities: [
            'Mole calculations practice',
            'Mass-mole conversions',
            'Problem-solving exercises'
          ],
          resources: ['Periodic table', 'Calculators', 'Practice worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to the Mole',
              duration: '60 min',
              objectives: ['Define the mole', 'Understand Avogadro\'s number'],
              introduction: 'How do chemists count atoms when they\'re too small to see?',
              mainActivities: [
                { activity: 'Mole Definition', duration: '20 min', description: 'The mole as a counting unit - 6.022 × 10²³ particles' },
                { activity: 'Avogadro\'s Number', duration: '20 min', description: 'Understanding the significance of this constant' },
                { activity: 'Conceptual Practice', duration: '20 min', description: 'Relating moles to everyday counting units' }
              ],
              materials: ['Mole concept diagrams', 'Visual aids'],
              assessment: 'Explain the mole concept in own words',
              homework: 'Research Amedeo Avogadro',
              teacherNotes: 'Use analogies like dozen, gross to explain mole'
            },
            {
              day: 'Tuesday',
              title: 'Molar Mass Calculations',
              duration: '60 min',
              objectives: ['Calculate molar mass from periodic table', 'Find molar mass of compounds'],
              introduction: 'Every element has a specific mass per mole.',
              mainActivities: [
                { activity: 'Atomic Mass Review', duration: '15 min', description: 'Reading atomic mass from periodic table' },
                { activity: 'Molar Mass of Elements', duration: '20 min', description: 'Molar mass equals atomic mass in g/mol' },
                { activity: 'Compound Molar Mass', duration: '25 min', description: 'Add up atomic masses for compounds like H₂O, NaCl' }
              ],
              materials: ['Periodic table', 'Calculators', 'Practice problems'],
              assessment: 'Calculate molar mass of 10 compounds',
              homework: 'Complete molar mass worksheet',
              teacherNotes: 'Ensure students can read periodic table accurately'
            },
            {
              day: 'Wednesday',
              title: 'Mole-Mass Conversions',
              duration: '60 min',
              objectives: ['Convert moles to mass', 'Convert mass to moles'],
              introduction: 'The formula: n = m/M connects moles, mass, and molar mass.',
              mainActivities: [
                { activity: 'Formula Introduction', duration: '15 min', description: 'n = m/M where n=moles, m=mass, M=molar mass' },
                { activity: 'Moles to Mass', duration: '20 min', description: 'Calculate mass from given moles' },
                { activity: 'Mass to Moles', duration: '25 min', description: 'Calculate moles from given mass' }
              ],
              materials: ['Formula sheet', 'Calculators', 'Practice problems'],
              assessment: 'Complete 10 conversion problems',
              homework: 'Mixed conversion practice',
              teacherNotes: 'Triangle method helps students rearrange formula'
            },
            {
              day: 'Thursday',
              title: 'Mole-Particle Conversions',
              duration: '60 min',
              objectives: ['Convert moles to number of particles', 'Convert particles to moles'],
              introduction: 'How many molecules are in 2 moles of water?',
              mainActivities: [
                { activity: 'Particles Formula', duration: '15 min', description: 'N = n × Nₐ where N=particles, n=moles, Nₐ=Avogadro\'s number' },
                { activity: 'Moles to Particles', duration: '20 min', description: 'Calculate number of atoms/molecules' },
                { activity: 'Particles to Moles', duration: '25 min', description: 'Calculate moles from particle count' }
              ],
              materials: ['Scientific calculators', 'Practice problems'],
              assessment: 'Complete particle conversion problems',
              homework: 'Mixed particle-mole conversions',
              teacherNotes: 'Scientific notation practice may be needed'
            },
            {
              day: 'Friday',
              title: 'Combined Mole Calculations',
              duration: '60 min',
              objectives: ['Solve multi-step mole problems', 'Apply all conversion formulas'],
              introduction: 'Let\'s put all our mole skills together!',
              mainActivities: [
                { activity: 'Review All Formulas', duration: '15 min', description: 'n=m/M and N=n×Nₐ review' },
                { activity: 'Multi-step Problems', duration: '30 min', description: 'Mass to particles, particles to mass' },
                { activity: 'Week Quiz', duration: '15 min', description: 'Assessment on mole concept' }
              ],
              materials: ['Formula sheet', 'Quiz papers'],
              assessment: 'Score 70%+ on mole concept quiz',
              homework: 'Study for next week - concentration',
              teacherNotes: 'Foundation for all stoichiometry calculations'
            }
          ]
        },
        {
          week: 2,
          topic: 'Concentration and Solutions',
          strand: 'Quantitative Chemistry',
          subStrand: 'Solutions',
          learningOutcomes: [
            'Define concentration and its units',
            'Calculate molarity of solutions',
            'Prepare solutions of known concentration'
          ],
          activities: [
            'Concentration calculations',
            'Solution preparation lab',
            'Dilution problems'
          ],
          resources: ['Volumetric flasks', 'Balances', 'Solutes'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'What is Concentration?',
              duration: '60 min',
              objectives: ['Define concentration', 'Understand molarity'],
              introduction: 'Why does strong coffee taste different from weak coffee?',
              mainActivities: [
                { activity: 'Concentration Concept', duration: '20 min', description: 'Amount of solute per unit volume of solution' },
                { activity: 'Molarity Definition', duration: '20 min', description: 'M = n/V where M=molarity, n=moles, V=volume in L' },
                { activity: 'Units Practice', duration: '20 min', description: 'mol/L or M (molar) as units' }
              ],
              materials: ['Concentration diagrams', 'Formula sheet'],
              assessment: 'Define concentration and molarity',
              homework: 'Identify concentrated vs dilute solutions',
              teacherNotes: 'Use everyday examples for concentration'
            },
            {
              day: 'Tuesday',
              title: 'Calculating Molarity',
              duration: '60 min',
              objectives: ['Calculate molarity from moles and volume', 'Solve for moles or volume'],
              introduction: 'The molarity formula is your new best friend.',
              mainActivities: [
                { activity: 'Basic Calculations', duration: '20 min', description: 'Calculate M when n and V are given' },
                { activity: 'Finding Moles', duration: '20 min', description: 'Rearrange to n = M × V' },
                { activity: 'Finding Volume', duration: '20 min', description: 'Rearrange to V = n/M' }
              ],
              materials: ['Calculators', 'Practice problems'],
              assessment: 'Complete 15 molarity calculations',
              homework: 'Mixed molarity problems',
              teacherNotes: 'Watch for unit conversions - mL to L'
            },
            {
              day: 'Wednesday',
              title: 'Solution Preparation - Theory',
              duration: '60 min',
              objectives: ['Calculate mass needed for solution preparation', 'Plan solution preparation steps'],
              introduction: 'How do we make a solution of exact concentration?',
              mainActivities: [
                { activity: 'Mass Calculation', duration: '25 min', description: 'Calculate mass of solute needed: m = M × V × Molar mass' },
                { activity: 'Step-by-Step Process', duration: '20 min', description: 'Weigh, dissolve, transfer, make up to volume' },
                { activity: 'Planning Exercise', duration: '15 min', description: 'Plan preparation of assigned solutions' }
              ],
              materials: ['Procedure guide', 'Planning sheets'],
              assessment: 'Calculate mass for 5 solution preparations',
              homework: 'Complete solution preparation calculations',
              teacherNotes: 'Preparation for practical tomorrow'
            },
            {
              day: 'Thursday',
              title: 'Solution Preparation - Practical',
              duration: '60 min',
              objectives: ['Prepare a standard solution', 'Use volumetric flask correctly'],
              introduction: 'Today we put theory into practice!',
              mainActivities: [
                { activity: 'Demonstration', duration: '15 min', description: 'Teacher demonstrates proper technique' },
                { activity: 'Student Practical', duration: '35 min', description: 'Prepare 100mL of 0.1M NaCl solution' },
                { activity: 'Clean Up', duration: '10 min', description: 'Proper disposal and equipment cleaning' }
              ],
              materials: ['Balances', 'Volumetric flasks', 'NaCl', 'Distilled water'],
              assessment: 'Successfully prepare solution',
              homework: 'Write practical report',
              teacherNotes: 'Monitor technique carefully'
            },
            {
              day: 'Friday',
              title: 'Dilution Calculations',
              duration: '60 min',
              objectives: ['Understand dilution concept', 'Use dilution formula'],
              introduction: 'How do we make weaker solutions from stronger ones?',
              mainActivities: [
                { activity: 'Dilution Concept', duration: '15 min', description: 'Adding solvent decreases concentration' },
                { activity: 'Dilution Formula', duration: '25 min', description: 'M₁V₁ = M₂V₂ and problem solving' },
                { activity: 'Week Review', duration: '20 min', description: 'Quiz on concentration and solutions' }
              ],
              materials: ['Formula sheet', 'Quiz papers'],
              assessment: 'Complete dilution problems and quiz',
              homework: 'Study for stoichiometry next week',
              teacherNotes: 'Dilution is commonly tested'
            }
          ]
        },
        {
          week: 3,
          topic: 'Stoichiometry Basics',
          strand: 'Quantitative Chemistry',
          subStrand: 'Stoichiometry',
          learningOutcomes: [
            'Interpret mole ratios from balanced equations',
            'Perform mole-to-mole calculations',
            'Calculate mass of products from reactants'
          ],
          activities: [
            'Mole ratio identification',
            'Stoichiometry calculations',
            'Mass-mass problems'
          ],
          resources: ['Balanced equations', 'Calculators', 'Worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Mole Ratios from Equations',
              duration: '60 min',
              objectives: ['Read mole ratios from balanced equations', 'Write mole ratio relationships'],
              introduction: 'Balanced equations tell us the recipe for reactions.',
              mainActivities: [
                { activity: 'Coefficient Meaning', duration: '20 min', description: 'Coefficients represent mole ratios' },
                { activity: 'Reading Ratios', duration: '25 min', description: 'Practice identifying ratios from equations' },
                { activity: 'Ratio Practice', duration: '15 min', description: 'Write all possible ratios for given equations' }
              ],
              materials: ['Equation examples', 'Practice worksheet'],
              assessment: 'Correctly identify mole ratios',
              homework: 'Mole ratio identification practice',
              teacherNotes: 'Foundation for all stoichiometry'
            },
            {
              day: 'Tuesday',
              title: 'Mole-to-Mole Calculations',
              duration: '60 min',
              objectives: ['Calculate moles of product from moles of reactant', 'Use mole ratios in calculations'],
              introduction: 'Now we use ratios to predict amounts.',
              mainActivities: [
                { activity: 'Setting Up Problems', duration: '20 min', description: 'Write given, find, ratio, calculate' },
                { activity: 'Mole Calculations', duration: '30 min', description: 'Calculate moles produced/consumed' },
                { activity: 'Check Method', duration: '10 min', description: 'Verify answers make sense' }
              ],
              materials: ['Problem sets', 'Calculators'],
              assessment: 'Complete 10 mole-to-mole problems',
              homework: 'Additional mole calculations',
              teacherNotes: 'Emphasize systematic problem-solving'
            },
            {
              day: 'Wednesday',
              title: 'Mass-to-Mass Calculations',
              duration: '60 min',
              objectives: ['Calculate mass of products from mass of reactants', 'Combine mole and mass conversions'],
              introduction: 'In the lab, we measure mass, not moles.',
              mainActivities: [
                { activity: 'Three-Step Method', duration: '20 min', description: 'Mass→Moles→Mole ratio→Mass' },
                { activity: 'Guided Practice', duration: '25 min', description: 'Work through problems together' },
                { activity: 'Independent Practice', duration: '15 min', description: 'Students solve problems alone' }
              ],
              materials: ['Periodic table', 'Calculators', 'Problems'],
              assessment: 'Complete mass-to-mass calculations',
              homework: 'Mass-mass problem set',
              teacherNotes: 'Most common type of stoichiometry problem'
            },
            {
              day: 'Thursday',
              title: 'Volume Stoichiometry (Gases)',
              duration: '60 min',
              objectives: ['Apply molar volume of gas at STP', 'Calculate gas volumes in reactions'],
              introduction: 'Gases have a special property at STP.',
              mainActivities: [
                { activity: 'Molar Volume', duration: '20 min', description: '22.4 L/mol at STP for any ideal gas' },
                { activity: 'Volume Calculations', duration: '25 min', description: 'Calculate gas volumes produced' },
                { activity: 'Mixed Problems', duration: '15 min', description: 'Mass-to-volume and volume-to-mass' }
              ],
              materials: ['Molar volume reference', 'Practice problems'],
              assessment: 'Complete gas stoichiometry problems',
              homework: 'Gas volume calculations',
              teacherNotes: 'STP = Standard Temperature and Pressure'
            },
            {
              day: 'Friday',
              title: 'Stoichiometry Review',
              duration: '60 min',
              objectives: ['Solve mixed stoichiometry problems', 'Demonstrate mastery'],
              introduction: 'Let\'s bring it all together!',
              mainActivities: [
                { activity: 'Problem Types Review', duration: '15 min', description: 'Review all stoichiometry methods' },
                { activity: 'Mixed Practice', duration: '25 min', description: 'Solve variety of problem types' },
                { activity: 'Quiz', duration: '20 min', description: 'Stoichiometry basics quiz' }
              ],
              materials: ['Review sheet', 'Quiz papers'],
              assessment: 'Stoichiometry quiz',
              homework: 'Study limiting reagent concept',
              teacherNotes: 'Ensure students have solid foundation'
            }
          ]
        },
        {
          week: 4,
          topic: 'Limiting Reagent and Percent Yield',
          strand: 'Quantitative Chemistry',
          subStrand: 'Stoichiometry',
          learningOutcomes: [
            'Identify the limiting reagent in a reaction',
            'Calculate theoretical yield',
            'Calculate percent yield from experimental data'
          ],
          activities: [
            'Limiting reagent identification',
            'Yield calculations',
            'Lab: percent yield determination'
          ],
          resources: ['Lab equipment', 'Chemicals', 'Calculators'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Limiting Reagent',
              duration: '60 min',
              objectives: ['Understand limiting reagent concept', 'Identify limiting reagent in simple cases'],
              introduction: 'What happens when you run out of one ingredient?',
              mainActivities: [
                { activity: 'Analogy Introduction', duration: '15 min', description: 'Sandwich making: bread vs cheese' },
                { activity: 'Chemical Application', duration: '25 min', description: 'Apply concept to chemical reactions' },
                { activity: 'Identification Practice', duration: '20 min', description: 'Identify limiting reagent by inspection' }
              ],
              materials: ['Visual aids', 'Practice problems'],
              assessment: 'Explain limiting reagent concept',
              homework: 'Limiting reagent identification',
              teacherNotes: 'Analogy makes concept accessible'
            },
            {
              day: 'Tuesday',
              title: 'Calculating Limiting Reagent',
              duration: '60 min',
              objectives: ['Calculate to determine limiting reagent', 'Use systematic method'],
              introduction: 'Sometimes we need to calculate to find the limit.',
              mainActivities: [
                { activity: 'Calculation Method', duration: '20 min', description: 'Calculate product from each reactant' },
                { activity: 'Comparison', duration: '20 min', description: 'Smaller amount indicates limiting reagent' },
                { activity: 'Practice Problems', duration: '20 min', description: 'Determine limiting reagent by calculation' }
              ],
              materials: ['Calculators', 'Problem sets'],
              assessment: 'Correctly identify limiting reagent',
              homework: 'Limiting reagent calculations',
              teacherNotes: 'Both methods should give same answer'
            },
            {
              day: 'Wednesday',
              title: 'Theoretical and Actual Yield',
              duration: '60 min',
              objectives: ['Define theoretical yield', 'Understand why actual yield differs'],
              introduction: 'Why don\'t we always get what we calculate?',
              mainActivities: [
                { activity: 'Theoretical Yield', duration: '20 min', description: 'Maximum possible product from limiting reagent' },
                { activity: 'Actual Yield', duration: '15 min', description: 'What we actually obtain in lab' },
                { activity: 'Reasons for Difference', duration: '25 min', description: 'Loss, side reactions, incomplete reaction' }
              ],
              materials: ['Yield comparison examples'],
              assessment: 'Calculate theoretical yield',
              homework: 'Theoretical yield problems',
              teacherNotes: 'Real yields are always less than theoretical'
            },
            {
              day: 'Thursday',
              title: 'Percent Yield Calculations',
              duration: '60 min',
              objectives: ['Calculate percent yield', 'Interpret percent yield values'],
              introduction: 'Percent yield tells us reaction efficiency.',
              mainActivities: [
                { activity: 'Formula', duration: '15 min', description: '% Yield = (Actual/Theoretical) × 100' },
                { activity: 'Calculations', duration: '30 min', description: 'Calculate percent yield from data' },
                { activity: 'Interpretation', duration: '15 min', description: 'What different percentages mean' }
              ],
              materials: ['Calculators', 'Practice data'],
              assessment: 'Calculate percent yield accurately',
              homework: 'Percent yield problem set',
              teacherNotes: 'Industrial importance of high yields'
            },
            {
              day: 'Friday',
              title: 'Percent Yield Lab',
              duration: '60 min',
              objectives: ['Determine percent yield experimentally', 'Apply calculations to real data'],
              introduction: 'Let\'s find our own percent yield!',
              mainActivities: [
                { activity: 'Reaction Setup', duration: '15 min', description: 'Prepare reactants, record masses' },
                { activity: 'Conduct Reaction', duration: '25 min', description: 'Carry out precipitation reaction' },
                { activity: 'Data Analysis', duration: '20 min', description: 'Calculate theoretical and percent yield' }
              ],
              materials: ['Chemicals', 'Balances', 'Filter paper'],
              assessment: 'Complete lab with calculations',
              homework: 'Write lab report',
              teacherNotes: 'MgCl₂ + NaOH precipitation works well'
            }
          ]
        },
        {
          week: 5,
          topic: 'Thermochemistry Introduction',
          strand: 'Energetics',
          subStrand: 'Thermochemistry',
          learningOutcomes: [
            'Define enthalpy and enthalpy change',
            'Distinguish exothermic and endothermic reactions',
            'Interpret energy diagrams'
          ],
          activities: [
            'Energy change demonstrations',
            'Temperature measurement labs',
            'Energy diagram drawing'
          ],
          resources: ['Calorimeters', 'Thermometers', 'Chemicals'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Energy in Chemical Reactions',
              duration: '60 min',
              objectives: ['Understand energy changes in reactions', 'Define enthalpy'],
              introduction: 'Why do some reactions feel hot and others cold?',
              mainActivities: [
                { activity: 'Energy Concept', duration: '20 min', description: 'All reactions involve energy changes' },
                { activity: 'Enthalpy Definition', duration: '20 min', description: 'H = heat content at constant pressure' },
                { activity: 'ΔH Introduction', duration: '20 min', description: 'Change in enthalpy during reaction' }
              ],
              materials: ['Energy diagrams', 'Examples'],
              assessment: 'Define enthalpy and ΔH',
              homework: 'Energy in everyday reactions',
              teacherNotes: 'Foundation for thermochemistry'
            },
            {
              day: 'Tuesday',
              title: 'Exothermic Reactions',
              duration: '60 min',
              objectives: ['Define exothermic reaction', 'Identify examples'],
              introduction: 'Why does burning wood release heat?',
              mainActivities: [
                { activity: 'Exothermic Definition', duration: '15 min', description: 'Energy released to surroundings, ΔH negative' },
                { activity: 'Examples', duration: '20 min', description: 'Combustion, neutralization, respiration' },
                { activity: 'Demonstration', duration: '25 min', description: 'Observe exothermic reactions' }
              ],
              materials: ['Demo chemicals', 'Thermometers'],
              assessment: 'List 5 exothermic reactions',
              homework: 'Find exothermic reactions at home',
              teacherNotes: 'Safety with exothermic demos'
            },
            {
              day: 'Wednesday',
              title: 'Endothermic Reactions',
              duration: '60 min',
              objectives: ['Define endothermic reaction', 'Identify examples'],
              introduction: 'Why do some reactions feel cold?',
              mainActivities: [
                { activity: 'Endothermic Definition', duration: '15 min', description: 'Energy absorbed from surroundings, ΔH positive' },
                { activity: 'Examples', duration: '20 min', description: 'Photosynthesis, thermal decomposition, cold packs' },
                { activity: 'Demonstration', duration: '25 min', description: 'Observe endothermic reactions' }
              ],
              materials: ['Demo chemicals', 'Thermometers'],
              assessment: 'List 5 endothermic reactions',
              homework: 'Compare exo- and endothermic',
              teacherNotes: 'NH₄NO₃ + water is very cold'
            },
            {
              day: 'Thursday',
              title: 'Energy Diagrams',
              duration: '60 min',
              objectives: ['Draw energy diagrams', 'Interpret energy diagrams'],
              introduction: 'Pictures help us understand energy changes.',
              mainActivities: [
                { activity: 'Diagram Components', duration: '15 min', description: 'Reactants, products, activation energy, ΔH' },
                { activity: 'Exothermic Diagrams', duration: '15 min', description: 'Products lower than reactants' },
                { activity: 'Endothermic Diagrams', duration: '15 min', description: 'Products higher than reactants' },
                { activity: 'Drawing Practice', duration: '15 min', description: 'Draw diagrams for given reactions' }
              ],
              materials: ['Graph paper', 'Diagram examples'],
              assessment: 'Draw correct energy diagrams',
              homework: 'Complete energy diagram worksheet',
              teacherNotes: 'Diagrams frequently tested'
            },
            {
              day: 'Friday',
              title: 'Activation Energy',
              duration: '60 min',
              objectives: ['Define activation energy', 'Explain its role in reactions'],
              introduction: 'Why do some reactions need a spark to start?',
              mainActivities: [
                { activity: 'Ea Definition', duration: '20 min', description: 'Minimum energy to start a reaction' },
                { activity: 'Ea on Diagrams', duration: '20 min', description: 'Identify Ea on energy diagrams' },
                { activity: 'Week Review', duration: '20 min', description: 'Quiz on thermochemistry basics' }
              ],
              materials: ['Diagram examples', 'Quiz papers'],
              assessment: 'Thermochemistry concepts quiz',
              homework: 'Study for calorimetry next week',
              teacherNotes: 'Catalysts lower activation energy'
            }
          ]
        },
        {
          week: 6,
          topic: 'Calorimetry',
          strand: 'Energetics',
          subStrand: 'Thermochemistry',
          learningOutcomes: [
            'Calculate heat using q = mcΔT',
            'Perform calorimetry experiments',
            'Calculate enthalpy changes from experimental data'
          ],
          activities: [
            'Heat calculations',
            'Calorimetry experiments',
            'Data analysis'
          ],
          resources: ['Calorimeters', 'Thermometers', 'Chemicals'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Heat Calculations',
              duration: '60 min',
              objectives: ['Use q = mcΔT formula', 'Calculate heat energy'],
              introduction: 'How do we measure heat mathematically?',
              mainActivities: [
                { activity: 'Formula Introduction', duration: '15 min', description: 'q = mcΔT where q=heat, m=mass, c=specific heat, ΔT=temp change' },
                { activity: 'Specific Heat', duration: '20 min', description: 'c for water = 4.18 J/g°C' },
                { activity: 'Calculations', duration: '25 min', description: 'Practice heat calculations' }
              ],
              materials: ['Calculators', 'Formula sheet'],
              assessment: 'Complete heat calculations',
              homework: 'Heat calculation worksheet',
              teacherNotes: 'Watch units carefully'
            },
            {
              day: 'Tuesday',
              title: 'Calorimeter Design',
              duration: '60 min',
              objectives: ['Understand calorimeter function', 'Identify sources of error'],
              introduction: 'How do we measure heat in the lab?',
              mainActivities: [
                { activity: 'Calorimeter Parts', duration: '20 min', description: 'Cup, lid, thermometer, stirrer' },
                { activity: 'Heat Absorption', duration: '20 min', description: 'Water absorbs heat released by reaction' },
                { activity: 'Error Sources', duration: '20 min', description: 'Heat loss, incomplete mixing' }
              ],
              materials: ['Calorimeter setup', 'Diagrams'],
              assessment: 'Explain how calorimeter works',
              homework: 'Design improvements for calorimeter',
              teacherNotes: 'Simple coffee cup calorimeter works'
            },
            {
              day: 'Wednesday',
              title: 'Calorimetry Lab - Neutralization',
              duration: '60 min',
              objectives: ['Measure heat of neutralization', 'Record data accurately'],
              introduction: 'Let\'s measure the heat of an acid-base reaction!',
              mainActivities: [
                { activity: 'Setup', duration: '10 min', description: 'Prepare calorimeter and solutions' },
                { activity: 'Experiment', duration: '35 min', description: 'Measure temperature change in NaOH + HCl' },
                { activity: 'Data Recording', duration: '15 min', description: 'Record all measurements carefully' }
              ],
              materials: ['Calorimeter', 'NaOH', 'HCl', 'Thermometer'],
              assessment: 'Collect accurate data',
              homework: 'Begin data analysis',
              teacherNotes: 'Use dilute solutions for safety'
            },
            {
              day: 'Thursday',
              title: 'Calculating Enthalpy Changes',
              duration: '60 min',
              objectives: ['Calculate ΔH from calorimetry data', 'Express ΔH per mole'],
              introduction: 'Now we turn data into enthalpy values.',
              mainActivities: [
                { activity: 'Data Analysis', duration: '20 min', description: 'Use q = mcΔT to find heat released' },
                { activity: 'Per Mole Calculation', duration: '25 min', description: 'Divide by moles to get ΔH' },
                { activity: 'Error Analysis', duration: '15 min', description: 'Compare to literature value' }
              ],
              materials: ['Lab data', 'Calculators'],
              assessment: 'Calculate ΔH from your data',
              homework: 'Complete lab report',
              teacherNotes: 'ΔH neutralization ≈ -57 kJ/mol'
            },
            {
              day: 'Friday',
              title: 'Calorimetry Applications',
              duration: '60 min',
              objectives: ['Apply calorimetry to food energy', 'Solve calorimetry problems'],
              introduction: 'How do we know calories in food?',
              mainActivities: [
                { activity: 'Food Calorimetry', duration: '20 min', description: 'Bomb calorimeter for food energy' },
                { activity: 'Problem Solving', duration: '25 min', description: 'Mixed calorimetry calculations' },
                { activity: 'Week Review', duration: '15 min', description: 'Summary and Q&A' }
              ],
              materials: ['Food energy data', 'Practice problems'],
              assessment: 'Complete application problems',
              homework: 'Study Hess\'s Law next week',
              teacherNotes: 'Connect to nutrition and biology'
            }
          ]
        },
        {
          week: 7,
          topic: 'Hess\'s Law',
          strand: 'Energetics',
          subStrand: 'Thermochemistry',
          learningOutcomes: [
            'State Hess\'s Law',
            'Calculate enthalpy changes using Hess\'s Law',
            'Use standard enthalpies of formation'
          ],
          activities: [
            'Hess\'s Law calculations',
            'Enthalpy cycle diagrams',
            'Problem solving'
          ],
          resources: ['Enthalpy data tables', 'Worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Hess\'s Law',
              duration: '60 min',
              objectives: ['State Hess\'s Law', 'Understand its basis'],
              introduction: 'Can we find ΔH without doing the reaction?',
              mainActivities: [
                { activity: 'Law Statement', duration: '20 min', description: 'ΔH depends only on initial and final states' },
                { activity: 'Path Independence', duration: '20 min', description: 'Total ΔH same regardless of route' },
                { activity: 'Simple Example', duration: '20 min', description: 'Work through introductory example' }
              ],
              materials: ['Hess\'s Law diagrams'],
              assessment: 'State Hess\'s Law in own words',
              homework: 'Read about Hess\'s Law applications',
              teacherNotes: 'Energy is a state function'
            },
            {
              day: 'Tuesday',
              title: 'Enthalpy Cycles',
              duration: '60 min',
              objectives: ['Draw enthalpy cycle diagrams', 'Use cycles to calculate ΔH'],
              introduction: 'Diagrams help us apply Hess\'s Law.',
              mainActivities: [
                { activity: 'Cycle Construction', duration: '25 min', description: 'Draw cycles for given reactions' },
                { activity: 'Applying Cycles', duration: '25 min', description: 'Calculate unknown ΔH from cycle' },
                { activity: 'Practice', duration: '10 min', description: 'Work through examples' }
              ],
              materials: ['Graph paper', 'Practice problems'],
              assessment: 'Complete enthalpy cycle problems',
              homework: 'Enthalpy cycle worksheet',
              teacherNotes: 'Arrows show direction of energy change'
            },
            {
              day: 'Wednesday',
              title: 'Manipulating Equations',
              duration: '60 min',
              objectives: ['Reverse equations and ΔH', 'Multiply equations and ΔH'],
              introduction: 'Sometimes we need to modify given equations.',
              mainActivities: [
                { activity: 'Reversing', duration: '20 min', description: 'Reverse equation = change sign of ΔH' },
                { activity: 'Multiplying', duration: '20 min', description: 'Multiply equation = multiply ΔH' },
                { activity: 'Combined Practice', duration: '20 min', description: 'Apply both manipulations' }
              ],
              materials: ['Example problems'],
              assessment: 'Manipulate equations correctly',
              homework: 'Equation manipulation practice',
              teacherNotes: 'Key skills for Hess\'s Law problems'
            },
            {
              day: 'Thursday',
              title: 'Standard Enthalpies of Formation',
              duration: '60 min',
              objectives: ['Define ΔHf°', 'Use ΔHf° values to calculate ΔHrxn'],
              introduction: 'Tables give us standard formation values.',
              mainActivities: [
                { activity: 'ΔHf° Definition', duration: '15 min', description: 'Enthalpy to form 1 mol from elements' },
                { activity: 'Using Tables', duration: '20 min', description: 'Read ΔHf° values from data book' },
                { activity: 'Formula Application', duration: '25 min', description: 'ΔHrxn = ΣΔHf°(products) - ΣΔHf°(reactants)' }
              ],
              materials: ['Data tables', 'Calculators'],
              assessment: 'Calculate ΔHrxn from ΔHf° data',
              homework: 'ΔHf° calculation problems',
              teacherNotes: 'ΔHf° of elements = 0'
            },
            {
              day: 'Friday',
              title: 'Hess\'s Law Problem Solving',
              duration: '60 min',
              objectives: ['Solve complex Hess\'s Law problems', 'Apply all techniques learned'],
              introduction: 'Let\'s tackle challenging problems!',
              mainActivities: [
                { activity: 'Review Methods', duration: '10 min', description: 'Review all Hess\'s Law techniques' },
                { activity: 'Complex Problems', duration: '35 min', description: 'Work through challenging examples' },
                { activity: 'Quiz', duration: '15 min', description: 'Hess\'s Law quiz' }
              ],
              materials: ['Practice problems', 'Quiz papers'],
              assessment: 'Hess\'s Law quiz',
              homework: 'Study for equilibrium next week',
              teacherNotes: 'Ensure students understand before moving on'
            }
          ]
        },
        {
          week: 8,
          topic: 'Chemical Equilibrium',
          strand: 'Chemical Equilibrium',
          subStrand: 'Equilibrium Concepts',
          learningOutcomes: [
            'Define chemical equilibrium',
            'Write equilibrium expressions',
            'Understand Le Chatelier\'s principle'
          ],
          activities: [
            'Equilibrium demonstrations',
            'Equilibrium constant calculations',
            'Le Chatelier\'s principle experiments'
          ],
          resources: ['Equilibrium demonstration kits', 'Calculators'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Equilibrium',
              duration: '60 min',
              objectives: ['Define dynamic equilibrium', 'Identify reversible reactions'],
              introduction: 'Not all reactions go to completion.',
              mainActivities: [
                { activity: 'Reversible Reactions', duration: '20 min', description: 'Reactions that can go both directions' },
                { activity: 'Dynamic Equilibrium', duration: '25 min', description: 'Forward and reverse rates equal' },
                { activity: 'Examples', duration: '15 min', description: 'Identify equilibrium systems' }
              ],
              materials: ['Equilibrium diagrams'],
              assessment: 'Define equilibrium in own words',
              homework: 'Find equilibrium examples',
              teacherNotes: 'Use ⇌ symbol for reversible reactions'
            },
            {
              day: 'Tuesday',
              title: 'Equilibrium Constants',
              duration: '60 min',
              objectives: ['Write equilibrium expressions', 'Calculate Kc values'],
              introduction: 'The equilibrium constant tells us the position of equilibrium.',
              mainActivities: [
                { activity: 'Kc Expression', duration: '20 min', description: 'Products over reactants, each raised to coefficient' },
                { activity: 'Writing Expressions', duration: '20 min', description: 'Practice writing Kc for various reactions' },
                { activity: 'Meaning of K', duration: '20 min', description: 'Large K = products favored, small K = reactants favored' }
              ],
              materials: ['Expression examples'],
              assessment: 'Write correct equilibrium expressions',
              homework: 'Equilibrium expression practice',
              teacherNotes: 'Pure solids and liquids not included'
            },
            {
              day: 'Wednesday',
              title: 'Calculating with Equilibrium Constants',
              duration: '60 min',
              objectives: ['Calculate Kc from concentration data', 'Calculate concentrations from Kc'],
              introduction: 'We can use K to predict concentrations.',
              mainActivities: [
                { activity: 'Finding Kc', duration: '25 min', description: 'Calculate Kc from equilibrium concentrations' },
                { activity: 'Finding Concentrations', duration: '25 min', description: 'Calculate unknown concentration using Kc' },
                { activity: 'ICE Tables', duration: '10 min', description: 'Introduction to ICE table method' }
              ],
              materials: ['Calculators', 'Practice problems'],
              assessment: 'Complete equilibrium calculations',
              homework: 'Kc calculation worksheet',
              teacherNotes: 'ICE = Initial, Change, Equilibrium'
            },
            {
              day: 'Thursday',
              title: 'Le Chatelier\'s Principle',
              duration: '60 min',
              objectives: ['State Le Chatelier\'s principle', 'Predict effects of changes'],
              introduction: 'How does equilibrium respond to stress?',
              mainActivities: [
                { activity: 'Principle Statement', duration: '15 min', description: 'System shifts to counteract change' },
                { activity: 'Concentration Changes', duration: '20 min', description: 'Adding/removing reactants or products' },
                { activity: 'Temperature Changes', duration: '25 min', description: 'Effect depends on whether exo- or endothermic' }
              ],
              materials: ['Le Chatelier\'s examples'],
              assessment: 'Predict shifts for various changes',
              homework: 'Le Chatelier\'s predictions',
              teacherNotes: 'Very commonly tested topic'
            },
            {
              day: 'Friday',
              title: 'Le Chatelier\'s Applications',
              duration: '60 min',
              objectives: ['Apply Le Chatelier\'s to pressure changes', 'Understand industrial applications'],
              introduction: 'Le Chatelier\'s is used in industry!',
              mainActivities: [
                { activity: 'Pressure Effects', duration: '20 min', description: 'Shift toward fewer gas moles' },
                { activity: 'Haber Process', duration: '25 min', description: 'Application to ammonia synthesis' },
                { activity: 'Week Quiz', duration: '15 min', description: 'Equilibrium concepts quiz' }
              ],
              materials: ['Haber process diagram', 'Quiz papers'],
              assessment: 'Equilibrium quiz',
              homework: 'Study for acids and bases next week',
              teacherNotes: 'Industrial chemistry application'
            }
          ]
        },
        {
          week: 9,
          topic: 'Acids and Bases - Advanced',
          strand: 'Acids and Bases',
          subStrand: 'Advanced Acid-Base Chemistry',
          learningOutcomes: [
            'Apply Brønsted-Lowry theory',
            'Calculate pH and pOH',
            'Perform acid-base titrations'
          ],
          activities: [
            'pH calculations',
            'Titration experiments',
            'Buffer solutions'
          ],
          resources: ['pH meters', 'Burettes', 'Indicators'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Brønsted-Lowry Theory',
              duration: '60 min',
              objectives: ['Define acids and bases by Brønsted-Lowry', 'Identify conjugate pairs'],
              introduction: 'A more advanced way to think about acids and bases.',
              mainActivities: [
                { activity: 'Theory Introduction', duration: '20 min', description: 'Acids donate H⁺, bases accept H⁺' },
                { activity: 'Conjugate Pairs', duration: '25 min', description: 'Every acid has a conjugate base and vice versa' },
                { activity: 'Identification Practice', duration: '15 min', description: 'Identify acids, bases, and conjugates' }
              ],
              materials: ['Conjugate pair examples'],
              assessment: 'Identify conjugate pairs correctly',
              homework: 'Conjugate pair worksheet',
              teacherNotes: 'More general than Arrhenius definition'
            },
            {
              day: 'Tuesday',
              title: 'pH and pOH',
              duration: '60 min',
              objectives: ['Calculate pH from [H⁺]', 'Calculate pOH and relate to pH'],
              introduction: 'pH is a convenient way to express acidity.',
              mainActivities: [
                { activity: 'pH Definition', duration: '15 min', description: 'pH = -log[H⁺]' },
                { activity: 'pH Calculations', duration: '20 min', description: 'Calculate pH from hydrogen ion concentration' },
                { activity: 'pOH and Relationship', duration: '25 min', description: 'pOH = -log[OH⁻], pH + pOH = 14' }
              ],
              materials: ['Scientific calculators'],
              assessment: 'Complete pH/pOH calculations',
              homework: 'pH calculation worksheet',
              teacherNotes: 'Log function on calculator essential'
            },
            {
              day: 'Wednesday',
              title: 'Strong vs Weak Acids',
              duration: '60 min',
              objectives: ['Distinguish strong and weak acids', 'Calculate pH for each type'],
              introduction: 'Not all acids ionize completely.',
              mainActivities: [
                { activity: 'Strong Acids', duration: '20 min', description: 'Complete ionization, pH calculation straightforward' },
                { activity: 'Weak Acids', duration: '25 min', description: 'Partial ionization, Ka and equilibrium' },
                { activity: 'Comparison', duration: '15 min', description: 'Same concentration, different pH' }
              ],
              materials: ['Ka values', 'Calculators'],
              assessment: 'Calculate pH for strong and weak acids',
              homework: 'Acid strength problems',
              teacherNotes: 'Ka indicates acid strength'
            },
            {
              day: 'Thursday',
              title: 'Acid-Base Titrations',
              duration: '60 min',
              objectives: ['Perform acid-base titration', 'Calculate unknown concentration'],
              introduction: 'Titration is a key analytical technique.',
              mainActivities: [
                { activity: 'Titration Theory', duration: '15 min', description: 'Neutralization to find concentration' },
                { activity: 'Practical', duration: '35 min', description: 'Titrate NaOH with HCl of known concentration' },
                { activity: 'Calculation', duration: '10 min', description: 'Calculate molarity from titration data' }
              ],
              materials: ['Burettes', 'Indicator', 'Solutions'],
              assessment: 'Successful titration with accurate calculation',
              homework: 'Write titration report',
              teacherNotes: 'Practice technique beforehand'
            },
            {
              day: 'Friday',
              title: 'Titration Calculations',
              duration: '60 min',
              objectives: ['Perform titration calculations', 'Apply to different scenarios'],
              introduction: 'Let\'s master titration math.',
              mainActivities: [
                { activity: 'Review Formula', duration: '15 min', description: 'MaVa = MbVb for 1:1 reactions' },
                { activity: 'Complex Stoichiometry', duration: '25 min', description: 'When mole ratio is not 1:1' },
                { activity: 'Week Quiz', duration: '20 min', description: 'Acids and bases quiz' }
              ],
              materials: ['Practice problems', 'Quiz papers'],
              assessment: 'Acid-base quiz',
              homework: 'Study for term review',
              teacherNotes: 'Common exam topic'
            }
          ]
        },
        {
          week: 10,
          topic: 'Term 1 Review and Assessment',
          strand: 'Review',
          subStrand: 'Term 1 Revision',
          learningOutcomes: [
            'Demonstrate mastery of stoichiometry',
            'Apply thermochemistry concepts',
            'Show understanding of equilibrium and acids/bases'
          ],
          activities: [
            'Comprehensive review',
            'Practice tests',
            'Term examination'
          ],
          resources: ['Review materials', 'Practice exams'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Stoichiometry Review',
              duration: '60 min',
              objectives: ['Review all stoichiometry concepts'],
              introduction: 'Let\'s make sure stoichiometry is solid.',
              mainActivities: [
                { activity: 'Mole Concept Review', duration: '15 min', description: 'Review moles, molarity, conversions' },
                { activity: 'Stoichiometry Review', duration: '25 min', description: 'Review calculation methods' },
                { activity: 'Practice Problems', duration: '20 min', description: 'Work through review problems' }
              ],
              materials: ['Review sheet', 'Practice problems'],
              assessment: 'Practice quiz',
              homework: 'Study stoichiometry',
              teacherNotes: 'Identify areas needing work'
            },
            {
              day: 'Tuesday',
              title: 'Thermochemistry Review',
              duration: '60 min',
              objectives: ['Review all thermochemistry concepts'],
              introduction: 'Energy is essential to chemistry.',
              mainActivities: [
                { activity: 'Concepts Review', duration: '20 min', description: 'Review ΔH, exo/endothermic, diagrams' },
                { activity: 'Calculations Review', duration: '25 min', description: 'Review calorimetry and Hess\'s Law' },
                { activity: 'Practice', duration: '15 min', description: 'Work through problems' }
              ],
              materials: ['Review materials'],
              assessment: 'Practice problems',
              homework: 'Study thermochemistry',
              teacherNotes: 'Energy diagrams commonly tested'
            },
            {
              day: 'Wednesday',
              title: 'Equilibrium and Acid-Base Review',
              duration: '60 min',
              objectives: ['Review equilibrium and acid-base concepts'],
              introduction: 'These topics connect many ideas.',
              mainActivities: [
                { activity: 'Equilibrium Review', duration: '25 min', description: 'Review Kc, Le Chatelier\'s' },
                { activity: 'Acid-Base Review', duration: '25 min', description: 'Review pH, titrations' },
                { activity: 'Q&A', duration: '10 min', description: 'Answer student questions' }
              ],
              materials: ['Review sheet'],
              assessment: 'Practice problems',
              homework: 'Final study preparation',
              teacherNotes: 'Address remaining confusion'
            },
            {
              day: 'Thursday',
              title: 'Practice Exam',
              duration: '60 min',
              objectives: ['Complete practice exam under timed conditions'],
              introduction: 'Let\'s practice for the real exam.',
              mainActivities: [
                { activity: 'Practice Exam', duration: '50 min', description: 'Complete practice exam' },
                { activity: 'Review Answers', duration: '10 min', description: 'Go over any questions' }
              ],
              materials: ['Practice exam papers'],
              assessment: 'Practice exam completion',
              homework: 'Review mistakes, final study',
              teacherNotes: 'Simulate exam conditions'
            },
            {
              day: 'Friday',
              title: 'Term 1 Assessment',
              duration: '60 min',
              objectives: ['Complete term 1 examination'],
              introduction: 'Show what you have learned.',
              mainActivities: [
                { activity: 'Written Exam', duration: '55 min', description: 'Complete term examination' },
                { activity: 'Submission', duration: '5 min', description: 'Submit all materials' }
              ],
              materials: ['Exam papers', 'Calculators', 'Data booklet'],
              assessment: 'Term 1 Examination',
              homework: 'Enjoy term break!',
              teacherNotes: 'Fair and comprehensive assessment'
            }
          ]
        }
      ]
    },
    term2: {
      name: 'Term 2',
      focus: 'Electrochemistry and Organic Chemistry',
      weeks: 10,
      scheme: [
        {
          week: 1,
          topic: 'Oxidation Numbers Review',
          strand: 'Electrochemistry',
          subStrand: 'Oxidation States',
          learningOutcomes: [
            'Assign oxidation numbers to atoms',
            'Identify oxidation and reduction from oxidation numbers',
            'Balance redox equations using oxidation numbers'
          ],
          activities: ['Oxidation number practice', 'Redox identification', 'Equation balancing'],
          resources: ['Oxidation rules chart', 'Practice worksheets', 'Periodic table'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Oxidation Number Rules',
              duration: '60 min',
              objectives: ['Review oxidation number rules', 'Assign oxidation numbers'],
              introduction: 'Oxidation numbers help track electrons in reactions.',
              mainActivities: [
                { activity: 'Rule Review', duration: '20 min', description: 'All oxidation number rules' },
                { activity: 'Simple Compounds', duration: '20 min', description: 'Assign to binary compounds' },
                { activity: 'Complex Compounds', duration: '20 min', description: 'Polyatomic ions, unusual cases' }
              ],
              materials: ['Rules chart', 'Practice compounds'],
              assessment: 'Assign oxidation numbers correctly',
              homework: 'Oxidation number worksheet',
              teacherNotes: 'Foundation for electrochemistry'
            },
            {
              day: 'Tuesday',
              title: 'Identifying Redox Reactions',
              duration: '60 min',
              objectives: ['Use oxidation numbers to identify redox', 'Find substance oxidized/reduced'],
              introduction: 'Changes in oxidation numbers reveal redox.',
              mainActivities: [
                { activity: 'Oxidation Number Changes', duration: '20 min', description: 'Track changes in reactions' },
                { activity: 'Identifying Oxidation/Reduction', duration: '25 min', description: 'Increase = oxidation, decrease = reduction' },
                { activity: 'Practice', duration: '15 min', description: 'Analyze various reactions' }
              ],
              materials: ['Reaction examples', 'Analysis worksheets'],
              assessment: 'Identify redox from oxidation numbers',
              homework: 'Redox identification practice',
              teacherNotes: 'Systematic approach works best'
            },
            {
              day: 'Wednesday',
              title: 'Balancing Redox - Half-Reaction Method',
              duration: '60 min',
              objectives: ['Write half-reactions', 'Balance half-reactions'],
              introduction: 'The half-reaction method systematically balances redox.',
              mainActivities: [
                { activity: 'Writing Half-Reactions', duration: '20 min', description: 'Separate oxidation and reduction' },
                { activity: 'Balancing Atoms', duration: '20 min', description: 'Balance atoms in each half' },
                { activity: 'Balancing Charge', duration: '20 min', description: 'Add electrons to balance charge' }
              ],
              materials: ['Method guide', 'Practice reactions'],
              assessment: 'Balance half-reactions correctly',
              homework: 'Half-reaction practice',
              teacherNotes: 'Step-by-step approach essential'
            },
            {
              day: 'Thursday',
              title: 'Combining Half-Reactions',
              duration: '60 min',
              objectives: ['Combine half-reactions', 'Complete balanced redox equations'],
              introduction: 'Put the halves together for the full equation.',
              mainActivities: [
                { activity: 'Electron Balancing', duration: '20 min', description: 'Multiply to equalize electrons' },
                { activity: 'Combining', duration: '25 min', description: 'Add half-reactions, cancel electrons' },
                { activity: 'Verification', duration: '15 min', description: 'Check atoms and charge' }
              ],
              materials: ['Practice reactions', 'Verification checklist'],
              assessment: 'Balance complete redox equations',
              homework: 'Redox balancing worksheet',
              teacherNotes: 'Common exam skill'
            },
            {
              day: 'Friday',
              title: 'Redox Review and Applications',
              duration: '60 min',
              objectives: ['Review redox concepts', 'Apply to real situations'],
              introduction: 'Redox reactions are everywhere!',
              mainActivities: [
                { activity: 'Concept Review', duration: '20 min', description: 'All redox concepts covered' },
                { activity: 'Real-World Examples', duration: '25 min', description: 'Batteries, corrosion, metabolism' },
                { activity: 'Quiz', duration: '15 min', description: 'Redox assessment' }
              ],
              materials: ['Review materials', 'Quiz papers'],
              assessment: 'Redox quiz',
              homework: 'Prepare for electrochemical cells',
              teacherNotes: 'Connect to everyday applications'
            }
          ]
        },
        {
          week: 2,
          topic: 'Electrochemical Cells',
          strand: 'Electrochemistry',
          subStrand: 'Galvanic Cells',
          learningOutcomes: [
            'Understand galvanic cell components',
            'Draw and label cell diagrams',
            'Calculate cell potential'
          ],
          activities: ['Cell construction', 'Diagram drawing', 'Potential calculations'],
          resources: ['Cell materials', 'Electrode metals', 'Voltmeters'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Galvanic Cells',
              duration: '60 min',
              objectives: ['Understand galvanic cell function', 'Identify cell components'],
              introduction: 'Batteries convert chemical energy to electrical energy.',
              mainActivities: [
                { activity: 'Cell Purpose', duration: '15 min', description: 'Spontaneous redox produces electricity' },
                { activity: 'Components', duration: '25 min', description: 'Electrodes, electrolytes, salt bridge' },
                { activity: 'Simple Cell Demo', duration: '20 min', description: 'Demonstrate Zn-Cu cell' }
              ],
              materials: ['Cell diagram', 'Demo materials'],
              assessment: 'Name and explain cell components',
              homework: 'Cell component questions',
              teacherNotes: 'Lemon battery as simple example'
            },
            {
              day: 'Tuesday',
              title: 'Cell Diagrams and Notation',
              duration: '60 min',
              objectives: ['Draw cell diagrams', 'Use cell notation'],
              introduction: 'Standard ways to represent electrochemical cells.',
              mainActivities: [
                { activity: 'Drawing Cells', duration: '25 min', description: 'Complete labeled cell diagrams' },
                { activity: 'Cell Notation', duration: '20 min', description: 'Zn|Zn²⁺||Cu²⁺|Cu format' },
                { activity: 'Practice', duration: '15 min', description: 'Draw and write various cells' }
              ],
              materials: ['Diagram templates', 'Notation examples'],
              assessment: 'Draw correct cell diagrams',
              homework: 'Cell diagram worksheet',
              teacherNotes: 'Conventions must be followed exactly'
            },
            {
              day: 'Wednesday',
              title: 'Standard Electrode Potentials',
              duration: '60 min',
              objectives: ['Understand E° values', 'Use standard electrode potential table'],
              introduction: 'E° values tell us about electron "push" and "pull".',
              mainActivities: [
                { activity: 'E° Concept', duration: '20 min', description: 'Standard reduction potential' },
                { activity: 'Using Tables', duration: '25 min', description: 'Read and interpret E° tables' },
                { activity: 'Comparing Electrodes', duration: '15 min', description: 'Which is better oxidizer/reducer?' }
              ],
              materials: ['E° tables', 'Comparison exercises'],
              assessment: 'Use E° table correctly',
              homework: 'E° interpretation practice',
              teacherNotes: 'Standard hydrogen electrode = 0.00 V'
            },
            {
              day: 'Thursday',
              title: 'Calculating Cell Potential',
              duration: '60 min',
              objectives: ['Calculate E°cell', 'Predict spontaneity'],
              introduction: 'E°cell = E°cathode - E°anode',
              mainActivities: [
                { activity: 'Cell Potential Formula', duration: '15 min', description: 'E°cell calculation method' },
                { activity: 'Calculations', duration: '30 min', description: 'Calculate E°cell for various cells' },
                { activity: 'Spontaneity', duration: '15 min', description: 'Positive E°cell = spontaneous' }
              ],
              materials: ['E° tables', 'Calculation practice'],
              assessment: 'Calculate cell potentials correctly',
              homework: 'E°cell calculation worksheet',
              teacherNotes: 'Sign errors are common'
            },
            {
              day: 'Friday',
              title: 'Building Electrochemical Cells',
              duration: '60 min',
              objectives: ['Construct working galvanic cell', 'Measure cell potential'],
              introduction: 'Let\'s build real cells!',
              mainActivities: [
                { activity: 'Cell Construction', duration: '35 min', description: 'Build Zn-Cu and other cells' },
                { activity: 'Potential Measurement', duration: '15 min', description: 'Use voltmeter to measure E' },
                { activity: 'Comparison', duration: '10 min', description: 'Compare measured vs calculated' }
              ],
              materials: ['Metal electrodes', 'Solutions', 'Voltmeters'],
              assessment: 'Construct working cell, measure potential',
              homework: 'Lab report on cell construction',
              teacherNotes: 'Real measurements may differ from E°'
            }
          ]
        },
        {
          week: 3,
          topic: 'Electrolysis',
          strand: 'Electrochemistry',
          subStrand: 'Electrolytic Cells',
          learningOutcomes: [
            'Understand electrolysis process',
            'Predict products of electrolysis',
            'Compare galvanic and electrolytic cells'
          ],
          activities: ['Electrolysis experiments', 'Product prediction', 'Cell comparison'],
          resources: ['Power supplies', 'Electrodes', 'Electrolyte solutions'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Electrolysis',
              duration: '60 min',
              objectives: ['Define electrolysis', 'Understand non-spontaneous reactions'],
              introduction: 'Electrolysis uses electricity to force reactions.',
              mainActivities: [
                { activity: 'Electrolysis Definition', duration: '15 min', description: 'Using electricity to drive reactions' },
                { activity: 'Galvanic vs Electrolytic', duration: '25 min', description: 'Key differences between cell types' },
                { activity: 'Examples', duration: '20 min', description: 'Electroplating, metal extraction' }
              ],
              materials: ['Cell comparison chart', 'Examples'],
              assessment: 'Explain electrolysis and compare cell types',
              homework: 'Cell type comparison questions',
              teacherNotes: 'Opposite of galvanic cells'
            },
            {
              day: 'Tuesday',
              title: 'Electrolysis of Molten Compounds',
              duration: '60 min',
              objectives: ['Predict products from molten ionic compounds', 'Write electrode equations'],
              introduction: 'Molten salts conduct electricity and electrolyze.',
              mainActivities: [
                { activity: 'Molten Compound Conduction', duration: '15 min', description: 'Ions free to move when molten' },
                { activity: 'Product Prediction', duration: '25 min', description: 'Metal at cathode, non-metal at anode' },
                { activity: 'Electrode Equations', duration: '20 min', description: 'Write equations for each electrode' }
              ],
              materials: ['Prediction guide', 'Equation practice'],
              assessment: 'Predict products and write equations',
              homework: 'Molten electrolysis worksheet',
              teacherNotes: 'Simpler than aqueous solutions'
            },
            {
              day: 'Wednesday',
              title: 'Electrolysis of Aqueous Solutions',
              duration: '60 min',
              objectives: ['Predict products from aqueous solutions', 'Consider competing reactions'],
              introduction: 'Aqueous solutions are more complex.',
              mainActivities: [
                { activity: 'Competing Ions', duration: '20 min', description: 'Water ions complicate predictions' },
                { activity: 'Selection Rules', duration: '25 min', description: 'Which ion is discharged?' },
                { activity: 'Practice Predictions', duration: '15 min', description: 'Predict for various solutions' }
              ],
              materials: ['Selection rules', 'Practice solutions'],
              assessment: 'Predict aqueous electrolysis products',
              homework: 'Aqueous electrolysis worksheet',
              teacherNotes: 'Concentration and electrode affect products'
            },
            {
              day: 'Thursday',
              title: 'Electrolysis Practical',
              duration: '60 min',
              objectives: ['Perform electrolysis experiment', 'Verify predictions'],
              introduction: 'Let\'s electrolyze some solutions.',
              mainActivities: [
                { activity: 'Predictions', duration: '10 min', description: 'Predict products before experiment' },
                { activity: 'Electrolysis', duration: '35 min', description: 'Electrolyze CuCl₂, NaCl solutions' },
                { activity: 'Analysis', duration: '15 min', description: 'Compare predictions with results' }
              ],
              materials: ['Power supply', 'Electrodes', 'Solutions'],
              assessment: 'Conduct electrolysis, verify predictions',
              homework: 'Electrolysis lab report',
              teacherNotes: 'Safety with chlorine gas'
            },
            {
              day: 'Friday',
              title: 'Applications of Electrolysis',
              duration: '60 min',
              objectives: ['Understand industrial applications', 'Appreciate economic importance'],
              introduction: 'Electrolysis is used worldwide in industry.',
              mainActivities: [
                { activity: 'Metal Extraction', duration: '20 min', description: 'Aluminum production' },
                { activity: 'Electroplating', duration: '20 min', description: 'Coating with metals' },
                { activity: 'Purification', duration: '20 min', description: 'Refining copper' }
              ],
              materials: ['Industry examples', 'Process diagrams'],
              assessment: 'Explain industrial electrolysis applications',
              homework: 'Research one industrial application',
              teacherNotes: 'High energy costs are a consideration'
            }
          ]
        },
        {
          week: 4,
          topic: 'Advanced Organic - Polymers',
          strand: 'Organic Chemistry',
          subStrand: 'Polymerization',
          learningOutcomes: [
            'Understand addition polymerization',
            'Understand condensation polymerization',
            'Identify monomers and polymers'
          ],
          activities: ['Polymer identification', 'Monomer drawing', 'Polymerization mechanisms'],
          resources: ['Polymer samples', 'Structure worksheets', 'Model kits'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Polymers',
              duration: '60 min',
              objectives: ['Define polymers and monomers', 'Understand polymer importance'],
              introduction: 'Polymers are giant molecules made from small units.',
              mainActivities: [
                { activity: 'Polymer Definition', duration: '15 min', description: 'Long chains of repeating units' },
                { activity: 'Monomer Concept', duration: '20 min', description: 'Small molecules that join' },
                { activity: 'Examples', duration: '25 min', description: 'Natural and synthetic polymers' }
              ],
              materials: ['Polymer samples', 'Concept diagrams'],
              assessment: 'Define polymer and monomer with examples',
              homework: 'Find polymers at home',
              teacherNotes: 'Plastics, proteins, DNA are polymers'
            },
            {
              day: 'Tuesday',
              title: 'Addition Polymerization',
              duration: '60 min',
              objectives: ['Understand addition polymerization', 'Draw polymer structures'],
              introduction: 'Alkenes can polymerize by addition.',
              mainActivities: [
                { activity: 'Addition Mechanism', duration: '20 min', description: 'Double bonds open and join' },
                { activity: 'Common Addition Polymers', duration: '25 min', description: 'Polyethylene, PVC, polystyrene' },
                { activity: 'Drawing Practice', duration: '15 min', description: 'Draw monomer and polymer' }
              ],
              materials: ['Polymer structures', 'Drawing practice'],
              assessment: 'Draw addition polymer from monomer',
              homework: 'Addition polymer worksheet',
              teacherNotes: 'No small molecule released'
            },
            {
              day: 'Wednesday',
              title: 'Condensation Polymerization',
              duration: '60 min',
              objectives: ['Understand condensation polymerization', 'Identify small molecule released'],
              introduction: 'Some polymers form by releasing water.',
              mainActivities: [
                { activity: 'Condensation Mechanism', duration: '20 min', description: 'Functional groups react, release H₂O' },
                { activity: 'Polyesters', duration: '20 min', description: 'Alcohol + acid → ester links' },
                { activity: 'Polyamides', duration: '20 min', description: 'Amine + acid → amide links (nylon)' }
              ],
              materials: ['Condensation diagrams', 'Polymer examples'],
              assessment: 'Explain condensation polymerization',
              homework: 'Condensation polymer worksheet',
              teacherNotes: 'Proteins are natural polyamides'
            },
            {
              day: 'Thursday',
              title: 'Polymer Properties',
              duration: '60 min',
              objectives: ['Relate structure to properties', 'Understand polymer applications'],
              introduction: 'Structure determines how polymers behave.',
              mainActivities: [
                { activity: 'Thermoplastics vs Thermosets', duration: '20 min', description: 'Melting behavior differences' },
                { activity: 'Strength and Flexibility', duration: '20 min', description: 'Chain structure effects' },
                { activity: 'Applications', duration: '20 min', description: 'Match polymer to use' }
              ],
              materials: ['Polymer samples', 'Property data'],
              assessment: 'Explain polymer property-structure relationship',
              homework: 'Polymer application research',
              teacherNotes: 'Cross-linking creates thermosets'
            },
            {
              day: 'Friday',
              title: 'Polymers and the Environment',
              duration: '60 min',
              objectives: ['Understand polymer environmental issues', 'Explore solutions'],
              introduction: 'Plastics pose environmental challenges.',
              mainActivities: [
                { activity: 'Environmental Problems', duration: '20 min', description: 'Persistence, ocean pollution' },
                { activity: 'Solutions', duration: '25 min', description: 'Recycling, biodegradable polymers' },
                { activity: 'Discussion', duration: '15 min', description: 'Vanuatu plastic issues' }
              ],
              materials: ['Environmental data', 'Local examples'],
              assessment: 'Discuss polymer environmental impact',
              homework: 'Research biodegradable alternatives',
              teacherNotes: 'Relevant to Pacific island nations'
            }
          ]
        },
        {
          week: 5,
          topic: 'Biochemistry - Carbohydrates and Lipids',
          strand: 'Biochemistry',
          subStrand: 'Biological Molecules',
          learningOutcomes: [
            'Understand carbohydrate structure and function',
            'Understand lipid structure and function',
            'Relate structure to biological role'
          ],
          activities: ['Structure analysis', 'Function exploration', 'Testing for biomolecules'],
          resources: ['Biomolecule models', 'Test reagents', 'Food samples'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Carbohydrates - Monosaccharides',
              duration: '60 min',
              objectives: ['Understand monosaccharide structure', 'Know common monosaccharides'],
              introduction: 'Carbohydrates are our main energy source.',
              mainActivities: [
                { activity: 'Carbohydrate Types', duration: '15 min', description: 'Mono-, di-, polysaccharides' },
                { activity: 'Glucose Structure', duration: '25 min', description: 'Ring structure, isomers' },
                { activity: 'Other Monosaccharides', duration: '20 min', description: 'Fructose, galactose' }
              ],
              materials: ['Structure diagrams', 'Model kits'],
              assessment: 'Draw glucose structure',
              homework: 'Monosaccharide worksheet',
              teacherNotes: 'Ring forms are most common in solution'
            },
            {
              day: 'Tuesday',
              title: 'Di- and Polysaccharides',
              duration: '60 min',
              objectives: ['Understand glycosidic bonds', 'Know common di/polysaccharides'],
              introduction: 'Sugars can link together.',
              mainActivities: [
                { activity: 'Glycosidic Bonds', duration: '20 min', description: 'Condensation forms glycosidic link' },
                { activity: 'Disaccharides', duration: '20 min', description: 'Sucrose, maltose, lactose' },
                { activity: 'Polysaccharides', duration: '20 min', description: 'Starch, glycogen, cellulose' }
              ],
              materials: ['Structure diagrams', 'Food examples'],
              assessment: 'Explain glycosidic bond formation',
              homework: 'Polysaccharide questions',
              teacherNotes: 'Cellulose not digestible by humans'
            },
            {
              day: 'Wednesday',
              title: 'Lipids - Fats and Oils',
              duration: '60 min',
              objectives: ['Understand triglyceride structure', 'Distinguish saturated from unsaturated'],
              introduction: 'Lipids store energy and build membranes.',
              mainActivities: [
                { activity: 'Triglyceride Structure', duration: '20 min', description: 'Glycerol + 3 fatty acids' },
                { activity: 'Saturated vs Unsaturated', duration: '25 min', description: 'Double bonds affect properties' },
                { activity: 'Health Implications', duration: '15 min', description: 'Fat types and health' }
              ],
              materials: ['Lipid structures', 'Food examples'],
              assessment: 'Distinguish saturated from unsaturated fats',
              homework: 'Lipid structure worksheet',
              teacherNotes: 'Connect to nutrition and health'
            },
            {
              day: 'Thursday',
              title: 'Testing for Biomolecules',
              duration: '60 min',
              objectives: ['Test for carbohydrates', 'Test for lipids'],
              introduction: 'We can identify biomolecules with tests.',
              mainActivities: [
                { activity: 'Carbohydrate Tests', duration: '25 min', description: 'Benedict\'s, iodine tests' },
                { activity: 'Lipid Test', duration: '20 min', description: 'Emulsion/ethanol test' },
                { activity: 'Unknown Testing', duration: '15 min', description: 'Test unknown food samples' }
              ],
              materials: ['Test reagents', 'Food samples'],
              assessment: 'Correctly identify biomolecules',
              homework: 'Lab report on food testing',
              teacherNotes: 'Safety with Benedict\'s solution'
            },
            {
              day: 'Friday',
              title: 'Biomolecule Review',
              duration: '60 min',
              objectives: ['Review carbohydrates and lipids', 'Connect structure to function'],
              introduction: 'Let\'s consolidate our biochemistry knowledge.',
              mainActivities: [
                { activity: 'Structure Review', duration: '20 min', description: 'Carbohydrate and lipid structures' },
                { activity: 'Function Review', duration: '20 min', description: 'Biological roles' },
                { activity: 'Quiz', duration: '20 min', description: 'Carbohydrate/lipid assessment' }
              ],
              materials: ['Review materials', 'Quiz papers'],
              assessment: 'Biomolecule quiz',
              homework: 'Prepare for proteins next week',
              teacherNotes: 'Foundation for protein study'
            }
          ]
        },
        {
          week: 6,
          topic: 'Biochemistry - Proteins',
          strand: 'Biochemistry',
          subStrand: 'Amino Acids and Proteins',
          learningOutcomes: [
            'Understand amino acid structure',
            'Understand protein structure levels',
            'Relate protein structure to function'
          ],
          activities: ['Structure drawing', 'Protein analysis', 'Denaturation experiments'],
          resources: ['Amino acid charts', 'Protein models', 'Denaturation materials'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Amino Acid Structure',
              duration: '60 min',
              objectives: ['Understand amino acid general structure', 'Know R group variation'],
              introduction: 'Amino acids are protein building blocks.',
              mainActivities: [
                { activity: 'General Structure', duration: '20 min', description: 'Amino group, carboxyl, R group' },
                { activity: 'R Group Variation', duration: '20 min', description: 'Different R groups = different amino acids' },
                { activity: 'Essential Amino Acids', duration: '20 min', description: '20 amino acids in proteins' }
              ],
              materials: ['Amino acid structures', 'Chart of 20 amino acids'],
              assessment: 'Draw general amino acid structure',
              homework: 'Learn amino acid structures',
              teacherNotes: 'Don\'t memorize all 20, understand variation'
            },
            {
              day: 'Tuesday',
              title: 'Peptide Bonds',
              duration: '60 min',
              objectives: ['Understand peptide bond formation', 'Draw dipeptides'],
              introduction: 'Amino acids join by peptide bonds.',
              mainActivities: [
                { activity: 'Bond Formation', duration: '25 min', description: 'Condensation between amino acids' },
                { activity: 'Dipeptide Drawing', duration: '20 min', description: 'Draw various dipeptides' },
                { activity: 'Polypeptides', duration: '15 min', description: 'Longer chains of amino acids' }
              ],
              materials: ['Bond formation diagrams', 'Drawing practice'],
              assessment: 'Draw peptide bond formation',
              homework: 'Peptide drawing worksheet',
              teacherNotes: 'Peptide bond is an amide bond'
            },
            {
              day: 'Wednesday',
              title: 'Protein Structure Levels',
              duration: '60 min',
              objectives: ['Understand the four levels of protein structure', 'Relate structure to function'],
              introduction: 'Proteins have complex 3D shapes.',
              mainActivities: [
                { activity: 'Primary Structure', duration: '10 min', description: 'Amino acid sequence' },
                { activity: 'Secondary Structure', duration: '15 min', description: 'Alpha helix, beta sheet' },
                { activity: 'Tertiary/Quaternary', duration: '20 min', description: '3D folding, multiple chains' },
                { activity: 'Structure-Function', duration: '15 min', description: 'Shape determines function' }
              ],
              materials: ['Structure level diagrams', 'Protein models'],
              assessment: 'Describe all four structure levels',
              homework: 'Protein structure questions',
              teacherNotes: 'Enzymes are good examples'
            },
            {
              day: 'Thursday',
              title: 'Denaturation',
              duration: '60 min',
              objectives: ['Understand denaturation', 'Investigate factors causing denaturation'],
              introduction: 'Proteins can lose their shape and function.',
              mainActivities: [
                { activity: 'Denaturation Concept', duration: '15 min', description: 'Loss of 3D structure' },
                { activity: 'Causes', duration: '15 min', description: 'Heat, pH, chemicals' },
                { activity: 'Practical', duration: '30 min', description: 'Denature egg protein' }
              ],
              materials: ['Eggs', 'Heat source', 'Acid'],
              assessment: 'Explain denaturation with examples',
              homework: 'Denaturation examples at home',
              teacherNotes: 'Cooking is denaturation!'
            },
            {
              day: 'Friday',
              title: 'Enzymes Introduction',
              duration: '60 min',
              objectives: ['Understand enzyme function', 'Relate to protein structure'],
              introduction: 'Enzymes are biological catalysts made of protein.',
              mainActivities: [
                { activity: 'Enzyme Function', duration: '20 min', description: 'Catalyze biological reactions' },
                { activity: 'Active Site', duration: '20 min', description: 'Specific shape for substrate' },
                { activity: 'Lock and Key', duration: '20 min', description: 'Specificity model' }
              ],
              materials: ['Enzyme diagrams', 'Lock-key models'],
              assessment: 'Explain enzyme specificity',
              homework: 'Enzyme research',
              teacherNotes: 'Foundation for enzyme kinetics'
            }
          ]
        },
        {
          week: 7,
          topic: 'Spectroscopy Introduction',
          strand: 'Analytical Chemistry',
          subStrand: 'Spectroscopic Methods',
          learningOutcomes: [
            'Understand basic spectroscopy principles',
            'Interpret simple IR spectra',
            'Identify functional groups from spectra'
          ],
          activities: ['Spectrum interpretation', 'Peak identification', 'Unknown analysis'],
          resources: ['Spectra examples', 'Peak correlation tables', 'Analysis worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Spectroscopy',
              duration: '60 min',
              objectives: ['Understand spectroscopy principle', 'Know types of spectroscopy'],
              introduction: 'Spectroscopy identifies compounds using light.',
              mainActivities: [
                { activity: 'Spectroscopy Concept', duration: '20 min', description: 'Matter interacts with electromagnetic radiation' },
                { activity: 'Types Overview', duration: '25 min', description: 'IR, UV-Vis, NMR, Mass spec' },
                { activity: 'Applications', duration: '15 min', description: 'Identification, quality control' }
              ],
              materials: ['Spectroscopy overview', 'Example spectra'],
              assessment: 'Explain spectroscopy principle',
              homework: 'Spectroscopy type research',
              teacherNotes: 'Focus on IR for Year 12'
            },
            {
              day: 'Tuesday',
              title: 'Infrared Spectroscopy Basics',
              duration: '60 min',
              objectives: ['Understand IR spectroscopy', 'Know what IR measures'],
              introduction: 'IR spectroscopy detects bond vibrations.',
              mainActivities: [
                { activity: 'IR Principle', duration: '20 min', description: 'Bonds absorb IR causing vibrations' },
                { activity: 'Spectrum Layout', duration: '20 min', description: 'Wavenumber, transmittance, fingerprint' },
                { activity: 'Reading Spectra', duration: '20 min', description: 'Practice reading IR spectra' }
              ],
              materials: ['IR spectra examples', 'Reading guide'],
              assessment: 'Read basic IR spectrum',
              homework: 'IR spectrum reading practice',
              teacherNotes: 'Wavenumber in cm⁻¹'
            },
            {
              day: 'Wednesday',
              title: 'Key IR Absorptions',
              duration: '60 min',
              objectives: ['Identify key functional group absorptions', 'Use correlation table'],
              introduction: 'Different bonds absorb at characteristic frequencies.',
              mainActivities: [
                { activity: 'O-H Absorption', duration: '15 min', description: 'Broad peak around 3300 cm⁻¹' },
                { activity: 'C=O Absorption', duration: '15 min', description: 'Strong peak around 1700 cm⁻¹' },
                { activity: 'Other Key Peaks', duration: '15 min', description: 'C-H, N-H, C=C absorptions' },
                { activity: 'Practice', duration: '15 min', description: 'Identify peaks in spectra' }
              ],
              materials: ['Correlation table', 'Practice spectra'],
              assessment: 'Identify key absorptions',
              homework: 'Peak identification worksheet',
              teacherNotes: 'Memorize key peak positions'
            },
            {
              day: 'Thursday',
              title: 'Identifying Compounds from IR',
              duration: '60 min',
              objectives: ['Identify functional groups from IR spectra', 'Determine compound class'],
              introduction: 'IR spectra help identify unknown compounds.',
              mainActivities: [
                { activity: 'Alcohol Spectra', duration: '15 min', description: 'Broad O-H, C-O peaks' },
                { activity: 'Carboxylic Acid Spectra', duration: '15 min', description: 'Very broad O-H, C=O' },
                { activity: 'Aldehyde/Ketone', duration: '15 min', description: 'C=O without broad O-H' },
                { activity: 'Practice', duration: '15 min', description: 'Identify compound type from spectrum' }
              ],
              materials: ['Reference spectra', 'Unknown spectra'],
              assessment: 'Identify compound type from spectrum',
              homework: 'Spectrum analysis worksheet',
              teacherNotes: 'Focus on functional group identification'
            },
            {
              day: 'Friday',
              title: 'Spectroscopy Practice',
              duration: '60 min',
              objectives: ['Practice spectrum interpretation', 'Review spectroscopy concepts'],
              introduction: 'Let\'s practice our spectroscopy skills.',
              mainActivities: [
                { activity: 'Review', duration: '15 min', description: 'Key concepts review' },
                { activity: 'Practice Problems', duration: '30 min', description: 'Analyze various spectra' },
                { activity: 'Quiz', duration: '15 min', description: 'Spectroscopy assessment' }
              ],
              materials: ['Practice spectra', 'Quiz papers'],
              assessment: 'Spectroscopy quiz',
              homework: 'Continue spectrum practice',
              teacherNotes: 'Important analytical skill'
            }
          ]
        },
        {
          week: 8,
          topic: 'Review and Applications',
          strand: 'Review',
          subStrand: 'Term 2 Content',
          learningOutcomes: [
            'Review electrochemistry concepts',
            'Review organic and biochemistry',
            'Apply knowledge to problems'
          ],
          activities: ['Comprehensive review', 'Problem solving', 'Exam preparation'],
          resources: ['Review materials', 'Practice problems', 'Past papers'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Electrochemistry Review',
              duration: '60 min',
              objectives: ['Review redox and electrochemical cells'],
              introduction: 'Let\'s review electrochemistry.',
              mainActivities: [
                { activity: 'Redox Review', duration: '25 min', description: 'Oxidation numbers, balancing' },
                { activity: 'Cells Review', duration: '25 min', description: 'Galvanic, electrolytic, calculations' },
                { activity: 'Practice', duration: '10 min', description: 'Mixed electrochemistry problems' }
              ],
              materials: ['Review sheet', 'Practice problems'],
              assessment: 'Complete electrochemistry problems',
              homework: 'Study electrochemistry',
              teacherNotes: 'Focus on calculations'
            },
            {
              day: 'Tuesday',
              title: 'Organic Chemistry Review',
              duration: '60 min',
              objectives: ['Review polymers and reactions'],
              introduction: 'Let\'s review organic chemistry.',
              mainActivities: [
                { activity: 'Polymer Review', duration: '25 min', description: 'Addition, condensation polymerization' },
                { activity: 'Drawing Practice', duration: '25 min', description: 'Monomer/polymer structures' },
                { activity: 'Problems', duration: '10 min', description: 'Organic problems' }
              ],
              materials: ['Review materials', 'Drawing practice'],
              assessment: 'Complete organic problems',
              homework: 'Study organic chemistry',
              teacherNotes: 'Drawing skills important'
            },
            {
              day: 'Wednesday',
              title: 'Biochemistry Review',
              duration: '60 min',
              objectives: ['Review carbohydrates, lipids, proteins'],
              introduction: 'Let\'s review biochemistry.',
              mainActivities: [
                { activity: 'Carbohydrates Review', duration: '15 min', description: 'Structures and functions' },
                { activity: 'Lipids Review', duration: '15 min', description: 'Structures and functions' },
                { activity: 'Proteins Review', duration: '20 min', description: 'Amino acids, structure levels' },
                { activity: 'Problems', duration: '10 min', description: 'Biochemistry problems' }
              ],
              materials: ['Review materials', 'Practice problems'],
              assessment: 'Complete biochemistry problems',
              homework: 'Study biochemistry',
              teacherNotes: 'Structure drawing practice'
            },
            {
              day: 'Thursday',
              title: 'Practice Examination',
              duration: '60 min',
              objectives: ['Complete practice exam under conditions'],
              introduction: 'Practice exam to prepare for assessment.',
              mainActivities: [
                { activity: 'Practice Exam', duration: '50 min', description: 'Full practice paper' },
                { activity: 'Quick Check', duration: '10 min', description: 'Check key answers' }
              ],
              materials: ['Practice papers', 'Calculators'],
              assessment: 'Complete practice examination',
              homework: 'Review practice exam',
              teacherNotes: 'Exam conditions'
            },
            {
              day: 'Friday',
              title: 'Exam Review and Preparation',
              duration: '60 min',
              objectives: ['Review practice exam', 'Address remaining questions'],
              introduction: 'Final preparation for Term 2 exam.',
              mainActivities: [
                { activity: 'Exam Review', duration: '30 min', description: 'Go through practice exam' },
                { activity: 'Q&A', duration: '20 min', description: 'Answer remaining questions' },
                { activity: 'Exam Tips', duration: '10 min', description: 'Final preparation tips' }
              ],
              materials: ['Practice exam answers', 'Review notes'],
              assessment: 'Participate in review',
              homework: 'Prepare for exam',
              teacherNotes: 'Supportive review'
            }
          ]
        },
        {
          week: 9,
          topic: 'Term 2 Assessment',
          strand: 'Assessment',
          subStrand: 'Examination',
          learningOutcomes: [
            'Demonstrate Term 2 knowledge',
            'Apply chemistry skills',
            'Complete examination successfully'
          ],
          activities: ['Written examination', 'Practical component', 'Review'],
          resources: ['Exam papers', 'Lab materials', 'Data booklets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Theory Examination',
              duration: '60 min',
              objectives: ['Complete theory examination'],
              introduction: 'Show your Term 2 knowledge.',
              mainActivities: [
                { activity: 'Theory Exam', duration: '55 min', description: 'All Term 2 content' },
                { activity: 'Submission', duration: '5 min', description: 'Submit papers' }
              ],
              materials: ['Exam papers', 'Calculators', 'Data booklet'],
              assessment: 'Term 2 theory examination',
              homework: 'Prepare for practical',
              teacherNotes: 'Exam conditions'
            },
            {
              day: 'Tuesday',
              title: 'Practical Examination',
              duration: '60 min',
              objectives: ['Complete practical assessment'],
              introduction: 'Demonstrate your practical skills.',
              mainActivities: [
                { activity: 'Practical Tasks', duration: '50 min', description: 'Electrochemistry/analysis practical' },
                { activity: 'Write-up', duration: '10 min', description: 'Complete report' }
              ],
              materials: ['Lab equipment', 'Chemicals', 'Report sheets'],
              assessment: 'Practical examination',
              homework: 'None',
              teacherNotes: 'Assess technique and accuracy'
            },
            {
              day: 'Wednesday',
              title: 'Exam Review',
              duration: '60 min',
              objectives: ['Review examination performance'],
              introduction: 'Let\'s learn from the exam.',
              mainActivities: [
                { activity: 'Paper Review', duration: '40 min', description: 'Go through examination' },
                { activity: 'Discussion', duration: '20 min', description: 'Discuss common errors' }
              ],
              materials: ['Marked papers'],
              assessment: 'None',
              homework: 'Reflect on learning',
              teacherNotes: 'Learning from mistakes'
            },
            {
              day: 'Thursday',
              title: 'Grade Distribution',
              duration: '60 min',
              objectives: ['Receive and discuss grades'],
              introduction: 'Let\'s look at your progress.',
              mainActivities: [
                { activity: 'Grades', duration: '20 min', description: 'Distribute grades' },
                { activity: 'Individual Feedback', duration: '30 min', description: 'One-on-one discussions' },
                { activity: 'Improvement Planning', duration: '10 min', description: 'Areas for improvement' }
              ],
              materials: ['Grade reports'],
              assessment: 'None',
              homework: 'Consider feedback',
              teacherNotes: 'Constructive feedback focus'
            },
            {
              day: 'Friday',
              title: 'Term Wrap-up',
              duration: '60 min',
              objectives: ['Complete Term 2'],
              introduction: 'Great work this term!',
              mainActivities: [
                { activity: 'Term Summary', duration: '20 min', description: 'What we covered' },
                { activity: 'Term 3 Preview', duration: '20 min', description: 'What\'s coming next' },
                { activity: 'Celebration', duration: '20 min', description: 'Celebrate achievements' }
              ],
              materials: ['None'],
              assessment: 'None',
              homework: 'Enjoy term break!',
              teacherNotes: 'Positive ending'
            }
          ]
        },
        {
          week: 10,
          topic: 'Extended Learning',
          strand: 'Enrichment',
          subStrand: 'Advanced Topics',
          learningOutcomes: [
            'Explore advanced chemistry topics',
            'Prepare for Year 13',
            'Develop independent learning skills'
          ],
          activities: ['Project work', 'Research', 'Presentations'],
          resources: ['Research materials', 'Internet access', 'Presentation equipment'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Independent Research Day',
              duration: '60 min',
              objectives: ['Begin independent research project'],
              introduction: 'Choose a chemistry topic to explore.',
              mainActivities: [
                { activity: 'Topic Selection', duration: '20 min', description: 'Choose research topic' },
                { activity: 'Research Planning', duration: '20 min', description: 'Plan research approach' },
                { activity: 'Begin Research', duration: '20 min', description: 'Start gathering information' }
              ],
              materials: ['Research resources'],
              assessment: 'Research plan',
              homework: 'Continue research',
              teacherNotes: 'Guide topic selection'
            },
            {
              day: 'Tuesday',
              title: 'Research Continuation',
              duration: '60 min',
              objectives: ['Continue research project'],
              introduction: 'Keep exploring your topic.',
              mainActivities: [
                { activity: 'Research', duration: '45 min', description: 'Continue investigation' },
                { activity: 'Progress Check', duration: '15 min', description: 'Share progress' }
              ],
              materials: ['Research materials'],
              assessment: 'Research progress',
              homework: 'Continue research',
              teacherNotes: 'Support individual projects'
            },
            {
              day: 'Wednesday',
              title: 'Presentation Preparation',
              duration: '60 min',
              objectives: ['Prepare research presentation'],
              introduction: 'Share what you\'ve learned.',
              mainActivities: [
                { activity: 'Organize Findings', duration: '25 min', description: 'Structure presentation' },
                { activity: 'Create Visuals', duration: '25 min', description: 'Prepare presentation aids' },
                { activity: 'Practice', duration: '10 min', description: 'Practice presenting' }
              ],
              materials: ['Presentation materials'],
              assessment: 'Presentation preparation',
              homework: 'Finish preparation',
              teacherNotes: 'Presentation skills development'
            },
            {
              day: 'Thursday',
              title: 'Student Presentations',
              duration: '60 min',
              objectives: ['Present research findings'],
              introduction: 'Time to share your work!',
              mainActivities: [
                { activity: 'Presentations', duration: '50 min', description: 'Student presentations' },
                { activity: 'Q&A', duration: '10 min', description: 'Questions after each' }
              ],
              materials: ['Presentation equipment'],
              assessment: 'Presentation quality',
              homework: 'None',
              teacherNotes: 'Supportive audience'
            },
            {
              day: 'Friday',
              title: 'Year 12 Completion',
              duration: '60 min',
              objectives: ['Complete Year 12 Chemistry'],
              introduction: 'Congratulations on finishing Year 12!',
              mainActivities: [
                { activity: 'Year Review', duration: '20 min', description: 'Review year achievements' },
                { activity: 'Year 13 Preview', duration: '20 min', description: 'What to expect next year' },
                { activity: 'Celebration', duration: '20 min', description: 'Celebrate completion' }
              ],
              materials: ['Certificates if applicable'],
              assessment: 'None',
              homework: 'Enjoy term break!',
              teacherNotes: 'End positively'
            }
          ]
        }
      ]
    }
  },
  year13: {
    term1: {
      name: 'Term 1',
      focus: 'Organic Chemistry and Electrochemistry',
      weeks: 10,
      scheme: [
        {
          week: 1,
          topic: 'Introduction to Organic Chemistry',
          strand: 'Organic Chemistry',
          subStrand: 'Hydrocarbons',
          learningOutcomes: [
            'Define organic chemistry',
            'Identify carbon bonding characteristics',
            'Classify organic compounds'
          ],
          activities: [
            'Molecular model building',
            'Classification exercises',
            'Structural formula practice'
          ],
          resources: ['Molecular models', 'Organic compound charts'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'What is Organic Chemistry?',
              duration: '60 min',
              objectives: ['Define organic chemistry', 'Understand why carbon is special'],
              introduction: 'Carbon forms more compounds than all other elements combined!',
              mainActivities: [
                { activity: 'Definition', duration: '15 min', description: 'Study of carbon-containing compounds' },
                { activity: 'Carbon Properties', duration: '25 min', description: 'Tetravalency, catenation, multiple bonding' },
                { activity: 'Examples', duration: '20 min', description: 'Organic compounds in daily life' }
              ],
              materials: ['Carbon bonding diagrams'],
              assessment: 'Explain carbon\'s unique bonding',
              homework: 'Find 10 organic compounds at home',
              teacherNotes: 'Make relevant to students\' lives'
            },
            {
              day: 'Tuesday',
              title: 'Representing Organic Molecules',
              duration: '60 min',
              objectives: ['Write molecular formulas', 'Draw structural formulas'],
              introduction: 'Different ways to show organic molecules.',
              mainActivities: [
                { activity: 'Molecular Formulas', duration: '15 min', description: 'Show number of each atom type' },
                { activity: 'Structural Formulas', duration: '25 min', description: 'Show how atoms are connected' },
                { activity: 'Condensed Formulas', duration: '20 min', description: 'Shortened structural notation' }
              ],
              materials: ['Formula examples', 'Practice sheets'],
              assessment: 'Draw structural formulas correctly',
              homework: 'Formula conversion practice',
              teacherNotes: 'Drawing skills essential'
            },
            {
              day: 'Wednesday',
              title: 'Alkanes - Structure and Naming',
              duration: '60 min',
              objectives: ['Identify alkane structure', 'Name alkanes using IUPAC rules'],
              introduction: 'Alkanes are the simplest organic compounds.',
              mainActivities: [
                { activity: 'Alkane Structure', duration: '15 min', description: 'Single bonds only, CnH2n+2' },
                { activity: 'IUPAC Naming', duration: '25 min', description: 'Prefixes: meth-, eth-, prop-, but-' },
                { activity: 'Naming Practice', duration: '20 min', description: 'Name alkanes from structures' }
              ],
              materials: ['Naming rules chart', 'Practice structures'],
              assessment: 'Name alkanes correctly',
              homework: 'Alkane naming worksheet',
              teacherNotes: 'Memorize prefixes meth- through dec-'
            },
            {
              day: 'Thursday',
              title: 'Alkanes - Properties and Reactions',
              duration: '60 min',
              objectives: ['Describe alkane properties', 'Write combustion reactions'],
              introduction: 'What can alkanes do?',
              mainActivities: [
                { activity: 'Physical Properties', duration: '20 min', description: 'Boiling points, solubility trends' },
                { activity: 'Combustion', duration: '25 min', description: 'Complete and incomplete combustion' },
                { activity: 'Writing Equations', duration: '15 min', description: 'Balanced combustion equations' }
              ],
              materials: ['Property data', 'Equation practice'],
              assessment: 'Write combustion equations',
              homework: 'Properties and reactions worksheet',
              teacherNotes: 'Connect to fossil fuels'
            },
            {
              day: 'Friday',
              title: 'Isomerism in Alkanes',
              duration: '60 min',
              objectives: ['Define isomerism', 'Draw structural isomers'],
              introduction: 'Same formula, different structures!',
              mainActivities: [
                { activity: 'Isomer Definition', duration: '15 min', description: 'Same molecular formula, different structure' },
                { activity: 'Drawing Isomers', duration: '30 min', description: 'Find all isomers of given formula' },
                { activity: 'Week Quiz', duration: '15 min', description: 'Alkanes quiz' }
              ],
              materials: ['Isomer examples', 'Quiz papers'],
              assessment: 'Alkanes quiz',
              homework: 'Study alkenes for next week',
              teacherNotes: 'C4H10 has 2 isomers, C5H12 has 3'
            }
          ]
        },
        {
          week: 2,
          topic: 'Alkenes and Alkynes',
          strand: 'Organic Chemistry',
          subStrand: 'Unsaturated Hydrocarbons',
          learningOutcomes: [
            'Identify alkene and alkyne structure',
            'Name unsaturated hydrocarbons',
            'Describe addition reactions'
          ],
          activities: [
            'Structure drawing',
            'Addition reaction practice',
            'Comparison activities'
          ],
          resources: ['Molecular models', 'Reaction diagrams'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Alkene Structure and Naming',
              duration: '60 min',
              objectives: ['Identify alkene structure', 'Name alkenes'],
              introduction: 'Alkenes have double bonds.',
              mainActivities: [
                { activity: 'Double Bond', duration: '20 min', description: 'C=C bond, CnH2n formula' },
                { activity: 'Naming Rules', duration: '25 min', description: 'Same prefixes, -ene ending, position number' },
                { activity: 'Practice', duration: '15 min', description: 'Name alkenes from structures' }
              ],
              materials: ['Naming guide', 'Practice structures'],
              assessment: 'Name alkenes correctly',
              homework: 'Alkene naming practice',
              teacherNotes: 'Number from end nearest double bond'
            },
            {
              day: 'Tuesday',
              title: 'Geometric Isomerism',
              duration: '60 min',
              objectives: ['Explain geometric isomerism', 'Identify cis and trans isomers'],
              introduction: 'Double bonds create a new type of isomerism.',
              mainActivities: [
                { activity: 'Restricted Rotation', duration: '15 min', description: 'No rotation around double bond' },
                { activity: 'Cis and Trans', duration: '25 min', description: 'Same side vs opposite sides' },
                { activity: 'Identification Practice', duration: '20 min', description: 'Identify and draw geometric isomers' }
              ],
              materials: ['Isomer diagrams', 'Models'],
              assessment: 'Draw cis and trans isomers',
              homework: 'Geometric isomer worksheet',
              teacherNotes: 'Different properties despite same formula'
            },
            {
              day: 'Wednesday',
              title: 'Alkene Reactions - Addition',
              duration: '60 min',
              objectives: ['Describe addition reaction mechanism', 'Write addition reaction equations'],
              introduction: 'Alkenes react by adding across the double bond.',
              mainActivities: [
                { activity: 'Addition Concept', duration: '15 min', description: 'Double bond breaks, atoms add' },
                { activity: 'Hydrogenation', duration: '20 min', description: 'Adding H2 with catalyst' },
                { activity: 'Halogenation', duration: '25 min', description: 'Adding Br2 or Cl2' }
              ],
              materials: ['Reaction diagrams'],
              assessment: 'Write addition reaction equations',
              homework: 'Addition reaction practice',
              teacherNotes: 'Bromine test for unsaturation'
            },
            {
              day: 'Thursday',
              title: 'More Alkene Reactions',
              duration: '60 min',
              objectives: ['Describe hydration and HX addition', 'Predict products'],
              introduction: 'More atoms can add to alkenes.',
              mainActivities: [
                { activity: 'Hydration', duration: '20 min', description: 'Adding H2O to form alcohols' },
                { activity: 'Hydrohalogenation', duration: '20 min', description: 'Adding HCl, HBr' },
                { activity: 'Product Prediction', duration: '20 min', description: 'Predict products of reactions' }
              ],
              materials: ['Reaction examples'],
              assessment: 'Predict addition products',
              homework: 'Mixed reaction practice',
              teacherNotes: 'Markovnikov\'s rule for unsymmetrical'
            },
            {
              day: 'Friday',
              title: 'Alkynes',
              duration: '60 min',
              objectives: ['Identify alkyne structure', 'Compare to alkenes'],
              introduction: 'Alkynes have triple bonds.',
              mainActivities: [
                { activity: 'Triple Bond Structure', duration: '20 min', description: 'C≡C bond, CnH2n-2' },
                { activity: 'Naming and Properties', duration: '20 min', description: '-yne ending, linear shape' },
                { activity: 'Week Review', duration: '20 min', description: 'Quiz on unsaturated hydrocarbons' }
              ],
              materials: ['Alkyne examples', 'Quiz papers'],
              assessment: 'Unsaturated hydrocarbons quiz',
              homework: 'Study alcohols for next week',
              teacherNotes: 'Acetylene (ethyne) most important'
            }
          ]
        },
        {
          week: 3,
          topic: 'Alcohols and Ethers',
          strand: 'Organic Chemistry',
          subStrand: 'Oxygen-Containing Compounds',
          learningOutcomes: [
            'Identify functional groups',
            'Name alcohols and ethers',
            'Describe properties and reactions'
          ],
          activities: [
            'Functional group identification',
            'Reaction demonstrations',
            'Classification exercises'
          ],
          resources: ['Alcohol samples', 'Reaction equipment'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Alcohol Structure and Naming',
              duration: '60 min',
              objectives: ['Identify -OH functional group', 'Name alcohols'],
              introduction: 'Alcohols contain the hydroxyl group.',
              mainActivities: [
                { activity: 'Hydroxyl Group', duration: '15 min', description: '-OH attached to carbon' },
                { activity: 'Naming Rules', duration: '25 min', description: '-ol ending, position number' },
                { activity: 'Classification', duration: '20 min', description: 'Primary, secondary, tertiary' }
              ],
              materials: ['Structure diagrams', 'Classification chart'],
              assessment: 'Name and classify alcohols',
              homework: 'Alcohol naming practice',
              teacherNotes: 'Classify by carbon attached to OH'
            },
            {
              day: 'Tuesday',
              title: 'Alcohol Properties',
              duration: '60 min',
              objectives: ['Explain hydrogen bonding in alcohols', 'Compare properties to alkanes'],
              introduction: 'Why do alcohols have higher boiling points?',
              mainActivities: [
                { activity: 'Hydrogen Bonding', duration: '25 min', description: 'OH enables H-bonding, raises BP' },
                { activity: 'Solubility', duration: '20 min', description: 'Small alcohols dissolve in water' },
                { activity: 'Property Comparison', duration: '15 min', description: 'Compare to alkanes of similar mass' }
              ],
              materials: ['Property data', 'Comparison tables'],
              assessment: 'Explain alcohol properties',
              homework: 'Property comparison questions',
              teacherNotes: 'Intermolecular forces key concept'
            },
            {
              day: 'Wednesday',
              title: 'Alcohol Reactions - Combustion and Oxidation',
              duration: '60 min',
              objectives: ['Write combustion equations', 'Describe oxidation products'],
              introduction: 'Alcohols can burn and be oxidized.',
              mainActivities: [
                { activity: 'Combustion', duration: '15 min', description: 'Alcohols as fuels' },
                { activity: 'Oxidation', duration: '30 min', description: 'Primary→aldehyde→acid, Secondary→ketone' },
                { activity: 'Equation Writing', duration: '15 min', description: 'Write oxidation equations' }
              ],
              materials: ['Oxidation diagrams'],
              assessment: 'Write alcohol oxidation products',
              homework: 'Oxidation reaction practice',
              teacherNotes: 'Tertiary alcohols resist oxidation'
            },
            {
              day: 'Thursday',
              title: 'Alcohol Reactions - Dehydration and Substitution',
              duration: '60 min',
              objectives: ['Describe dehydration reaction', 'Write substitution reactions'],
              introduction: 'More reactions of alcohols.',
              mainActivities: [
                { activity: 'Dehydration', duration: '25 min', description: 'Remove H2O to form alkene' },
                { activity: 'Substitution', duration: '25 min', description: 'Replace OH with halogen' },
                { activity: 'Equation Practice', duration: '10 min', description: 'Write reaction equations' }
              ],
              materials: ['Reaction diagrams'],
              assessment: 'Write dehydration and substitution equations',
              homework: 'Mixed alcohol reactions',
              teacherNotes: 'Reverse of alkene hydration'
            },
            {
              day: 'Friday',
              title: 'Ethers',
              duration: '60 min',
              objectives: ['Identify ether structure', 'Compare to alcohols'],
              introduction: 'Ethers are oxygen between two carbons.',
              mainActivities: [
                { activity: 'Ether Structure', duration: '20 min', description: 'R-O-R structure, naming' },
                { activity: 'Properties', duration: '20 min', description: 'Lower BP than alcohols (no H-bonding)' },
                { activity: 'Week Quiz', duration: '20 min', description: 'Alcohols and ethers quiz' }
              ],
              materials: ['Ether examples', 'Quiz papers'],
              assessment: 'Alcohols and ethers quiz',
              homework: 'Study aldehydes and ketones',
              teacherNotes: 'Diethyl ether is common example'
            }
          ]
        },
        {
          week: 4,
          topic: 'Aldehydes, Ketones, and Carboxylic Acids',
          strand: 'Organic Chemistry',
          subStrand: 'Carbonyl Compounds',
          learningOutcomes: [
            'Identify carbonyl functional group',
            'Name aldehydes, ketones, and carboxylic acids',
            'Describe characteristic reactions'
          ],
          activities: [
            'Functional group tests',
            'Naming exercises',
            'Reaction demonstrations'
          ],
          resources: ['Test reagents', 'Compound samples'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'The Carbonyl Group',
              duration: '60 min',
              objectives: ['Identify C=O group', 'Distinguish aldehydes from ketones'],
              introduction: 'The carbonyl group is very reactive.',
              mainActivities: [
                { activity: 'Carbonyl Structure', duration: '15 min', description: 'C=O double bond, polar' },
                { activity: 'Aldehydes', duration: '20 min', description: 'C=O at end of chain, -al suffix' },
                { activity: 'Ketones', duration: '25 min', description: 'C=O in middle, -one suffix' }
              ],
              materials: ['Structure diagrams'],
              assessment: 'Distinguish aldehydes from ketones',
              homework: 'Naming practice',
              teacherNotes: 'Position of C=O is key difference'
            },
            {
              day: 'Tuesday',
              title: 'Aldehyde and Ketone Reactions',
              duration: '60 min',
              objectives: ['Describe reduction reactions', 'Test for aldehydes'],
              introduction: 'How do we identify these compounds?',
              mainActivities: [
                { activity: 'Reduction', duration: '20 min', description: 'Form alcohols with reducing agents' },
                { activity: 'Oxidation Test', duration: '25 min', description: 'Tollens\' and Fehling\'s tests for aldehydes' },
                { activity: 'Test Practice', duration: '15 min', description: 'Identify unknowns using tests' }
              ],
              materials: ['Test reagents', 'Unknown samples'],
              assessment: 'Correctly identify aldehydes',
              homework: 'Carbonyl reactions worksheet',
              teacherNotes: 'Silver mirror test is visual'
            },
            {
              day: 'Wednesday',
              title: 'Carboxylic Acids',
              duration: '60 min',
              objectives: ['Identify -COOH group', 'Name carboxylic acids'],
              introduction: 'The most acidic organic compounds.',
              mainActivities: [
                { activity: 'Structure', duration: '20 min', description: '-COOH functional group' },
                { activity: 'Naming', duration: '20 min', description: '-oic acid suffix' },
                { activity: 'Common Examples', duration: '20 min', description: 'Formic, acetic, citric acids' }
              ],
              materials: ['Acid structure chart'],
              assessment: 'Name carboxylic acids',
              homework: 'Carboxylic acid naming',
              teacherNotes: 'Many in foods and biology'
            },
            {
              day: 'Thursday',
              title: 'Carboxylic Acid Reactions',
              duration: '60 min',
              objectives: ['Describe acid-base reactions', 'Write esterification equations'],
              introduction: 'Carboxylic acids react as acids and more.',
              mainActivities: [
                { activity: 'Acid Reactions', duration: '15 min', description: 'React with bases, metals, carbonates' },
                { activity: 'Esterification', duration: '30 min', description: 'Acid + alcohol → ester + water' },
                { activity: 'Equation Practice', duration: '15 min', description: 'Write esterification equations' }
              ],
              materials: ['Reaction examples'],
              assessment: 'Write esterification equations',
              homework: 'Carboxylic acid reactions',
              teacherNotes: 'Esters have pleasant smells'
            },
            {
              day: 'Friday',
              title: 'Esters and Week Review',
              duration: '60 min',
              objectives: ['Name esters', 'Review carbonyl compounds'],
              introduction: 'Esters are everywhere - flavors and fragrances!',
              mainActivities: [
                { activity: 'Ester Naming', duration: '20 min', description: 'Alcohol part + acid part + -oate' },
                { activity: 'Hydrolysis', duration: '15 min', description: 'Breaking esters with water' },
                { activity: 'Week Quiz', duration: '25 min', description: 'Carbonyl compounds quiz' }
              ],
              materials: ['Ester examples', 'Quiz papers'],
              assessment: 'Carbonyl compounds quiz',
              homework: 'Study for electrochemistry',
              teacherNotes: 'Connect to everyday products'
            }
          ]
        },
        {
          week: 5,
          topic: 'Introduction to Electrochemistry',
          strand: 'Electrochemistry',
          subStrand: 'Redox and Electrochemical Cells',
          learningOutcomes: [
            'Define oxidation and reduction',
            'Assign oxidation numbers',
            'Balance redox equations'
          ],
          activities: [
            'Oxidation number practice',
            'Redox equation balancing',
            'Half-reaction method'
          ],
          resources: ['Periodic table', 'Worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Oxidation and Reduction Revisited',
              duration: '60 min',
              objectives: ['Define redox in terms of electrons', 'Identify oxidation and reduction'],
              introduction: 'OIL RIG: Oxidation Is Loss, Reduction Is Gain.',
              mainActivities: [
                { activity: 'Electron Transfer', duration: '20 min', description: 'Oxidation = lose electrons, reduction = gain' },
                { activity: 'Identifying Redox', duration: '25 min', description: 'Find what is oxidized/reduced' },
                { activity: 'Practice', duration: '15 min', description: 'Identify redox in reactions' }
              ],
              materials: ['Redox examples'],
              assessment: 'Identify oxidation and reduction',
              homework: 'Redox identification practice',
              teacherNotes: 'Both must occur together'
            },
            {
              day: 'Tuesday',
              title: 'Oxidation Numbers',
              duration: '60 min',
              objectives: ['Assign oxidation numbers', 'Track electron changes'],
              introduction: 'Oxidation numbers help us track electrons.',
              mainActivities: [
                { activity: 'Rules', duration: '25 min', description: 'Rules for assigning oxidation numbers' },
                { activity: 'Calculation Practice', duration: '25 min', description: 'Assign oxidation numbers in compounds' },
                { activity: 'Change Identification', duration: '10 min', description: 'Find oxidation number changes' }
              ],
              materials: ['Rules sheet', 'Practice problems'],
              assessment: 'Assign oxidation numbers correctly',
              homework: 'Oxidation number worksheet',
              teacherNotes: 'Sum must equal charge on species'
            },
            {
              day: 'Wednesday',
              title: 'Half-Reactions',
              duration: '60 min',
              objectives: ['Write half-reactions', 'Identify oxidation and reduction half-reactions'],
              introduction: 'Split the reaction to see electron flow.',
              mainActivities: [
                { activity: 'Half-Reaction Concept', duration: '15 min', description: 'Separate oxidation and reduction' },
                { activity: 'Writing Half-Reactions', duration: '30 min', description: 'Include electrons in equations' },
                { activity: 'Practice', duration: '15 min', description: 'Write half-reactions for given reactions' }
              ],
              materials: ['Half-reaction examples'],
              assessment: 'Write correct half-reactions',
              homework: 'Half-reaction practice',
              teacherNotes: 'Electrons must balance when combined'
            },
            {
              day: 'Thursday',
              title: 'Balancing Redox Equations',
              duration: '60 min',
              objectives: ['Balance redox equations using half-reaction method', 'Handle acidic solutions'],
              introduction: 'A systematic way to balance complex redox equations.',
              mainActivities: [
                { activity: 'Method Steps', duration: '20 min', description: 'Half-reactions, balance atoms, balance charge, combine' },
                { activity: 'Acidic Solution', duration: '25 min', description: 'Add H+ and H2O as needed' },
                { activity: 'Practice', duration: '15 min', description: 'Balance equations in acidic solution' }
              ],
              materials: ['Balancing guide', 'Practice equations'],
              assessment: 'Balance redox equations',
              homework: 'Redox balancing worksheet',
              teacherNotes: 'Takes practice to master'
            },
            {
              day: 'Friday',
              title: 'Redox in Basic Solution',
              duration: '60 min',
              objectives: ['Balance equations in basic solution', 'Review redox concepts'],
              introduction: 'Basic solutions need an extra step.',
              mainActivities: [
                { activity: 'Basic Solution Method', duration: '25 min', description: 'Add OH- to neutralize H+' },
                { activity: 'Practice Problems', duration: '20 min', description: 'Balance in basic solution' },
                { activity: 'Week Quiz', duration: '15 min', description: 'Redox concepts quiz' }
              ],
              materials: ['Method guide', 'Quiz papers'],
              assessment: 'Redox quiz',
              homework: 'Study electrochemical cells',
              teacherNotes: 'Same method, extra step for basic'
            }
          ]
        },
        {
          week: 6,
          topic: 'Electrochemical Cells',
          strand: 'Electrochemistry',
          subStrand: 'Galvanic Cells',
          learningOutcomes: [
            'Describe galvanic cell components',
            'Use standard reduction potentials',
            'Calculate cell voltage'
          ],
          activities: [
            'Cell construction',
            'Voltage measurements',
            'Calculations'
          ],
          resources: ['Electrodes', 'Voltmeters', 'Salt bridge materials'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Galvanic Cell Basics',
              duration: '60 min',
              objectives: ['Identify galvanic cell components', 'Explain how cells produce electricity'],
              introduction: 'Turn chemical energy into electrical energy!',
              mainActivities: [
                { activity: 'Cell Components', duration: '25 min', description: 'Anode, cathode, salt bridge, external circuit' },
                { activity: 'Electron Flow', duration: '20 min', description: 'Oxidation at anode, reduction at cathode' },
                { activity: 'Cell Diagram', duration: '15 min', description: 'Draw and label galvanic cell' }
              ],
              materials: ['Cell diagrams', 'Model cells'],
              assessment: 'Label galvanic cell correctly',
              homework: 'Cell diagram practice',
              teacherNotes: 'AN OX RED CAT mnemonic'
            },
            {
              day: 'Tuesday',
              title: 'Standard Reduction Potentials',
              duration: '60 min',
              objectives: ['Use standard reduction potential table', 'Predict reactions'],
              introduction: 'E° values tell us which way reactions go.',
              mainActivities: [
                { activity: 'E° Table', duration: '20 min', description: 'Reading and interpreting the table' },
                { activity: 'Predicting Reactions', duration: '25 min', description: 'Higher E° gets reduced' },
                { activity: 'Practice', duration: '15 min', description: 'Predict spontaneous reactions' }
              ],
              materials: ['E° table', 'Practice problems'],
              assessment: 'Predict reactions using E°',
              homework: 'E° prediction practice',
              teacherNotes: 'Standard conditions: 1M, 1atm, 25°C'
            },
            {
              day: 'Wednesday',
              title: 'Calculating Cell Voltage',
              duration: '60 min',
              objectives: ['Calculate E°cell', 'Determine cell spontaneity'],
              introduction: 'Calculate the voltage of any cell.',
              mainActivities: [
                { activity: 'E°cell Formula', duration: '20 min', description: 'E°cell = E°cathode - E°anode' },
                { activity: 'Calculations', duration: '30 min', description: 'Calculate E° for various cells' },
                { activity: 'Spontaneity', duration: '10 min', description: 'Positive E° = spontaneous' }
              ],
              materials: ['E° table', 'Calculators'],
              assessment: 'Calculate E°cell correctly',
              homework: 'E°cell calculation problems',
              teacherNotes: 'Don\'t multiply by coefficients'
            },
            {
              day: 'Thursday',
              title: 'Building a Galvanic Cell',
              duration: '60 min',
              objectives: ['Construct working galvanic cell', 'Measure cell voltage'],
              introduction: 'Let\'s build our own battery!',
              mainActivities: [
                { activity: 'Cell Setup', duration: '15 min', description: 'Prepare electrodes and solutions' },
                { activity: 'Construction', duration: '30 min', description: 'Build Zn/Cu galvanic cell' },
                { activity: 'Measurement', duration: '15 min', description: 'Measure and record voltage' }
              ],
              materials: ['Zn and Cu electrodes', 'Solutions', 'Voltmeter'],
              assessment: 'Working cell with correct voltage',
              homework: 'Write lab report',
              teacherNotes: 'Daniell cell is classic example'
            },
            {
              day: 'Friday',
              title: 'Cell Notation and Review',
              duration: '60 min',
              objectives: ['Write cell notation', 'Review electrochemistry'],
              introduction: 'A shorthand way to represent cells.',
              mainActivities: [
                { activity: 'Cell Notation', duration: '25 min', description: 'Anode | solution || solution | Cathode' },
                { activity: 'Reading Notation', duration: '15 min', description: 'Interpret given cell notation' },
                { activity: 'Week Quiz', duration: '20 min', description: 'Galvanic cells quiz' }
              ],
              materials: ['Notation examples', 'Quiz papers'],
              assessment: 'Galvanic cells quiz',
              homework: 'Study electrolysis',
              teacherNotes: 'Salt bridge shown as ||'
            }
          ]
        },
        {
          week: 7,
          topic: 'Electrolysis',
          strand: 'Electrochemistry',
          subStrand: 'Electrolytic Cells',
          learningOutcomes: [
            'Explain electrolysis process',
            'Predict products of electrolysis',
            'Perform electrolysis calculations'
          ],
          activities: [
            'Electrolysis experiments',
            'Product prediction',
            'Faraday\'s law calculations'
          ],
          resources: ['Power supply', 'Electrodes', 'Electrolytes'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Electrolysis',
              duration: '60 min',
              objectives: ['Define electrolysis', 'Compare to galvanic cells'],
              introduction: 'Using electricity to drive reactions.',
              mainActivities: [
                { activity: 'Electrolysis Definition', duration: '20 min', description: 'Non-spontaneous reaction driven by electricity' },
                { activity: 'Comparison', duration: '20 min', description: 'Galvanic vs electrolytic cells' },
                { activity: 'Applications', duration: '20 min', description: 'Electroplating, extraction, production' }
              ],
              materials: ['Cell comparison diagrams'],
              assessment: 'Compare cell types',
              homework: 'Electrolysis applications research',
              teacherNotes: 'Energy input required'
            },
            {
              day: 'Tuesday',
              title: 'Electrolysis of Molten Salts',
              duration: '60 min',
              objectives: ['Predict products from molten salts', 'Write electrode equations'],
              introduction: 'Simplest case - only one possible product at each electrode.',
              mainActivities: [
                { activity: 'Molten NaCl', duration: '25 min', description: 'Na+ → Na at cathode, Cl- → Cl2 at anode' },
                { activity: 'Other Examples', duration: '20 min', description: 'Electrolysis of other molten salts' },
                { activity: 'Equation Practice', duration: '15 min', description: 'Write electrode equations' }
              ],
              materials: ['Electrode equation examples'],
              assessment: 'Predict molten salt products',
              homework: 'Molten salt electrolysis problems',
              teacherNotes: 'Industrial sodium production'
            },
            {
              day: 'Wednesday',
              title: 'Electrolysis of Aqueous Solutions',
              duration: '60 min',
              objectives: ['Predict products from aqueous solutions', 'Apply selection rules'],
              introduction: 'Water adds complexity - more choices at electrodes.',
              mainActivities: [
                { activity: 'Competing Reactions', duration: '20 min', description: 'Water can be oxidized or reduced' },
                { activity: 'Selection Rules', duration: '25 min', description: 'Use E° and concentration to predict' },
                { activity: 'Practice', duration: '15 min', description: 'Predict aqueous solution products' }
              ],
              materials: ['Selection rules guide'],
              assessment: 'Predict aqueous products correctly',
              homework: 'Aqueous electrolysis problems',
              teacherNotes: 'Consider concentration effects'
            },
            {
              day: 'Thursday',
              title: 'Electrolysis Calculations - Faraday\'s Laws',
              duration: '60 min',
              objectives: ['Apply Faraday\'s laws', 'Calculate mass of products'],
              introduction: 'How much product can we make?',
              mainActivities: [
                { activity: 'Faraday\'s Laws', duration: '20 min', description: 'Mass proportional to charge and equivalent mass' },
                { activity: 'Formula Application', duration: '25 min', description: 'Q = It, n = Q/F, mass calculations' },
                { activity: 'Problem Solving', duration: '15 min', description: 'Calculate masses produced' }
              ],
              materials: ['Faraday constant', 'Calculators'],
              assessment: 'Complete electrolysis calculations',
              homework: 'Faraday\'s law problems',
              teacherNotes: 'F = 96500 C/mol'
            },
            {
              day: 'Friday',
              title: 'Electrolysis Practical',
              duration: '60 min',
              objectives: ['Perform electrolysis', 'Verify calculations'],
              introduction: 'Let\'s see electrolysis in action!',
              mainActivities: [
                { activity: 'Setup', duration: '10 min', description: 'Prepare electrolysis apparatus' },
                { activity: 'Electrolysis', duration: '35 min', description: 'Electrolyze CuSO4 solution, weigh products' },
                { activity: 'Verification', duration: '15 min', description: 'Compare experimental to calculated' }
              ],
              materials: ['Power supply', 'Copper electrodes', 'CuSO4'],
              assessment: 'Accurate mass measurement',
              homework: 'Complete practical report',
              teacherNotes: 'Copper plating is visual'
            }
          ]
        },
        {
          week: 8,
          topic: 'Applications of Electrochemistry',
          strand: 'Electrochemistry',
          subStrand: 'Practical Applications',
          learningOutcomes: [
            'Explain battery operation',
            'Describe corrosion and prevention',
            'Understand industrial applications'
          ],
          activities: [
            'Battery analysis',
            'Corrosion experiments',
            'Industrial process research'
          ],
          resources: ['Various batteries', 'Corrosion samples'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Batteries',
              duration: '60 min',
              objectives: ['Explain primary and secondary batteries', 'Describe common battery types'],
              introduction: 'Batteries power our modern world.',
              mainActivities: [
                { activity: 'Primary Batteries', duration: '20 min', description: 'Dry cell, alkaline - not rechargeable' },
                { activity: 'Secondary Batteries', duration: '25 min', description: 'Lead-acid, lithium-ion - rechargeable' },
                { activity: 'Comparison', duration: '15 min', description: 'Advantages and disadvantages' }
              ],
              materials: ['Battery samples', 'Diagrams'],
              assessment: 'Explain battery operation',
              homework: 'Battery research project',
              teacherNotes: 'Connect to everyday devices'
            },
            {
              day: 'Tuesday',
              title: 'Fuel Cells',
              duration: '60 min',
              objectives: ['Explain fuel cell operation', 'Discuss advantages and challenges'],
              introduction: 'Converting fuel directly to electricity.',
              mainActivities: [
                { activity: 'Fuel Cell Operation', duration: '25 min', description: 'H2 + O2 → H2O + electricity' },
                { activity: 'Advantages', duration: '15 min', description: 'Clean, efficient, continuous operation' },
                { activity: 'Challenges', duration: '20 min', description: 'Cost, hydrogen storage, infrastructure' }
              ],
              materials: ['Fuel cell diagrams'],
              assessment: 'Explain fuel cell advantages',
              homework: 'Fuel cell technology research',
              teacherNotes: 'Future of clean energy'
            },
            {
              day: 'Wednesday',
              title: 'Corrosion',
              duration: '60 min',
              objectives: ['Explain electrochemical corrosion', 'Identify corrosion as electrochemical process'],
              introduction: 'Rusting is electrochemistry in action.',
              mainActivities: [
                { activity: 'Corrosion Process', duration: '25 min', description: 'Iron oxidation as mini galvanic cells' },
                { activity: 'Factors', duration: '20 min', description: 'Water, oxygen, salt accelerate corrosion' },
                { activity: 'Observation', duration: '15 min', description: 'Examine corroded samples' }
              ],
              materials: ['Corroded metal samples'],
              assessment: 'Explain corrosion mechanism',
              homework: 'Corrosion observation at home',
              teacherNotes: 'Very relevant to island environment'
            },
            {
              day: 'Thursday',
              title: 'Corrosion Prevention',
              duration: '60 min',
              objectives: ['Describe corrosion prevention methods', 'Explain cathodic protection'],
              introduction: 'How do we stop corrosion?',
              mainActivities: [
                { activity: 'Coatings', duration: '15 min', description: 'Paint, oil, plastic coatings' },
                { activity: 'Galvanization', duration: '15 min', description: 'Zinc coating protects iron' },
                { activity: 'Cathodic Protection', duration: '20 min', description: 'Sacrificial anode method' },
                { activity: 'Examples', duration: '10 min', description: 'Local applications in Vanuatu' }
              ],
              materials: ['Protection examples'],
              assessment: 'Explain prevention methods',
              homework: 'Prevention methods research',
              teacherNotes: 'Ships, pipelines, structures'
            },
            {
              day: 'Friday',
              title: 'Industrial Electrochemistry',
              duration: '60 min',
              objectives: ['Describe industrial electrolysis processes', 'Review applications'],
              introduction: 'Electrochemistry on a large scale.',
              mainActivities: [
                { activity: 'Aluminum Production', duration: '15 min', description: 'Hall-Héroult process' },
                { activity: 'Chlor-alkali Process', duration: '15 min', description: 'NaCl electrolysis for Cl2 and NaOH' },
                { activity: 'Electroplating', duration: '15 min', description: 'Coating objects with metals' },
                { activity: 'Week Quiz', duration: '15 min', description: 'Applications quiz' }
              ],
              materials: ['Process diagrams', 'Quiz papers'],
              assessment: 'Applications quiz',
              homework: 'Study for term review',
              teacherNotes: 'Industrial importance'
            }
          ]
        },
        {
          week: 9,
          topic: 'Organic and Electrochemistry Review',
          strand: 'Review',
          subStrand: 'Term Review',
          learningOutcomes: [
            'Review all organic chemistry concepts',
            'Review all electrochemistry concepts',
            'Prepare for term assessment'
          ],
          activities: [
            'Comprehensive review',
            'Practice problems',
            'Mock assessments'
          ],
          resources: ['Review materials', 'Practice tests'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Organic Chemistry Review - Hydrocarbons',
              duration: '60 min',
              objectives: ['Review alkanes, alkenes, alkynes'],
              introduction: 'Let\'s revisit organic fundamentals.',
              mainActivities: [
                { activity: 'Naming Review', duration: '20 min', description: 'Review IUPAC naming rules' },
                { activity: 'Reactions Review', duration: '25 min', description: 'Review key reactions' },
                { activity: 'Practice', duration: '15 min', description: 'Mixed problem set' }
              ],
              materials: ['Review sheet'],
              assessment: 'Practice quiz',
              homework: 'Study hydrocarbons',
              teacherNotes: 'Focus on common errors'
            },
            {
              day: 'Tuesday',
              title: 'Organic Chemistry Review - Functional Groups',
              duration: '60 min',
              objectives: ['Review oxygen-containing compounds'],
              introduction: 'Functional groups determine properties.',
              mainActivities: [
                { activity: 'Alcohols Review', duration: '15 min', description: 'Properties and reactions' },
                { activity: 'Carbonyl Review', duration: '20 min', description: 'Aldehydes, ketones, acids, esters' },
                { activity: 'Practice', duration: '25 min', description: 'Identify and name compounds' }
              ],
              materials: ['Functional group chart'],
              assessment: 'Practice problems',
              homework: 'Study functional groups',
              teacherNotes: 'Drawing skills important'
            },
            {
              day: 'Wednesday',
              title: 'Electrochemistry Review',
              duration: '60 min',
              objectives: ['Review all electrochemistry topics'],
              introduction: 'From redox to applications.',
              mainActivities: [
                { activity: 'Redox Review', duration: '15 min', description: 'Oxidation numbers, half-reactions' },
                { activity: 'Cells Review', duration: '20 min', description: 'Galvanic and electrolytic' },
                { activity: 'Calculations Review', duration: '25 min', description: 'E°cell and Faraday\'s laws' }
              ],
              materials: ['E° table', 'Review problems'],
              assessment: 'Practice calculations',
              homework: 'Study electrochemistry',
              teacherNotes: 'Calculation methods'
            },
            {
              day: 'Thursday',
              title: 'Practice Exam',
              duration: '60 min',
              objectives: ['Complete practice exam'],
              introduction: 'Simulate exam conditions.',
              mainActivities: [
                { activity: 'Practice Exam', duration: '50 min', description: 'Full practice exam' },
                { activity: 'Quick Review', duration: '10 min', description: 'Immediate feedback on key questions' }
              ],
              materials: ['Practice exam'],
              assessment: 'Practice exam',
              homework: 'Review and final preparation',
              teacherNotes: 'Build confidence'
            },
            {
              day: 'Friday',
              title: 'Final Q&A and Preparation',
              duration: '60 min',
              objectives: ['Address remaining questions'],
              introduction: 'Last chance for questions!',
              mainActivities: [
                { activity: 'Q&A Session', duration: '40 min', description: 'Answer all student questions' },
                { activity: 'Exam Tips', duration: '10 min', description: 'Strategies for success' },
                { activity: 'Encouragement', duration: '10 min', description: 'Build confidence' }
              ],
              materials: ['Q&A materials'],
              assessment: 'Participation',
              homework: 'Rest and prepare',
              teacherNotes: 'Positive atmosphere'
            }
          ]
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'Assessment',
          subStrand: 'Term Examination',
          learningOutcomes: [
            'Demonstrate mastery of organic chemistry',
            'Apply electrochemistry knowledge',
            'Show problem-solving skills'
          ],
          activities: [
            'Term examination',
            'Practical assessment'
          ],
          resources: ['Exam papers', 'Lab equipment'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Written Exam - Part 1',
              duration: '60 min',
              objectives: ['Complete organic chemistry section'],
              introduction: 'Focus on organic chemistry.',
              mainActivities: [
                { activity: 'Exam - Organic', duration: '55 min', description: 'Organic chemistry questions' },
                { activity: 'Submission', duration: '5 min', description: 'Submit papers' }
              ],
              materials: ['Exam papers'],
              assessment: 'Organic chemistry exam',
              homework: 'Study for Part 2',
              teacherNotes: 'Fair and comprehensive'
            },
            {
              day: 'Tuesday',
              title: 'Written Exam - Part 2',
              duration: '60 min',
              objectives: ['Complete electrochemistry section'],
              introduction: 'Focus on electrochemistry.',
              mainActivities: [
                { activity: 'Exam - Electrochem', duration: '55 min', description: 'Electrochemistry questions' },
                { activity: 'Submission', duration: '5 min', description: 'Submit papers' }
              ],
              materials: ['Exam papers', 'E° table'],
              assessment: 'Electrochemistry exam',
              homework: 'Prepare for practical',
              teacherNotes: 'Provide data booklet'
            },
            {
              day: 'Wednesday',
              title: 'Practical Examination',
              duration: '60 min',
              objectives: ['Demonstrate practical skills'],
              introduction: 'Show your laboratory skills.',
              mainActivities: [
                { activity: 'Practical Exam', duration: '55 min', description: 'Practical tasks and analysis' },
                { activity: 'Clean Up', duration: '5 min', description: 'Clean work area' }
              ],
              materials: ['Lab equipment', 'Chemicals'],
              assessment: 'Practical exam',
              homework: 'None',
              teacherNotes: 'Assess technique and results'
            },
            {
              day: 'Thursday',
              title: 'Exam Review',
              duration: '60 min',
              objectives: ['Review exam performance'],
              introduction: 'Let\'s learn from the exam.',
              mainActivities: [
                { activity: 'Answer Review', duration: '40 min', description: 'Go through exam answers' },
                { activity: 'Common Errors', duration: '20 min', description: 'Discuss common mistakes' }
              ],
              materials: ['Exam solutions'],
              assessment: 'Participation',
              homework: 'Reflect on learning',
              teacherNotes: 'Educational feedback'
            },
            {
              day: 'Friday',
              title: 'Term Wrap-Up',
              duration: '60 min',
              objectives: ['Conclude term positively'],
              introduction: 'Celebrate your achievements!',
              mainActivities: [
                { activity: 'Achievement Recognition', duration: '20 min', description: 'Recognize student achievements' },
                { activity: 'Looking Ahead', duration: '20 min', description: 'Preview next term topics' },
                { activity: 'Farewell', duration: '20 min', description: 'End of term activities' }
              ],
              materials: ['Certificates if applicable'],
              assessment: 'None',
              homework: 'Enjoy term break!',
              teacherNotes: 'End positively'
            }
          ]
        }
      ]
    }
  }
};

export default chemistryWeeklyLessons;
